import Preloader from "../../components/custom/preLoader";
import PondDetailWorkflowTabs from "../../components/custom/PondDetailWorkflowTabs";
import PondDetailWorkflowPageHeader from "../../components/custom/PondDetailWorkflowPageHeader";
import Select from "react-select";

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { enqueueSnackbar } from "notistack";
import DropDownStyles from "../../components/content/dropdownStyles";
import {
  SAMPLING_THEME,
  createSamplingFilterSelectStyles,
  samplingFilterLabelRowStyle,
  samplingFilterLabelStyle,
  samplingFetchButtonStyle,
  samplingCheckboxLabelStyle,
  samplingCheckboxTextStyle,
  samplingCheckboxInputStyle,
} from "./samplingFilterTheme";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import axiosInstance, {
  checkTokenValidity,
  clearAuthData,
  getUserDetails,
  isFisherWideUser,
  isJwtAuthFailure,
  isSuperUser,
} from "../../components/content/axiosConfig";
import { fetchPondDropdownOptions, resolveFisherId } from "../../utils/superPanelApi";
import "react-datepicker/dist/react-datepicker.css";
import CustomDateTimePicker from "../../components/custom/dateTimePicker";
import showErrorToast from "../../components/toast/toast";
import { formatDisplayDate } from "../../utils/formatDisplayDate";
import { useParams } from "react-router-dom";
import {
  CalendarRange,
  ChevronDown,
  ChevronUp,
  Download,
  Fish,
  FileSpreadsheet,
  Hash,
  Play,
  Ruler,
  Tags,
  Waves,
} from "lucide-react";
import { getConversionsForDisplay } from "./graphView/displayUnitConversions";
import {
  getCaptureMethodCounts,
  mapWithConcurrency,
  parseToDecimalHours,
  resolveEffortUnits,
  durationHoursFromRange,
  formatDurationHoursAsHm,
  formatSessionOnSiteDuration,
  resolveSessionOnSiteTimes,
  toClockTimeValue,
  normalizeSamplingMethod,
} from "../../utils/samplingCpueUtils";
import {
  SessionCpueTables,
  PerSpeciesCpueTables,
  appendCpueExportRows,
  getPerSpeciesCpueExportBlocks,
  getSessionCpueExportBlock,
} from "./CpueTables";
import {
  applyExcelPondStatFooterStyle,
  drawPondStatPdfFooter,
  PONDSTAT_EXPORT_FOOTER,
} from "./graphView/generateSamplingGraphPdf";
import { conversionFactors } from "./graphView/conversionFactors";
import { WrLabel, WsLabel, LABEL, displayMethodLabel, displaySpeciesTableLabel, SpeciesTableLabel, formatSpeciesExtremeLW } from "../../utils/samplingDisplayLabels";
import {
  filterStockingEventsByGraphRange,
} from "./graphView/data/stockingPredatorTableRows";
import { stockingPredatorRowIsBatch, predatorRowHeadcount } from "../../utils/stockingPredatorMath";
import { computePsdTableRowsForSession } from "./graphView/psdMetrics";
import { normalizeFishTypesKey } from "../../hooks/queries/useSamplingEnrichment";

const formatForage = (forageString) => {
  if (!forageString || forageString === "N/A") return "N/A";
  const forageItems = forageString
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
  return forageItems.join(", ");
};

/** Wr stored as ratio (e.g. 0.999); same as formatValue "averageWr" / "stdDevWr" → "99.9%". */
const formatBinWrPercent = (value) => {
  if (value === null || value === undefined) return "0.0%";
  const numValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numValue)) return "0.0%";
  return `${(numValue * 100).toFixed(1)}%`;
};

const formatEstimatedVolumeAcreFt = (vol) => {
  if (vol === null || vol === undefined || vol === "") return "N/A";
  const n = typeof vol === "number" ? vol : parseFloat(vol);
  if (isNaN(n)) return "N/A";
  return `${n.toFixed(2)} (acre-ft.)`;
};

const MODERN_TABLE_CLASS = "table table-sm align-middle mb-0";
const MODERN_TABLE_HEAD_CLASS = "text-uppercase";
const MODERN_TABLE_HEAD_STYLE = {
  fontSize: "11px",
  letterSpacing: "0.04em",
  color: "var(--tv-table-head-text, #334155)",
  background: "var(--tv-table-head-bg, linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%))",
};
const MODERN_TABLE_CELL_STYLE = {
  fontSize: "12px",
  color: "var(--tv-table-cell-text, inherit)",
  borderColor: "var(--tv-table-cell-border, #e2e8f0)",
};
const MODERN_TABLE_WRAPPER_STYLE = {
  border: "1px solid var(--tv-table-wrap-border, #e2e8f0)",
  borderRadius: "10px",
  background: "var(--tv-table-wrap-bg, #fff)",
};

const MODERN_TABLE_SHELL_CLASS = "donezo-tv-table-shell donezo-tv-table-shell--fill";

const formatValueWithUnit = (value, unitLabel) => {
  if (value === null || value === undefined || value === "" || value === "N/A") {
    return "N/A";
  }
  return `${value} ${unitLabel}`;
};

const formatAquaticVegetation = (value) => {
  if (value === null || value === undefined) return "None Listed";
  const normalized = String(value).trim();
  if (!normalized || normalized.toLowerCase() === "n/a") return "None Listed";
  return normalized;
};

/**
 * Forage row total # — mirrors pond `stockingData` `parsePreyTotalNumber` (Tag encodes count in UI;
 * API may store whole counts in `Number`).
 */
const parsePreyHeadcountFromStockingRow = (row) => {
  const fromTag = row?.Tag != null ? String(row.Tag).trim() : "";
  const fromNumber =
    row?.Number !== undefined && row?.Number !== null ? String(row.Number).trim() : "";
  const rawValue = fromTag || fromNumber;
  const normalized = rawValue.replaceAll(",", "");
  const parsed = normalized === "" ? NaN : parseInt(normalized, 10);
  const isWholeNumber = !Number.isNaN(parsed) && String(parsed) === normalized;
  return isWholeNumber ? Math.max(0, parsed) : null;
};

const formatPreyHeadcountDisplayFromRow = (row) => {
  const n = parsePreyHeadcountFromStockingRow(row);
  if (n != null) return n.toLocaleString();
  const tag = row?.Tag != null ? String(row.Tag).trim() : "";
  if (tag) return tag;
  return "—";
};

const sumPreyHeadcountsFromRows = (rows) => {
  if (!Array.isArray(rows)) return 0;
  let sum = 0;
  for (const row of rows) {
    const n = parsePreyHeadcountFromStockingRow(row);
    if (n != null) sum += n;
  }
  return sum;
};

/** Predator row headcount — uses shared `predatorRowHeadcount` util. */
const parsePredatorHeadcountFromStockingRow = (row) => predatorRowHeadcount(row);

const sumPredatorHeadcountsFromRows = (rows) => {
  if (!Array.isArray(rows)) return 0;
  let sum = 0;
  for (const row of rows) {
    sum += stockingPredatorRowCountDelta(row);
  }
  return sum;
};

/** Species / forage label (prey rows often use `ForageTypeName` without a `fishType`). */
const stockingRowSpeciesLabel = (row) => {
  const fromFish =
    row?.fishType?.Name || row?.FishTypeName || row?.fishType?.name || "";
  if (fromFish && String(fromFish).trim()) return String(fromFish).trim();
  const fromForage =
    (row?.ForageTypeName != null && String(row.ForageTypeName).trim()) ||
    (row?.forageTypeName != null && String(row.forageTypeName).trim()) ||
    "";
  return fromForage || "Unknown";
};

/** Dense table cell — prefer fish-type abbreviation. */
const stockingRowSpeciesDisplayLabel = (row) =>
  displaySpeciesTableLabel(
    stockingRowSpeciesLabel(row),
    row?.fishType?.Abbreviation ||
      row?.FishTypeAbbreviation ||
      row?.Abbreviation
  );

/**
 * Headcount contribution for one stocking predator row in summaries.
 * Bulk rows use numeric Tag/Number; individual fish often use alpha tags (e.g. LT1000)
 * and may omit `FishTypeID` while still sending `FishTypeName` / nested `fishType`.
 */
const stockingPredatorRowCountDelta = (row) => {
  if (!row) return 0;
  const hc = parsePredatorHeadcountFromStockingRow(row);
  if (hc != null && hc > 0) return hc;
  if (row.FishTypeID != null && row.FishTypeID !== "") return 1;
  if (row.fishTypeId != null && row.fishTypeId !== "") return 1;
  if (row.FishTypeId != null && row.FishTypeId !== "") return 1;
  const sp = stockingRowSpeciesLabel(row);
  if (sp && sp !== "Unknown") return 1;
  return 0;
};

/** Note body from a pond-note API row (aliases / casing differ by route or driver). */
const extractPondNoteBodyFromApiRow = (entry) => {
  if (!entry || typeof entry !== "object") return "";
  const t =
    entry.PondNote ??
    entry.pondNote ??
    entry.Note ??
    entry.note;
  if (t == null) return "";
  return String(t).trim();
};

/**
 * Stable calendar key YYYY-MM-DD for sorting (matches filter logic in stockingPredatorGraphRows).
 * Uses string prefix when API sends DATEONLY; avoids relying on `new Date` alone for odd strings.
 */
const stockingEventYmdKey = (event) => {
  if (!event || typeof event !== "object") return null;
  const raw = event.StockingDate ?? event.stockingDate;
  if (raw == null || raw === "") return null;
  if (typeof raw === "string") {
    const s = raw.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  }
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    const y = raw.getFullYear();
    const m = String(raw.getMonth() + 1).padStart(2, "0");
    const day = String(raw.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/** Stocking event storage units → English | Metric (same basis as stocking graphs). */
const stockingEventConversionsToDisplayUnit = (event, displayUnitKey) => {
  const lName = event?.lengthUnit?.Name ?? event?.lengthUnit?.name ?? "";
  const wName = event?.weightUnit?.Name ?? event?.weightUnit?.name ?? "";
  const lk = String(lName).toLowerCase().includes("mm") ? "mm" : "inch";
  const wLower = String(wName).toLowerCase();
  const wk =
    (wLower.includes("g") || wLower.includes("gram")) &&
    !wLower.includes("lb") &&
    !wLower.includes("pound")
      ? "gram"
      : "pound";
  const target = displayUnitKey === "Metric" ? "Metric" : "English";
  const lenC =
    conversionFactors.length[lk]?.[target] ?? conversionFactors.length.inch[target];
  const wtC =
    conversionFactors.weight[wk]?.[target] ?? conversionFactors.weight.pound[target];
  return {
    convertLength: (v) => parseFloat(v || 0) * lenC,
    convertWeight: (v) => parseFloat(v || 0) * wtC,
  };
};

/** Tagged tracking table: fixed two decimals for length/weight cells. */
const formatTaggedLgtWgtCell = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const n = typeof value === "number" ? value : parseFloat(value);
  if (!Number.isFinite(n)) return "-";
  return n.toFixed(2);
};

const TAG_GROUP_DIVIDER_STYLE = "2px solid #94a3b8";

/** Ascending by stocking date (earliest first), then by id; missing dates last. */
const sortStockingEventsByStockingDateAsc = (events) => {
  if (!Array.isArray(events) || events.length === 0) return Array.isArray(events) ? events : [];
  return [...events].sort((a, b) => {
    const da = stockingEventYmdKey(a);
    const db = stockingEventYmdKey(b);
    const ida = Number(a?.id) || 0;
    const idb = Number(b?.id) || 0;
    if (da === null && db === null) return ida - idb;
    if (da === null) return 1;
    if (db === null) return -1;
    if (da !== db) return da < db ? -1 : 1;
    return ida - idb;
  });
};

/**
 * 4×3 water-quality grid (row-major, left→right). Matches pond detail form; used by Table / PDF / XLSX.
 */
const WATER_QUALITY_GRID_ROWS = [
  [
    { label: "Water Temperature, F", key: "WaterTemperature" },
    { label: "Water Clarity, ft.", key: "WaterClarity" },
    { label: "pH (1-14)", key: "PH" },
  ],
  [
    { label: "Dissolved Oxygen, mg/L", key: "DO" },
    { label: "BOD5, mg/L", key: "BOD5" },
    { label: "COD, mg/L", key: "COD" },
  ],
  [
    { label: "Alkalinity, mg/L CaCO3", key: "Alkalinity" },
    { label: "Nitrite-N, mg/L NO2-N", key: "NitriteN" },
    { label: "Total Hardness, mg/L CaCO3", key: "TotalHardness" },
  ],
  [
    { label: "Phosphate Ion, mg/L PO4", key: "PhosphateIon" },
    { label: "Phosphorus, mg/L P", key: "Phosphorous" },
    { label: "Total Ammonia Nitrogen, mg/L NH3-N", key: "TotalAmmoniaNitrogen" },
  ],
];

const formatPondDetailWQValue = (detail, key) => {
  const v = detail[key];
  if (v === null || v === undefined || v === "") return "N/A";
  return String(v);
};

const DEFAULT_AGGREGATE_COLLAPSED = {
  binSummary: true,
  overallSummary: true,
  taggedTracking: true,
};

const FishDataTableView = () => {
  const { id: pondDetailId } = useParams(); // Get pondDetailId from URL
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [useAllData, setUseAllData] = useState(true);
  const [selectedPond, setSelectedPond] = useState(null);
  const [fishTypes, setFishTypes] = useState([]);
  const [selectedFish, setSelectedFish] = useState([]);
  const [ponds, setPonds] = useState([]);
  const [pondDetails, setPondDetails] = useState([]);
  /** Cross-session aggregates from POST /api/PondDetail/TableAggregates (full Fetch list). */
  const [tableAggregates, setTableAggregates] = useState(null);
  const [aggregatesLoading, setAggregatesLoading] = useState(false);
  const exportAggregatesRef = useRef(null);
  const aggregatesRequestIdRef = useRef(0);
  const [selectedPondInfo, setSelectedPondInfo] = useState(null);
  const userDetails = getUserDetails();
  const superUser = isSuperUser(userDetails);
  const FisherID = userDetails.FisherID;
  const fisherWide = isFisherWideUser(userDetails);
  const isSinglePondDetailMode = Boolean(pondDetailId);

  const [singlePondDetailTemplate, setSinglePondDetailTemplate] =
    useState(null);
  const [selectedTableUnit, setSelectedTableUnit] = useState({
    label: "English System",
    value: "English",
  });
  const [selectedTagMode, setSelectedTagMode] = useState({
    label: "All Fish",
    value: "all",
  });
  const [selectedTags, setSelectedTags] = useState([]);
  const [includeUntagged, setIncludeUntagged] = useState(false);
  const [includeStockingPredators, setIncludeStockingPredators] =
    useState(false);
  const [stockingEventsFiltered, setStockingEventsFiltered] = useState([]);
  /** Always chronological for table, PDF, and XLSX (even if state order ever differs). */
  const stockingEventsSorted = useMemo(
    () => sortStockingEventsByStockingDateAsc(stockingEventsFiltered),
    [stockingEventsFiltered]
  );
  const [collapsedSamplingCards, setCollapsedSamplingCards] = useState({});
  const [collapsedStockingCards, setCollapsedStockingCards] = useState({});
  const [collapsedAggregateSections, setCollapsedAggregateSections] = useState(
    DEFAULT_AGGREGATE_COLLAPSED
  );

  const filterSelectStyles = useMemo(
    () => createSamplingFilterSelectStyles(DropDownStyles),
    []
  );

  const FetchFishTypes = useCallback(async (pondId) => {
    if (!pondId) return;

    try {
      setIsLoading(true);
      const response = await axiosInstance.get(
        `/api/PondFishes/GetAllPondFishes/${pondId}`,
        {
          params: { search: "" },
        }
      );

      const dropdownFishTypes =
        response.data?.map((fish) => ({
          label: fish.FishTypeName,
          value: fish.FishTypeID,
        })) || [];

      setFishTypes(dropdownFishTypes);
    } catch (error) {
      if (isJwtAuthFailure(error)) {
        clearAuthData();
        window.location.href = "/user/login";
        showErrorToast("Session expired. Please log in again.");
      } else {
        const errorMessage =
          error.response?.data?.message || "Failed to fetch fish types.";
        console.error("Failed to fetch fish types", error);
        showErrorToast(errorMessage);
        setFishTypes([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  function formatAge(birthDate) {
    if (birthDate === null || birthDate === undefined || birthDate === "") {
      return "N/A";
    }

    const numericAge = Number(birthDate);
    if (Number.isFinite(numericAge) && numericAge >= 0) {
      const roundedAge = Math.round(numericAge * 10) / 10;
      return `${roundedAge} Year${roundedAge === 1 ? "" : "s"}`;
    }

    try {
      // Convert birthDate to a Date object
      const dob = new Date(birthDate);
      if (isNaN(dob)) return "N/A"; // Invalid date check

      const today = new Date();

      // Calculate years and months
      let years = today.getFullYear() - dob.getFullYear();
      let months = today.getMonth() - dob.getMonth();

      // Adjust if the current month/day is before the birth month/day
      if (months < 0 || (months === 0 && today.getDate() < dob.getDate())) {
        years--;
        months += 12;
      }

      // Format the result
      if (years === 0 && months === 0) {
        return "Less than a month";
      }

      let result = "";
      if (years > 0) {
        result += `${years} Year${years > 1 ? "s" : ""}`;
      }
      if (months > 0) {
        result += `${years > 0 ? " " : ""}${months} Month${months > 1 ? "s" : ""
          }`;
      }

      return result.trim() || "Less than a month";
    } catch (error) {
      console.error("Error parsing birth date:", error);
      return "N/A";
    }
  }

  const formatPondAge = (pondInfo) => {
    if (!pondInfo) return "N/A";
    if (pondInfo.AgeInYears !== null && pondInfo.AgeInYears !== undefined) {
      return formatAge(pondInfo.AgeInYears);
    }
    return formatAge(pondInfo.Age);
  };

  const FetchPonds = useCallback(async () => {
    if (!superUser && !FisherID) return;

    setIsLoading(true);
    try {
      const dropdownPonds = await fetchPondDropdownOptions(
        axiosInstance,
        userDetails,
        fisherWide
      );
      setPonds(dropdownPonds);
    } catch (error) {
      if (isJwtAuthFailure(error)) {
        clearAuthData();
        window.location.href = "/user/login";
        showErrorToast("Session expired. Please log in again.");
      } else {
        const errorMessage =
          error.response?.data?.message || "Failed to fetch ponds.";
        console.error("Failed to fetch ponds", error);
        showErrorToast(errorMessage);
        setPonds([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [FisherID, fisherWide, superUser]);

  const FetchSelectedPond = useCallback(
    async (pondId) => {
      if (!pondId) return;

      try {
        setIsLoading(true);
        const pondResponse = await axiosInstance.get(`/api/Pond/GetPondByID`, {
          params: { id: pondId },
        });

        const pond = pondResponse.data;
        if (pond) {
          let preyFishNames = [];
          let predatorFishNames = [];
          try {
            const [pondFishesResponse, stockingResponse] = await Promise.all([
              axiosInstance.get(`/api/PondFishes/GetAllPondFishes/${pondId}`, {
                params: { search: "" },
              }),
              axiosInstance.get(`/api/PondStocking/GetByPond/${pondId}`, {
                params: { FisherID },
              }),
            ]);
            const predatorNameSet = new Set();
            const preyNameSet = new Set();
            const pondFishRows = Array.isArray(pondFishesResponse.data)
              ? pondFishesResponse.data
              : pondFishesResponse.data?.data || [];
            pondFishRows.forEach((fish) => {
              const fishName = String(fish?.FishTypeName || "").trim();
              if (fishName) predatorNameSet.add(fishName);
            });
            (stockingResponse.data || []).forEach((event) => {
              (event?.preyRows || []).forEach((prey) => {
                const fishName = stockingRowSpeciesLabel(prey);
                if (fishName && fishName !== "Unknown") preyNameSet.add(fishName);
              });
              (event?.predatorRows || []).forEach((pred) => {
                const fishName =
                  pred?.fishType?.Name || pred?.FishTypeName || "";
                if (fishName) predatorNameSet.add(fishName);
              });
            });
            preyFishNames = Array.from(preyNameSet);
            predatorFishNames = Array.from(predatorNameSet).sort((a, b) =>
              a.localeCompare(b)
            );
          } catch (stockingError) {
            console.error("Failed to fetch pond fishes / stocking events", stockingError);
          }

          setSelectedPondInfo({
            id: pond.id,
            Name: pond.Name || "N/A",
            Location: pond.Location || "N/A",
            Forage: pond.Forage || "N/A",
            ForageType: pond.ForageType || "N/A",
            Type: pond.Type || "N/A",
            Age: pond.Age ? pond.Age.toString() : "N/A",
            AgeInYears:
              pond.AgeInYears !== undefined && pond.AgeInYears !== null
                ? pond.AgeInYears
                : null,
            Size: pond.Size ? pond.Size.toString() : "N/A",
            Depth: pond.Depth ? pond.Depth.toString() : "N/A",
            EstimatedVolume: pond.EstimatedVolume,
            AquaticVegetationPresent: pond.AquaticVegetationPresent || "N/A",
            WaterSupply: pond.WaterSupply || "N/A",
            PreyFishNames: preyFishNames,
            PredatorFishNames: predatorFishNames,
            FisherID,
          });
        }

        await FetchFishTypes(pondId);
      } catch (error) {
        if (isJwtAuthFailure(error)) {
          clearAuthData();
          window.location.href = "/user/login";
          showErrorToast("Session expired. Please log in again.");
        } else {
          const errorMessage =
            error.response?.data?.message || "Failed to fetch pond details.";
          console.error("Failed to fetch pond details", error);
          showErrorToast(errorMessage);
          setSelectedPondInfo(null);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [FetchFishTypes, FisherID]
  );

  const handlePondChange = async (selectedOption) => {
    if (isSinglePondDetailMode) return;
    setSelectedPond(selectedOption);
    setSelectedFish([]);
    setFishTypes([]);
    setSelectedPondInfo(null);
    setPondDetails([]);
    setTableAggregates(null);
    exportAggregatesRef.current = null;
    setCollapsedAggregateSections(DEFAULT_AGGREGATE_COLLAPSED);
    setStockingEventsFiltered([]);
    setUseAllData(true);
    setStartDate(null);
    setEndDate(null);

    if (selectedOption?.value) {
      await FetchSelectedPond(selectedOption.value);
    }
  };

  const handleFishChange = (selectedOption) => {
    setSelectedFish(selectedOption || []);
  };

  const applySamplingTagFilter = useCallback(
    (samplingData) => {
      if (!samplingData?.length) return [];
      if (!isSinglePondDetailMode || selectedTagMode?.value === "all") {
        return samplingData;
      }
      if (selectedTagMode?.value === "individual") {
        const tagSet = new Set((selectedTags || []).map((t) => t.value));
        return samplingData.filter((fish) => {
          const t = (fish.TagNumber || "").toString().trim();
          if (!t) return false;
          return tagSet.size === 0 || tagSet.has(t);
        });
      }
      if (selectedTagMode?.value === "grouped") {
        return samplingData.filter((fish) => {
          const t = (fish.TagNumber || "").toString().trim();
          if (t) return true;
          return includeUntagged;
        });
      }
      return samplingData;
    },
    [
      includeUntagged,
      isSinglePondDetailMode,
      selectedTagMode?.value,
      selectedTags,
    ]
  );

  const tableTagOptions = useMemo(() => {
    if (!isSinglePondDetailMode) return [];
    const rows = pondDetails.flatMap((d) => d?.SamplingData || []);
    const tagSet = new Set();
    const selectedFishLabels = new Set((selectedFish || []).map((f) => f.label));
    const hasSpeciesFilter = selectedFishLabels.size > 0;
    rows.forEach((fish) => {
      const fishName = (fish?.FishTypeName || "").toString().trim();
      if (hasSpeciesFilter && !selectedFishLabels.has(fishName)) return;
      const normalizedTag = (fish?.TagNumber || "").toString().trim();
      if (!normalizedTag) return;
      tagSet.add(normalizedTag);
    });
    return Array.from(tagSet)
      .sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
      )
      .map((tag) => ({ label: tag, value: tag }));
  }, [isSinglePondDetailMode, pondDetails, selectedFish]);

  const getUnitSystem = (lengthUnit, weightUnit) => {
    if (!lengthUnit || !weightUnit) return "English"; // Default

    const lUnit = String(lengthUnit).toLowerCase();
    const wUnit = String(weightUnit).toLowerCase();

    if (
      lUnit.includes("mm") ||
      lUnit.includes("millimeter") ||
      wUnit.includes("g") ||
      wUnit.includes("gram")
    ) {
      return "Metric";
    }

    return "English"; // Default to English units
  };

  const formatValue = (value, type, unitSystem, defaultValue = "0") => {
    if (value === null || value === undefined || isNaN(value)) {
      return defaultValue;
    }

    const numValue = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(numValue)) return defaultValue;

    switch (type) {
      case "length":
        return unitSystem === "Metric"
          ? numValue.toFixed(0) // Metric: whole numbers
          : numValue.toFixed(4); // English: 4 decimals

      case "weight":
        return unitSystem === "Metric"
          ? numValue.toFixed(1) // Metric: 1 decimal
          : numValue.toFixed(4); // English: 4 decimals

      case "Ws":
        return unitSystem === "Metric"
          ? numValue.toFixed(1) // Metric: 1 decimal
          : numValue.toFixed(4); // English: 4 decimals

      case "Wr":
      case "averageWr":
      case "stdDevWr":
        return `${(numValue * 100).toFixed(1)}%`;

      default:
        return numValue.toString();
    }
  };

  const formatDate = (date) => {
    if (!date) return null;

    if (typeof date === "string" && date.includes("T")) {
      return date.split("T")[0];
    }

    const dateObj = new Date(date);
    return `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(dateObj.getDate()).padStart(2, "0")}`;
  };

  const fetchStockingEventsForTable = useCallback(
    async (pondId) => {
      const res = await axiosInstance.get(
        `/api/PondStocking/GetByPond/${pondId}`,
        { params: { FisherID } }
      );
      const events = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
      if (isSinglePondDetailMode) {
        const sessionYmd = singlePondDetailTemplate?.Date
          ? formatDate(singlePondDetailTemplate.Date)
          : null;
        if (!sessionYmd) return [];
        return sortStockingEventsByStockingDateAsc(
          filterStockingEventsByGraphRange({
            events,
            useAllData: false,
            startYmd: null,
            endYmd: null,
            singleSessionYmd: sessionYmd,
          })
        );
      }
      const startYmd = !useAllData && startDate ? formatDate(startDate) : null;
      const endYmd = !useAllData && endDate ? formatDate(endDate) : null;
      return sortStockingEventsByStockingDateAsc(
        filterStockingEventsByGraphRange({
          events,
          useAllData,
          startYmd,
          endYmd,
          singleSessionYmd: null,
        })
      );
    },
    [
      FisherID,
      isSinglePondDetailMode,
      singlePondDetailTemplate,
      useAllData,
      startDate,
      endDate,
    ]
  );

  useEffect(() => {
    if (!includeStockingPredators) {
      setStockingEventsFiltered([]);
    }
  }, [includeStockingPredators]);


  const fetchTableAggregates = useCallback(
    async (sessionIds) => {
      const ids = [
        ...new Set(
          (sessionIds || [])
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id) && id > 0)
        ),
      ];
      if (!ids.length) {
        setTableAggregates(null);
        exportAggregatesRef.current = null;
        return null;
      }
      const requestId = (aggregatesRequestIdRef.current += 1);
      setAggregatesLoading(true);
      try {
        const response = await axiosInstance.post(
          "/api/PondDetail/TableAggregates",
          {
            pondDIDs: ids,
            unitSystem: selectedTableUnit?.value === "Metric" ? "Metric" : "English",
            fishTypes: normalizeFishTypesKey(selectedFish),
            includeStocking: includeStockingPredators,
          }
        );
        if (requestId !== aggregatesRequestIdRef.current) {
          return null;
        }
        const data = response.data || null;
        setTableAggregates(data);
        exportAggregatesRef.current = data;
        return data;
      } catch (error) {
        if (requestId !== aggregatesRequestIdRef.current) {
          return null;
        }
        if (isJwtAuthFailure(error)) {
          clearAuthData();
          window.location.href = "/user/login";
          showErrorToast("Session expired. Please log in again.");
        } else {
          const errorMessage =
            error.response?.data?.message ||
            "Failed to load table aggregates.";
          console.error("Failed to load table aggregates", error);
          showErrorToast(errorMessage);
        }
        setTableAggregates(null);
        exportAggregatesRef.current = null;
        return null;
      } finally {
        if (requestId === aggregatesRequestIdRef.current) {
          setAggregatesLoading(false);
        }
      }
    },
    [includeStockingPredators, selectedFish, selectedTableUnit?.value]
  );

  const fetchAdditionalPondData = async (detail) => {
    try {
      const fishTypeIds = selectedFish.map((fish) => fish.value).join(",");

      const [samplingDataResponse, fishTypeResponse, pondNotesResponse, effortUnitsResponse] =
        await Promise.all([
          axiosInstance.get("/api/FishSampling/SamplingData", {
            params: {
              PondDID: detail.id,
              FishTypes: fishTypeIds,
            },
          }),
          axiosInstance.get("/api/SamplingFishes/SamplingFishType", {
            params: {
              PondDID: detail.id,
              FishTypes: fishTypeIds,
            },
          }),
          axiosInstance.get(
            `/api/PondNote/GetAllNotesViaPondDetailID/${detail.id}`,
            {
              params: { search: "" },
            }
          ),
          axiosInstance
            .get(`/api/SamplingEffort/${detail.id}`)
            .catch(() => ({ data: detail.EffortUnits || [] })),
        ]);

      const effortUnits = resolveEffortUnits(detail, effortUnitsResponse?.data);

      return {
        ...detail,
        EffortUnits: effortUnits,
        SamplingData: samplingDataResponse.data || [],
        FishType: fishTypeResponse.data || [],
        PondNotes: pondNotesResponse.data || [],
      };
    } catch (error) {
      if (isJwtAuthFailure(error)) {
        clearAuthData();
        window.location.href = "/user/login";
        showErrorToast("Session expired. Please log in again.");
        return {
          ...detail,
          EffortUnits: detail.EffortUnits ?? [],
          SamplingData: [],
          FishType: [],
          PondNotes: [],
        };
      }
      const errorMessage =
        error.response?.data?.message ||
        "Error fetching additional pond data.";
      console.error("Error fetching additional pond data:", error);
      showErrorToast(errorMessage);
      return {
        ...detail,
        EffortUnits: detail.EffortUnits ?? [],
        SamplingData: [],
        FishType: [],
        PondNotes: [],
      };
    }
  };

  const handleFetchPondSamplingData = async () => {
    if (isSinglePondDetailMode) {
      if (!singlePondDetailTemplate?.id) {
        enqueueSnackbar("Sampling session is still loading. Try again.", {
          variant: "warning",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          autoHideDuration: 2000,
        });
        return;
      }
      try {
        setIsLoading(true);
        const enriched = await fetchAdditionalPondData(
          singlePondDetailTemplate
        );
        let stockingFiltered = [];
        if (includeStockingPredators && singlePondDetailTemplate?.PondID) {
          try {
            stockingFiltered = await fetchStockingEventsForTable(
              singlePondDetailTemplate.PondID
            );
          } catch (stockErr) {
            console.error(
              "Failed to fetch stocking events for table",
              stockErr
            );
            stockingFiltered = [];
          }
        }
        setStockingEventsFiltered(stockingFiltered);
        setPondDetails([enriched]);
      } catch (error) {
        if (isJwtAuthFailure(error)) {
          clearAuthData();
          window.location.href = "/user/login";
          showErrorToast("Session expired. Please log in again.");
        } else {
          const errorMessage =
            error.response?.data?.message || "Failed to fetch pond Details.";
          console.error("Failed to fetch pond Details", error);
          showErrorToast(errorMessage);
          setPondDetails([]);
          setStockingEventsFiltered([]);
        }
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (!selectedPond?.value) {
      enqueueSnackbar("Please select a pond", {
        variant: "warning",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 2000,
      });
      return;
    }

    if (!useAllData && !startDate) {
      enqueueSnackbar("Please select a start date or choose 'ALL Data'", {
        variant: "warning",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 3000,
      });
      return;
    }

    try {
      setIsLoading(true);

      const effectiveFisherId = resolveFisherId(userDetails, selectedPond?.fisherId);
      const params = {
        PondID: selectedPond.value,
      };
      if (effectiveFisherId) {
        params.FisherID = effectiveFisherId;
      }

      // Only include date parameters if not using all data
      if (!useAllData) {
        params.StartDate = formatDate(startDate);
        if (endDate) {
          params.EndDate = formatDate(endDate);
        }
      }

      const response = await axiosInstance.get(
        "/api/PondDetail/GetPondDetails",
        {
          params,
        }
      );

      const pondDetailRows = Array.isArray(response.data) ? response.data : [];
      if (pondDetailRows.length === 0) {
        showErrorToast("No pond details found for the selected criteria");
        setPondDetails([]);
        setStockingEventsFiltered([]);
        return;
      }

      const pondDetailsWithData = await mapWithConcurrency(
        pondDetailRows,
        fetchAdditionalPondData,
        4
      );

      let stockingFiltered = [];
      if (includeStockingPredators && selectedPond?.value) {
        try {
          stockingFiltered = await fetchStockingEventsForTable(
            selectedPond.value
          );
        } catch (stockErr) {
          console.error("Failed to fetch stocking events for table", stockErr);
          stockingFiltered = [];
        }
      }
      setStockingEventsFiltered(stockingFiltered);
      setPondDetails(pondDetailsWithData);
    } catch (error) {
      if (isJwtAuthFailure(error)) {
        clearAuthData();
        window.location.href = "/user/login";
        showErrorToast("Session expired. Please log in again.");
      } else {
        const errorMessage =
          error.response?.data?.message || "Failed to fetch pond Details.";
        console.error("Failed to fetch pond Details", error);
        showErrorToast(errorMessage);
        setPondDetails([]);
        setStockingEventsFiltered([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkTokenValidity();
    if (!isSinglePondDetailMode) {
      FetchPonds();
    }
  }, [FetchPonds, isSinglePondDetailMode]);

  useEffect(() => {
    if (!isSinglePondDetailMode || !pondDetailId) return;
    if (!superUser && !FisherID) return;

    let cancelled = false;
    (async () => {
      try {
        setIsLoading(true);
        const pdRes = superUser
          ? await axiosInstance.get(`/api/PondDetail/GetPondDetailByID`, {
              params: { id: pondDetailId },
            })
          : await axiosInstance.get(`/api/PondDetail/GetPondDetail`, {
              params: { id: pondDetailId, FisherID },
            });
        const pd = pdRes.data;
        if (cancelled) return;
        if (!pd) {
          showErrorToast("Pond detail not found");
          setSinglePondDetailTemplate(null);
          setPondDetails([]);
          return;
        }

        const template = { ...pd, id: pd.id };
        setSinglePondDetailTemplate(template);

        const [pondRes, fishRes, enriched] = await Promise.all([
          axiosInstance.get(`/api/Pond/GetPondByID`, { params: { id: pd.PondID } }),
          axiosInstance.get(`/api/SamplingFishes/GetAllFishesBYPondDID/${pondDetailId}`),
          fetchAdditionalPondData(template),
        ]);

        if (cancelled) return;

        setSelectedPond({
          value: pd.PondID,
          label: pondRes.data?.Name || `Pond ${pd.PondID}`,
        });
        setFishTypes(
          (fishRes.data || []).map((f) => ({
            label: f.FishTypeName,
            value: f.FishTypeID,
          }))
        );
        setPondDetails([enriched]);

        const pond = pondRes.data;
        if (pond) {
          const scopedFisherID = superUser ? pd.FisherID : FisherID;
          let preyFishNames = [];
          let predatorFishNames = [];
          try {
            const [pondFishesResponse, stockingResponse] = await Promise.all([
              axiosInstance.get(`/api/PondFishes/GetAllPondFishes/${pd.PondID}`, {
                params: { search: "" },
              }),
              axiosInstance.get(`/api/PondStocking/GetByPond/${pd.PondID}`, {
                params:
                  scopedFisherID != null && scopedFisherID !== ""
                    ? { FisherID: scopedFisherID }
                    : {},
              }),
            ]);
            const predatorNameSet = new Set();
            const preyNameSet = new Set();
            const pondFishRows = Array.isArray(pondFishesResponse.data)
              ? pondFishesResponse.data
              : pondFishesResponse.data?.data || [];
            pondFishRows.forEach((fish) => {
              const fishName = String(fish?.FishTypeName || "").trim();
              if (fishName) predatorNameSet.add(fishName);
            });
            (stockingResponse.data || []).forEach((event) => {
              (event?.preyRows || []).forEach((prey) => {
                const fishName = stockingRowSpeciesLabel(prey);
                if (fishName && fishName !== "Unknown") preyNameSet.add(fishName);
              });
              (event?.predatorRows || []).forEach((pred) => {
                const fishName =
                  pred?.fishType?.Name || pred?.FishTypeName || "";
                if (fishName) predatorNameSet.add(fishName);
              });
            });
            preyFishNames = Array.from(preyNameSet);
            predatorFishNames = Array.from(predatorNameSet).sort((a, b) =>
              a.localeCompare(b)
            );
          } catch (stockingError) {
            console.error(
              "Failed to fetch pond fishes / stocking for predator list",
              stockingError
            );
          }

          setSelectedPondInfo({
            id: pond.id,
            Name: pond.Name || "N/A",
            Location: pond.Location || "N/A",
            Forage: pond.Forage || "N/A",
            ForageType: pond.ForageType || "N/A",
            Type: pond.Type || "N/A",
            Age: pond.Age ? pond.Age.toString() : "N/A",
            AgeInYears:
              pond.AgeInYears !== undefined && pond.AgeInYears !== null
                ? pond.AgeInYears
                : null,
            Size: pond.Size ? pond.Size.toString() : "N/A",
            Depth: pond.Depth ? pond.Depth.toString() : "N/A",
            EstimatedVolume: pond.EstimatedVolume,
            AquaticVegetationPresent: pond.AquaticVegetationPresent || "N/A",
            WaterSupply: pond.WaterSupply || "N/A",
            PreyFishNames: preyFishNames,
            PredatorFishNames: predatorFishNames,
            FisherID: scopedFisherID,
          });
        }
      } catch (error) {
        if (cancelled) return;
        if (isJwtAuthFailure(error)) {
          clearAuthData();
          window.location.href = "/user/login";
          showErrorToast("Session expired. Please log in again.");
        } else {
          const errorMessage =
            error.response?.data?.message || "Failed to load sampling session.";
          showErrorToast(errorMessage);
          setSinglePondDetailTemplate(null);
          setPondDetails([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [FisherID, isSinglePondDetailMode, pondDetailId, selectedFish, superUser]);

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return formatDisplayDate(dateString);
    } catch {
      return "N/A";
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "N/A";
    return timeString;
  };

  const downloadFile = async (blob, filename, mimeType) => {
    // Detect if the user is on a mobile device
    const isMobile =
      /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );

    try {
      if (isMobile) {
        // For mobile devices, upload to server and redirect to file URL
        const formData = new FormData();
        formData.append("file", blob, filename);

        const response = await axiosInstance.post(
          "api/files/upload",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );

        if (response.data.error) {
          throw new Error(response.data.error);
        }

        const fileUrl = response.data.url;
        window.open(fileUrl, "_blank");
      } else {
        // For desktop devices, trigger direct download
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.style.display = "none";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      }
    } catch (error) {
      console.error("Download failed:", error);

      // Fallback for both mobile and desktop
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result;
        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = filename;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        enqueueSnackbar(
          `Download failed. Use this URL to download: ${dataUrl}`,
          {
            variant: "error",
            anchorOrigin: { vertical: "top", horizontal: "right" },
            autoHideDuration: 5000,
          }
        );
      };
      reader.readAsDataURL(blob);
    }
  };

  function calculateFishingDuration(startTime, stopTime, detail = null) {
    if (detail) {
      return formatSessionOnSiteDuration(detail);
    }
    if (
      !startTime ||
      !stopTime ||
      startTime === "N/A" ||
      stopTime === "N/A"
    ) {
      return "N/A";
    }
    return formatDurationHoursAsHm(durationHoursFromRange(startTime, stopTime));
  }

  const formatDecimal = (value, defaultValue = "0") => {
    if (value === null || value === undefined || isNaN(value)) {
      return defaultValue;
    }

    // Convert to number if it's a string
    const numValue = typeof value === "string" ? parseFloat(value) : value;

    // If it's a whole number, return without decimal places
    if (numValue % 1 === 0) {
      return String(numValue);
    }

    // For decimal numbers, format to 4 places and remove trailing zeros
    return String(numValue.toFixed(4).replace(/\.?0+$/, ""));
  };


  const renderSamplingDataTable = (
    samplingData,
    unitSystem,
    defaultCaptureMethod = null,
    displayOpts = null
  ) => {
    if (!samplingData || samplingData.length === 0) {
      return (
        <div className="text-center py-3">
          <p className="text-muted">No Fishing/Sampling data available</p>
        </div>
      );
    }

    const conv =
      displayOpts?.detail && displayOpts?.displayUnit
        ? getConversionsForDisplay(displayOpts.detail, displayOpts.displayUnit)
        : null;
    const displayUnitKey = displayOpts?.displayUnit?.value ?? unitSystem;

    return (
      <div className="table-responsive" style={MODERN_TABLE_WRAPPER_STYLE}>
        <table className={MODERN_TABLE_CLASS}>
          <thead className={MODERN_TABLE_HEAD_CLASS} style={MODERN_TABLE_HEAD_STYLE}>
            <tr>
              <th className="text-center" style={MODERN_TABLE_CELL_STYLE}>Number</th>
              <th className="text-center" style={MODERN_TABLE_CELL_STYLE}>Species</th>
              <th className="text-center" style={MODERN_TABLE_CELL_STYLE}>Length</th>
              <th className="text-center" style={MODERN_TABLE_CELL_STYLE}>Weight</th>
              <th className="text-center" style={MODERN_TABLE_CELL_STYLE}>
                <WsLabel />
              </th>
              <th className="text-center" style={MODERN_TABLE_CELL_STYLE}>
                <WrLabel />
              </th>
              <th className="text-center" style={MODERN_TABLE_CELL_STYLE}>Tag Number</th>
              <th className="text-center" style={MODERN_TABLE_CELL_STYLE}>Capture Method</th>
              <th className="text-center" style={MODERN_TABLE_CELL_STYLE}>Culled</th>
              <th className="text-center" style={MODERN_TABLE_CELL_STYLE}>Released</th>
            </tr>
          </thead>
          <tbody>
            {samplingData.map((fish, index) => (
              <tr key={fish.FishSamplingID || index}>
                <td className="text-center" style={MODERN_TABLE_CELL_STYLE}>{index + 1}</td>
                <td style={MODERN_TABLE_CELL_STYLE}>
                  <SpeciesTableLabel
                    name={fish.FishTypeName}
                    abbreviation={
                      fish.FishTypeAbbreviation || fish.Abbreviation
                    }
                  />
                </td>
                <td className="text-center" style={MODERN_TABLE_CELL_STYLE}>
                  {formatValue(
                    conv
                      ? conv.convertLength(fish.Length)
                      : fish.Length,
                    "length",
                    displayUnitKey,
                    "0"
                  )}
                </td>
                <td className="text-center" style={MODERN_TABLE_CELL_STYLE}>
                  {formatValue(
                    conv
                      ? conv.convertWeight(fish.Weight)
                      : fish.Weight,
                    "weight",
                    displayUnitKey,
                    "0"
                  )}
                </td>
                <td className="text-center" style={MODERN_TABLE_CELL_STYLE}>
                  {formatValue(
                    conv ? conv.convertWeight(fish.Ws) : fish.Ws,
                    "Ws",
                    displayUnitKey,
                    "0"
                  )}
                </td>
                <td className="text-center" style={MODERN_TABLE_CELL_STYLE}>
                  {formatValue(fish.Wr, "Wr", displayUnitKey, "0.000")}
                </td>
                <td className="text-center" style={MODERN_TABLE_CELL_STYLE}>{fish.TagNumber || "N/A"}</td>
                <td className="text-center" style={MODERN_TABLE_CELL_STYLE}>
                  {fish.CaptureMethod ||
                    fish.captureMethod ||
                    defaultCaptureMethod ||
                    "N/A"}
                </td>
                <td className="text-center" style={MODERN_TABLE_CELL_STYLE}>
                  {fish.Status === "Culled" ? "Yes" : "No"}
                </td>
                <td className="text-center" style={MODERN_TABLE_CELL_STYLE}>
                  {fish.Status === "Released" ? "Yes" : "No"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };


  /**
   * Tagged fish matrix + bin/overall summaries come from TableAggregates (all Fetch sessions).
   */
  const pondDetailIdsKey = useMemo(
    () =>
      (pondDetails || [])
        .map((d) => Number(d?.id))
        .filter((id) => Number.isFinite(id) && id > 0)
        .join(","),
    [pondDetails]
  );

  const fishTypesKey = useMemo(
    () => normalizeFishTypesKey(selectedFish),
    [selectedFish]
  );

  useEffect(() => {
    if (!pondDetailIdsKey) {
      setTableAggregates(null);
      exportAggregatesRef.current = null;
      return;
    }
    const ids = pondDetailIdsKey.split(",").map((x) => Number(x));
    fetchTableAggregates(ids);
  }, [
    pondDetailIdsKey,
    fishTypesKey,
    includeStockingPredators,
    selectedTableUnit?.value,
    fetchTableAggregates,
  ]);

  const taggedFishTrackingPivot = useMemo(() => {
    const tracking = tableAggregates?.taggedFishTracking;
    if (!tracking) {
      return {
        orderedDates: [],
        sortedTags: [],
        getEntry: () => null,
      };
    }
    const cells = tracking.cells || {};
    return {
      orderedDates: tracking.orderedDates || [],
      sortedTags: tracking.sortedTags || [],
      getEntry: (tagNumber, dateKey) =>
        cells[tagNumber]?.[dateKey] ?? null,
    };
  }, [tableAggregates]);

  const overallSpeciesSummary = useMemo(
    () =>
      Array.isArray(tableAggregates?.overallSpeciesSummary)
        ? tableAggregates.overallSpeciesSummary
        : [],
    [tableAggregates]
  );

  const allSessionsSpeciesBins = useMemo(
    () =>
      Array.isArray(tableAggregates?.speciesBins)
        ? tableAggregates.speciesBins
        : [],
    [tableAggregates]
  );

  /**
   * Tagged fish matrix: dates as rows, tag numbers as column groups (L / W / Wr).
   * Matches export layout; avoids unbounded horizontal growth from one column group per date.
   */
  const buildTaggedFishTrackingPivot = () => {
    const dateMetaByKey = new Map();
    const groupedByTag = new Map();

    pondDetails.forEach((detail) => {
      const dateKey = formatDate(detail.Date);
      if (!dateKey) return;

      if (!dateMetaByKey.has(dateKey)) {
        dateMetaByKey.set(dateKey, {
          key: dateKey,
          label: formatDateTime(detail.Date),
        });
      }

      const unitSystem = selectedTableUnit.value;
      const conv = detail
        ? getConversionsForDisplay(detail, selectedTableUnit)
        : null;

      const filteredRows = applySamplingTagFilter(detail.SamplingData || []);

      filteredRows.forEach((fish) => {
        const normalizedTag = (fish?.TagNumber || "").toString().trim();
        if (!normalizedTag) return;

        if (!groupedByTag.has(normalizedTag)) {
          groupedByTag.set(normalizedTag, []);
        }

        const entry = {
          dateKey,
          length: conv
            ? conv.convertLength(fish.Length)
            : Number(fish.Length),
          weight: conv
            ? conv.convertWeight(fish.Weight)
            : Number(fish.Weight),
          wr: Number(fish.Wr),
          unitSystem,
        };
        groupedByTag.get(normalizedTag).push(entry);
      });
    });

    if (includeStockingPredators && Array.isArray(stockingEventsSorted) && stockingEventsSorted.length) {
      const displayUnitKey = selectedTableUnit.value;

      stockingEventsSorted.forEach((event) => {
        if (!event?.IsInitial) return;
        const dateKey = stockingEventYmdKey(event);
        if (!dateKey) return;

        if (!dateMetaByKey.has(dateKey)) {
          dateMetaByKey.set(dateKey, {
            key: dateKey,
            label: formatDateTime(event.StockingDate),
          });
        }

        const convStock = stockingEventConversionsToDisplayUnit(event, displayUnitKey);
        const unitSystem = displayUnitKey === "Metric" ? "Metric" : "English";
        const predatorRows = Array.isArray(event.predatorRows) ? event.predatorRows : [];

        predatorRows.forEach((row) => {
          const normalizedTag = (row?.Tag ?? "").toString().trim();
          if (!normalizedTag) return;

          if (!groupedByTag.has(normalizedTag)) {
            groupedByTag.set(normalizedTag, []);
          }
          const list = groupedByTag.get(normalizedTag);
          if (list.some((e) => e.dateKey === dateKey)) return;

          const lenRaw =
            row.Length !== undefined && row.Length !== null && row.Length !== ""
              ? Number(row.Length)
              : NaN;
          const wtRaw =
            row.Weight !== undefined && row.Weight !== null && row.Weight !== ""
              ? Number(row.Weight)
              : NaN;
          const wrRaw =
            row.Wr !== undefined && row.Wr !== null && row.Wr !== "" ? Number(row.Wr) : NaN;

          list.push({
            dateKey,
            length: Number.isFinite(lenRaw) ? convStock.convertLength(lenRaw) : NaN,
            weight: Number.isFinite(wtRaw) ? convStock.convertWeight(wtRaw) : NaN,
            wr: Number.isFinite(wrRaw) ? wrRaw : NaN,
            unitSystem,
          });
        });
      });
    }

    const orderedDates = Array.from(dateMetaByKey.values()).sort((a, b) =>
      a.key.localeCompare(b.key)
    );

    const sortedTags = Array.from(groupedByTag.keys()).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
    );

    const getEntry = (tagNumber, dateKey) => {
      const entries = groupedByTag.get(tagNumber);
      if (!entries?.length) return null;
      let hit = null;
      entries.forEach((entry) => {
        if (entry.dateKey === dateKey) hit = entry;
      });
      return hit;
    };

    return { orderedDates, sortedTags, getEntry };
  };

  const renderTaggedFishTrackingTable = () => {
    const { orderedDates, sortedTags, getEntry } = taggedFishTrackingPivot;

    if (!sortedTags.length) {
      return (
        <div className="text-center py-3">
          <p className="text-muted">No tagged fish found for selected criteria</p>
        </div>
      );
    }

    const taggedDateColStickyTh = {
      position: "sticky",
      left: 0,
      zIndex: 4,
      background: "var(--tv-table-sticky-head-bg, linear-gradient(180deg, #f1f5f9 0%, #e2e8f0 100%))",
      boxShadow: "4px 0 10px -4px rgba(15, 23, 42, 0.2)",
      borderRight: "2px solid var(--tv-table-cell-border, #cbd5e1)",
      minWidth: "11rem",
      maxWidth: "14rem",
      textAlign: "left",
    };

    const taggedDateColStickyTd = {
      position: "sticky",
      left: 0,
      zIndex: 3,
      backgroundColor: "var(--tv-table-sticky-bg, #ffffff)",
      boxShadow: "4px 0 10px -4px rgba(15, 23, 42, 0.15)",
      borderRight: "2px solid var(--tv-table-cell-border, #cbd5e1)",
      minWidth: "11rem",
      maxWidth: "14rem",
      textAlign: "left",
      verticalAlign: "middle",
    };

    return (
      <div
        className="table-responsive"
        style={{
          ...MODERN_TABLE_WRAPPER_STYLE,
          maxWidth: "100%",
          overflowX: "auto",
          overflowY: "visible",
          WebkitOverflowScrolling: "touch",
          position: "relative",
        }}
      >
        <table
          className={MODERN_TABLE_CLASS}
          style={{ minWidth: "max-content", borderCollapse: "separate", borderSpacing: 0 }}
        >
          <thead className={MODERN_TABLE_HEAD_CLASS} style={MODERN_TABLE_HEAD_STYLE}>
            <tr>
              <th
                className="align-middle"
                style={{ ...MODERN_TABLE_CELL_STYLE, ...taggedDateColStickyTh }}
                rowSpan={2}
              >
                Date
              </th>
              {sortedTags.map((tag) => (
                <th
                  key={`tag-h-${tag}`}
                  className="text-center"
                  style={{
                    ...MODERN_TABLE_CELL_STYLE,
                    borderLeft: TAG_GROUP_DIVIDER_STYLE,
                  }}
                  colSpan={3}
                >
                  {tag}
                </th>
              ))}
            </tr>
            <tr>
              {sortedTags.map((tag) => (
                <Fragment key={`tag-sub-${tag}`}>
                  <th
                    className="text-center"
                    style={{
                      ...MODERN_TABLE_CELL_STYLE,
                      borderLeft: TAG_GROUP_DIVIDER_STYLE,
                    }}
                  >
                    Lgt
                  </th>
                  <th className="text-center" style={MODERN_TABLE_CELL_STYLE}>
                    Wgt
                  </th>
                  <th className="text-center" style={MODERN_TABLE_CELL_STYLE}>
                    <WrLabel />
                  </th>
                </Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {orderedDates.map((d, rowIdx) => (
              <tr key={`dt-${d.key}`}>
                <td
                  className="fw-semibold"
                  style={{
                    ...MODERN_TABLE_CELL_STYLE,
                    ...taggedDateColStickyTd,
                    backgroundColor: rowIdx % 2 === 1 ? "var(--tv-table-row-alt, #f8fafc)" : "var(--tv-table-wrap-bg, #ffffff)",
                  }}
                >
                  {d.label}
                </td>
                {sortedTags.map((tag) => {
                  const v = getEntry(tag, d.key);
                  if (!v) {
                    return (
                      <Fragment key={`e-${d.key}-${tag}`}>
                        <td
                          className="text-center"
                          style={{
                            ...MODERN_TABLE_CELL_STYLE,
                            borderLeft: TAG_GROUP_DIVIDER_STYLE,
                          }}
                        >
                          -
                        </td>
                        <td className="text-center" style={MODERN_TABLE_CELL_STYLE}>
                          -
                        </td>
                        <td className="text-center" style={MODERN_TABLE_CELL_STYLE}>
                          -
                        </td>
                      </Fragment>
                    );
                  }
                  return (
                    <Fragment key={`v-${d.key}-${tag}`}>
                      <td
                        className="text-center"
                        style={{
                          ...MODERN_TABLE_CELL_STYLE,
                          borderLeft: TAG_GROUP_DIVIDER_STYLE,
                        }}
                      >
                        {formatTaggedLgtWgtCell(v.length)}
                      </td>
                      <td className="text-center" style={MODERN_TABLE_CELL_STYLE}>
                        {formatTaggedLgtWgtCell(v.weight)}
                      </td>
                      <td className="text-center" style={MODERN_TABLE_CELL_STYLE}>
                        {formatValue(v.wr, "Wr", v.unitSystem, "-")}
                      </td>
                    </Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderFishTypeSummary = (
    fishTypeData,
    unitSystem,
    samplingData = [],
    fishingStartTime = null,
    fishingStopTime = null,
    numberOfAnglers = 0,
    sessionDetail = null
  ) => {
    const fishingDurationHours = sessionDetail
      ? resolveSessionOnSiteTimes(sessionDetail).hours
      : parseToDecimalHours(fishingStartTime, fishingStopTime);

    if (!fishTypeData || fishTypeData.length === 0) {
      return (
        <div className="text-center py-3">
          <p className="text-muted">No fish type summary available</p>
        </div>
      );
    }

    // Filter out fish types with 0 fish
    const filteredData = fishTypeData.filter(
      (summary) => summary.NumberOfFishes > 0
    );

    if (filteredData.length === 0) {
      return (
        <div className="text-center py-3">
          <p className="text-muted">No fish with sampling data available</p>
        </div>
      );
    }

    return (
      <div className="table-responsive" style={MODERN_TABLE_WRAPPER_STYLE}>
        <table className={MODERN_TABLE_CLASS}>
          <thead className={MODERN_TABLE_HEAD_CLASS} style={MODERN_TABLE_HEAD_STYLE}>
            <tr>
              <th>Specie</th>
              <th>{LABEL.NUM}</th>
              <th>
                Avg <WrLabel />
              </th>
              <th>
                {LABEL.STD_DEV} <WrLabel />
              </th>
              <th>Total Culled</th>
              <th>Total Released</th>
              <th>{LABEL.NUM_ANGLERS}</th>
              <th>{LABEL.FISH_CAUGHT_PER_ANGLER_HR}</th>
              <th>Seine</th>
              <th>Gillnet</th>
              <th>Fyke Net</th>
              <th>{displayMethodLabel("Electrofishing")}</th>
              <th>Longest Fish (L/W)</th>
              <th>Heaviest Fish (L/W)</th>
              <th>Shortest Fish (L/W)</th>
              <th>Lightest Fish (L/W)</th>
            </tr>
          </thead>
          <tbody>
            {fishTypeData.map((summary, index) => {
              const { biggest, smallest, heaviest, lightest } =
                getBiggestSmallestHeaviestLightestFish(
                  summary.FishTypeName,
                  samplingData
                );
              const captureMethodCounts = getCaptureMethodCounts(
                samplingData,
                summary.FishTypeName || "Unknown"
              );
              const anglersCount = Number(numberOfAnglers) || 0;
              const anglingFishCount = captureMethodCounts.angling;
              const fishCaughtPerHourAngling =
                anglersCount > 0 && fishingDurationHours > 0 && anglingFishCount > 0
                  ? (
                      anglingFishCount /
                      fishingDurationHours /
                      anglersCount
                    ).toFixed(2)
                  : "-";
              return (
                summary.NumberOfFishes > 0 && (
                  <tr key={summary.PondFishesID || index}>
                    <td>
                      <SpeciesTableLabel
                        name={summary.FishTypeName}
                        abbreviation={
                          summary.FishTypeAbbreviation || summary.Abbreviation
                        }
                      />
                    </td>
                    <td>{summary.NumberOfFishes || 0}</td>
                    <td>
                      {formatValue(
                        summary.AverageWr,
                        "averageWr",
                        unitSystem,
                        "0.000"
                      )}
                    </td>
                    <td>
                      {formatValue(
                        summary.StandardDeviationWr,
                        "stdDevWr",
                        unitSystem,
                        "0.000"
                      )}
                    </td>
                    <td>{summary.NumberOfFishesCulled || 0}</td>
                    <td>{summary.NumberOfFishesReleased || 0}</td>
                    <td>{anglersCount}</td>
                    <td>{fishCaughtPerHourAngling}</td>
                    <td>{captureMethodCounts.seine}</td>
                    <td>{captureMethodCounts.gillnet}</td>
                    <td>{captureMethodCounts.fykeNet}</td>
                    <td>{captureMethodCounts.electrofishing}</td>
                    <td>
                      {biggest
                        ? formatSpeciesExtremeLW(biggest.Length, biggest.Weight)
                        : "-"}
                    </td>
                    <td>
                      {heaviest
                        ? formatSpeciesExtremeLW(heaviest.Length, heaviest.Weight)
                        : "-"}
                    </td>
                    <td>
                      {smallest
                        ? formatSpeciesExtremeLW(smallest.Length, smallest.Weight)
                        : "-"}
                    </td>
                    <td>
                      {lightest
                        ? formatSpeciesExtremeLW(lightest.Length, lightest.Weight)
                        : "-"}
                    </td>
                  </tr>
                )
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const calculateOverallSpeciesSummary = () => {
    if (!pondDetails || !Array.isArray(pondDetails)) return [];

    // Aggregate data by FishTypeName
    const speciesMap = new Map();

    pondDetails.forEach((detail) => {
      if (detail.FishType && Array.isArray(detail.FishType)) {
        detail.FishType.forEach((summary) => {
          const fishTypeName = summary?.FishTypeName || "Unknown";
          const numFishes = Number(summary?.NumberOfFishes) || 0;

          // Only aggregate entries that actually have fishes counted
          if (numFishes > 0) {
            if (!speciesMap.has(fishTypeName)) {
              speciesMap.set(fishTypeName, {
                FishTypeName: fishTypeName,
                NumberOfFishes: 0,
                AverageWr: [],
                NumberOfFishesCulled: 0,
                NumberOfFishesReleased: 0,
              });
            }

            const current = speciesMap.get(fishTypeName);
            current.NumberOfFishes += numFishes;
            if (Number.isFinite(Number(summary?.AverageWr))) {
              current.AverageWr.push(Number(summary.AverageWr));
            }
            current.NumberOfFishesCulled += Number(summary?.NumberOfFishesCulled) || 0;
            current.NumberOfFishesReleased += Number(summary?.NumberOfFishesReleased) || 0;
          }
        });
      }
    });

    // Convert map to array and calculate averages and standard deviations
    const summaryArray = Array.from(speciesMap.values()).map((species) => {
      const count = species.AverageWr.length;
      const avgWr =
        count > 0
          ? species.AverageWr.reduce((sum, val) => sum + (val || 0), 0) / count
          : 0;
      const stdDevWr =
        count > 0
          ? Math.sqrt(
            species.AverageWr.reduce(
              (sum, val) => sum + Math.pow((val || 0) - avgWr, 2),
              0
            ) / count
          )
          : 0;

      return {
        FishTypeName: species.FishTypeName,
        NumberOfFishes: species.NumberOfFishes,
        AverageWr: avgWr,
        StandardDeviationWr: stdDevWr,
        NumberOfFishesCulled: species.NumberOfFishesCulled,
        NumberOfFishesReleased: species.NumberOfFishesReleased,
      };
    });

    return summaryArray;
  };

  const renderPondNote = (pondNote) => {
    if (!pondNote || pondNote.length === 0) {
      return (
        <div className="text-center py-3">
          <p className="text-muted">No Notes Available</p>
        </div>
      );
    }

    return (
      <div className="table-responsive">
        {pondNote.map((summary, index) => (
          <p
            key={index}
            style={{
              wordWrap: "break-word",
              whiteSpace: "normal",
              overflowWrap: "break-word",
              maxWidth: "100%",
            }}
          >
            {extractPondNoteBodyFromApiRow(summary) || "—"}
          </p>
        ))}
      </div>
    );
  };

  const tableContent = (
    <div className="donezo-table-view">
      {!isSinglePondDetailMode && (
        <div className="d-flex align-items-center mb-3">
          <div>
            <ul className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/">Home</Link>
              </li>
              <li className="breadcrumb-item active">Table View</li>
            </ul>
            <h1 className="page-header mb-0">Table View</h1>
          </div>
          <div className="ms-auto"></div>
        </div>
      )}

      {/* Data selection controls */}
      <div className="donezo-sampling-filter">
        <div className="donezo-sampling-filter__accent" />
        <div className="donezo-sampling-filter__body">
          {/* ── ROW 1 : Pond · Fish · Date Range (pond & date hidden for single PondDetail URL) ── */}
          <div className="row g-2 align-items-end">
            {!isSinglePondDetailMode && (
              <div className="col-12 col-md-3">
                <div style={samplingFilterLabelRowStyle}>
                  <Waves size={13} color={SAMPLING_THEME.iconPond} strokeWidth={2.5} />
                  <span style={samplingFilterLabelStyle}>Pond</span>
                </div>
                <Select
                  onChange={handlePondChange}
                  options={ponds}
                  value={selectedPond}
                  styles={filterSelectStyles}
                  menuPortalTarget={document.body}
                  isMulti={false}
                  placeholder="Select a pond..."
                  isClearable
                />
              </div>
            )}
            <div className={isSinglePondDetailMode ? "col-12 col-md-6" : "col-12 col-md-2"}>
              <div style={samplingFilterLabelRowStyle}>
                <Fish size={13} color={SAMPLING_THEME.iconFish} strokeWidth={2.5} />
                <span style={samplingFilterLabelStyle}>Fish Species</span>
              </div>
              <Select
                onChange={handleFishChange}
                options={fishTypes}
                value={selectedFish}
                styles={filterSelectStyles}
                menuPortalTarget={document.body}
                isMulti={true}
                placeholder="Select fish..."
                isClearable
              />
            </div>
            {!isSinglePondDetailMode && (
              <div className="col-12 col-md-2">
                <div style={samplingFilterLabelRowStyle}>
                  <Ruler size={13} color={SAMPLING_THEME.iconNeutral} strokeWidth={2.5} />
                  <span style={samplingFilterLabelStyle}>Units</span>
                </div>
                <Select
                  onChange={(option) => setSelectedTableUnit(option)}
                  options={[
                    { label: "English System", value: "English" },
                    { label: "Metric System", value: "Metric" },
                  ]}
                  styles={filterSelectStyles}
                  menuPortalTarget={document.body}
                  placeholder="Units..."
                  value={selectedTableUnit}
                />
              </div>
            )}
            {!isSinglePondDetailMode && (
              <div className="col-12 col-md-5">
                <div style={samplingFilterLabelRowStyle}>
                  <CalendarRange size={13} color={SAMPLING_THEME.iconDate} strokeWidth={2.5} />
                  <span style={samplingFilterLabelStyle}>Date Range</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <label
                    htmlFor="useAllDataTable"
                    style={samplingCheckboxLabelStyle(useAllData)}
                  >
                    <input
                      type="checkbox"
                      id="useAllDataTable"
                      checked={useAllData}
                      onChange={(e) => {
                        setUseAllData(e.target.checked);
                        if (e.target.checked) {
                          setStartDate(null);
                          setEndDate(null);
                        }
                      }}
                      style={samplingCheckboxInputStyle}
                    />
                    <span style={samplingCheckboxTextStyle(useAllData)}>
                      ALL Data
                    </span>
                  </label>
                  {!useAllData && (
                    <>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <CustomDateTimePicker
                          label=""
                          mode="date"
                          value={startDate}
                          onChange={(date) => setStartDate(date)}
                          ampm={true}
                          placeholder="Start date"
                        />
                      </div>
                      <span style={{ color: "#cbd5e1", fontSize: 14, fontWeight: 500, flexShrink: 0 }}>—</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <CustomDateTimePicker
                          label=""
                          mode="date"
                          value={endDate}
                          onChange={(date) => setEndDate(date)}
                          placeholder="End date"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── Divider ── */}
          <div className="donezo-sampling-filter__divider" />

          {isSinglePondDetailMode && (
            <>
              <div className="row g-2 align-items-end">
                <div className="col-6 col-md-2">
                  <div className="donezo-sampling-filter__label-row">
                    <Tags size={13} color="#f59e0b" strokeWidth={2.5} />
                    <span className="donezo-sampling-filter__label">Tags</span>
                  </div>
                  <Select
                    onChange={(option) => {
                      setSelectedTagMode(option);
                      setSelectedTags([]);
                    }}
                    options={[
                      { label: "All Fish", value: "all" },
                      { label: "Individual Tags", value: "individual" },
                      { label: "Grouped Tags", value: "grouped" },
                    ]}
                    value={selectedTagMode}
                    styles={filterSelectStyles}
                    menuPortalTarget={document.body}
                    placeholder="Mode..."
                    isClearable={false}
                  />
                </div>
                <div className="col-6 col-md-2">
                  <div className="donezo-sampling-filter__label-row">
                    <Ruler size={13} color="var(--sf-label, #64748b)" strokeWidth={2.5} />
                    <span className="donezo-sampling-filter__label">Units</span>
                  </div>
                  <Select
                    onChange={(option) => setSelectedTableUnit(option)}
                    options={[
                      { label: "English System", value: "English" },
                      { label: "Metric System", value: "Metric" },
                    ]}
                    styles={filterSelectStyles}
                    menuPortalTarget={document.body}
                    placeholder="Units..."
                    value={selectedTableUnit}
                  />
                </div>
              </div>
              {selectedTagMode?.value === "individual" && (
                <div className="row g-2 mt-1">
                  <div className="col-12 col-md-6">
                    <div className="donezo-sampling-filter__label-row">
                      <Hash size={13} color="#f43f5e" strokeWidth={2.5} />
                      <span className="donezo-sampling-filter__label">Tag Numbers</span>
                    </div>
                    <Select
                      isMulti
                      options={tableTagOptions}
                      value={selectedTags}
                      onChange={(option) => setSelectedTags(option || [])}
                      noOptionsMessage={() =>
                        pondDetails.length === 0
                          ? "Load data first (fish tags appear after fetch)"
                          : "No tag numbers for current filters"
                      }
                      styles={filterSelectStyles}
                      menuPortalTarget={document.body}
                      placeholder="Select tag numbers..."
                      closeMenuOnSelect={false}
                      isClearable
                    />
                  </div>
                </div>
              )}
              {selectedTagMode?.value === "grouped" && (
                <div className="row g-2 mt-1">
                  <div className="col-12 col-md-3">
                    <label
                      htmlFor="includeUntaggedTable"
                      className={`donezo-sampling-filter__untagged${includeUntagged ? " is-active" : ""}`}
                    >
                      <input
                        type="checkbox"
                        id="includeUntaggedTable"
                        checked={includeUntagged}
                        onChange={(e) => setIncludeUntagged(e.target.checked)}
                        style={{ width: 15, height: 15, accentColor: "#f59e0b", cursor: "pointer" }}
                      />
                      <span className="donezo-sampling-filter__untagged-text">
                        Include untagged
                      </span>
                    </label>
                  </div>
                </div>
              )}
              <div className="donezo-sampling-filter__divider" />
            </>
          )}

          {/* ── ROW 2 : Stocking option + Fetch + Export ── */}
          {(selectedPond?.value || isSinglePondDetailMode) && (
            <div className="row g-2 mb-2">
              <div className="col-12">
                <label
                  htmlFor="includeStockingPredatorsTable"
                  className={`donezo-sampling-filter__chip${includeStockingPredators ? " is-active" : ""}`}
                >
                  <input
                    type="checkbox"
                    id="includeStockingPredatorsTable"
                    checked={includeStockingPredators}
                    onChange={(e) =>
                      setIncludeStockingPredators(e.target.checked)
                    }
                    style={samplingCheckboxInputStyle}
                  />
                  <Fish size={14} color={SAMPLING_THEME.iconFish} strokeWidth={2.5} />
                  <span className="donezo-sampling-filter__chip-text">
                    Include stocking events data (apply on Fetch)
                  </span>
                </label>
              </div>
            </div>
          )}
          <div className="row g-2 align-items-end">
            <div className="col-12 col-md-3 d-flex align-items-end" style={{ gap: 8 }}>
              <button
                type="button"
                onClick={handleFetchPondSamplingData}
                disabled={isLoading}
                style={{
                  ...samplingFetchButtonStyle,
                  opacity: isLoading ? 0.7 : 1,
                  cursor: isLoading ? "wait" : "pointer",
                }}
              >
                <Play size={14} strokeWidth={2.5} />
                Fetch
              </button>
              {pondDetails.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => handleExport("excel")}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6,
                      height: 36,
                      padding: "0 14px",
                      borderRadius: 8,
                      border: "1.5px solid #d1fae5",
                      background: "#ecfdf5",
                      color: "#059669",
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                  >
                    <FileSpreadsheet size={14} strokeWidth={2.5} />
                    XLSX
                  </button>
                  <button
                    type="button"
                    onClick={() => handleExport("pdf")}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6,
                      height: 36,
                      padding: "0 14px",
                      borderRadius: 8,
                      border: "1.5px solid var(--sf-control-border, #e2e8f0)",
                      background: "var(--sf-control-bg, #fff)",
                      color: "var(--sf-control-text, #475569)",
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                  >
                    <Download size={14} strokeWidth={2.5} />
                    PDF
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Show merged view when data is fetched */}
      {pondDetails.length > 0 && (
        <MergedViewSection
          pondDetails={pondDetails}
          selectedPondInfo={selectedPondInfo}
          getUnitSystem={getUnitSystem}
          formatDateTime={formatDateTime}
          formatTime={formatTime}
          calculateFishingDuration={calculateFishingDuration}
          renderSamplingDataTable={renderSamplingDataTable}
          renderFishTypeSummary={renderFishTypeSummary}
          renderPondNote={renderPondNote}
          calculateOverallSpeciesSummary={calculateOverallSpeciesSummary}
          renderTaggedFishTrackingTable={renderTaggedFishTrackingTable}
        />
      )}
    </div>
  );

  if (isSinglePondDetailMode && pondDetailId) {
    return (
      <div className="donezo-page donezo-workflow-page py-3">
        <div className="container-fluid px-3 px-md-4">
          <PondDetailWorkflowPageHeader
            pondDetailId={pondDetailId}
            section="table"
            detailName={
              singlePondDetailTemplate?.Name || selectedPond?.label || ""
            }
          />
          <PondDetailWorkflowTabs id={pondDetailId} active="table" />
          {isLoading ? <Preloader /> : tableContent}
        </div>
      </div>
    );
  }

  return isLoading ? <Preloader /> : tableContent;

  // --- Merged View Section (combines Normal and Bin views) ---
  function MergedViewSection({
    pondDetails,
    selectedPondInfo,
    getUnitSystem,
    formatDateTime,
    formatTime,
    calculateFishingDuration,
    renderSamplingDataTable,
    renderFishTypeSummary,
    renderPondNote,
    calculateOverallSpeciesSummary,
    renderTaggedFishTrackingTable,
  }) {
    const stockingDisplayUnit = selectedTableUnit.value;
    const normalizeSpecies = stockingRowSpeciesLabel;
    const selectedFishSet = new Set((selectedFish || []).map((f) => f.label));
    const hasSelectedFishFilter = selectedFishSet.size > 0;
    const isSelectedSpecies = (name) =>
      !hasSelectedFishFilter || selectedFishSet.has(name);
    const parseNum = (v) => {
      const n = typeof v === "string" ? parseFloat(v) : Number(v);
      return Number.isFinite(n) ? n : null;
    };
    const computePredatorSummary = (rows) => {
      const map = new Map();
      rows.forEach((row) => {
        const filterKey = normalizeSpecies(row);
        if (!isSelectedSpecies(filterKey)) return;
        if (!map.has(filterKey)) {
          map.set(filterKey, {
            species: stockingRowSpeciesDisplayLabel(row),
            speciesFullName: stockingRowSpeciesLabel(row),
            speciesAbbreviation:
              row?.fishType?.Abbreviation ||
              row?.FishTypeAbbreviation ||
              row?.Abbreviation ||
              "",
            count: 0,
            wrVals: [],
            lenVals: [],
            wtVals: [],
            tagged: 0,
            batchTotalWeight: 0,
            batchLenNumerator: 0,
            batchLenDen: 0,
          });
        }
        const entry = map.get(filterKey);
        if (stockingPredatorRowIsBatch(row)) {
          const n = stockingPredatorRowCountDelta(row);
          const tw = parseNum(row?.TotalWeight);
          const len = parseNum(row?.Length);
          entry.count += n;
          if (tw !== null) entry.batchTotalWeight += tw;
          if (len !== null && n > 0) {
            entry.batchLenNumerator += len * n;
            entry.batchLenDen += n;
          }
          return;
        }
        const wr = parseNum(row?.Wr);
        const len = parseNum(row?.Length);
        const wt = parseNum(row?.Weight);
        const tag = (row?.Tag ?? "").toString().trim();
        entry.count += stockingPredatorRowCountDelta(row);
        if (wr !== null) entry.wrVals.push(wr);
        if (len !== null) entry.lenVals.push(len);
        if (wt !== null) entry.wtVals.push(wt);
        if (tag) entry.tagged += 1;
      });
      return Array.from(map.values()).map((entry) => {
        const avg = (arr) =>
          arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
        const stdev = (arr) => {
          if (!arr.length) return null;
          const mean = avg(arr);
          const variance =
            arr.reduce((sum, x) => sum + Math.pow(x - mean, 2), 0) / arr.length;
          return Math.sqrt(variance);
        };
        const indivWtSum = entry.wtVals.reduce((a, b) => a + b, 0);
        const totalWt = entry.batchTotalWeight + indivWtSum;
        const avgWeight = entry.count > 0 ? totalWt / entry.count : null;
        const lenNum =
          entry.batchLenNumerator +
          (entry.lenVals.length ? entry.lenVals.reduce((a, l) => a + l, 0) : 0);
        const lenDen = entry.batchLenDen + entry.lenVals.length;
        const avgLength = lenDen > 0 ? lenNum / lenDen : null;
        return {
          species: entry.species,
          speciesFullName: entry.speciesFullName || entry.species,
          speciesAbbreviation: entry.speciesAbbreviation || "",
          count: entry.count,
          avgWr: avg(entry.wrVals),
          stdevWr: stdev(entry.wrVals),
          avgLength,
          avgWeight,
          tagged: entry.tagged,
        };
      });
    };
    const renderStockingEventEntriesTable = (rows, type) => {
      if (!rows?.length) {
        return <p className="text-muted mb-0">No {type} entries for this event.</p>;
      }
      if (type === "forage") {
        /* Stocking forage is historical pond data: show every row (same as meta
           "Forage Entries" count). Sampling species filter applies to predators only. */
        return (
          <div className="table-responsive" style={MODERN_TABLE_WRAPPER_STYLE}>
            <table className={MODERN_TABLE_CLASS}>
              <thead className={MODERN_TABLE_HEAD_CLASS} style={MODERN_TABLE_HEAD_STYLE}>
                <tr>
                  <th className="text-center">#</th>
                  <th>Species / Forage</th>
                  <th className="text-center">Number</th>
                  <th className="text-center">Total weight</th>
                  <th className="text-center">Avg length</th>
                  <th className="text-center">Avg weight</th>
                  <th className="text-center">Tag</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={`forage-${idx}-${normalizeSpecies(row)}-${formatPreyHeadcountDisplayFromRow(row)}`}>
                    <td className="text-center">{idx + 1}</td>
                    <td>
                      <SpeciesTableLabel
                        name={stockingRowSpeciesLabel(row)}
                        abbreviation={
                          row?.fishType?.Abbreviation ||
                          row?.FishTypeAbbreviation ||
                          row?.Abbreviation
                        }
                      />
                    </td>
                    <td className="text-center">
                      {formatPreyHeadcountDisplayFromRow(row)}
                    </td>
                    <td className="text-center">
                      {formatValue(
                        parseNum(row?.TotalWeight ?? row?.totalWeight),
                        "weight",
                        stockingDisplayUnit,
                        "—"
                      )}
                    </td>
                    <td className="text-center">
                      {formatValue(
                        parseNum(row?.MinLength ?? row?.minLength),
                        "length",
                        stockingDisplayUnit,
                        "—"
                      )}
                    </td>
                    <td className="text-center">
                      {formatValue(
                        parseNum(row?.MaxLength ?? row?.maxLength),
                        "weight",
                        stockingDisplayUnit,
                        "—"
                      )}
                    </td>
                    <td className="text-center">
                      {(row?.Tag ?? "").toString().trim() || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      const visibleRows = rows.filter((row) => isSelectedSpecies(normalizeSpecies(row)));
      if (!visibleRows.length) {
        return (
          <p className="text-muted mb-0">No {type} entries match the selected species filter.</p>
        );
      }
      const batchRows = visibleRows.filter((row) => stockingPredatorRowIsBatch(row));
      const indivRows = visibleRows.filter((row) => !stockingPredatorRowIsBatch(row));
      return (
        <div className="d-flex flex-column gap-3">
          {batchRows.length > 0 && (
            <div className="table-responsive" style={MODERN_TABLE_WRAPPER_STYLE}>
              <div className="small text-muted mb-1 fw-semibold">Predator (batch)</div>
              <table className={MODERN_TABLE_CLASS}>
                <thead className={MODERN_TABLE_HEAD_CLASS} style={MODERN_TABLE_HEAD_STYLE}>
                  <tr>
                    <th className="text-center">#</th>
                    <th>Species</th>
                    <th className="text-center">Total #</th>
                    <th className="text-center">Total weight</th>
                    <th className="text-center">Avg length</th>
                    <th className="text-center">Avg weight</th>
                  </tr>
                </thead>
                <tbody>
                  {batchRows.map((row, idx) => {
                    const n = parsePreyHeadcountFromStockingRow(row);
                    const tw = parseNum(row?.TotalWeight);
                    const avgWt =
                      n != null && n > 0 && tw != null ? tw / n : null;
                    return (
                      <tr key={`${type}-batch-${idx}-${normalizeSpecies(row)}`}>
                        <td className="text-center">{idx + 1}</td>
                        <td>
                      <SpeciesTableLabel
                        name={stockingRowSpeciesLabel(row)}
                        abbreviation={
                          row?.fishType?.Abbreviation ||
                          row?.FishTypeAbbreviation ||
                          row?.Abbreviation
                        }
                      />
                    </td>
                        <td className="text-center">{formatPreyHeadcountDisplayFromRow(row)}</td>
                        <td className="text-center">
                          {formatValue(tw, "weight", stockingDisplayUnit, "—")}
                        </td>
                        <td className="text-center">
                          {formatValue(parseNum(row?.Length), "length", stockingDisplayUnit, "—")}
                        </td>
                        <td className="text-center">
                          {formatValue(avgWt, "weight", stockingDisplayUnit, "—")}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {indivRows.length > 0 && (
            <div className="table-responsive" style={MODERN_TABLE_WRAPPER_STYLE}>
              {batchRows.length > 0 && (
                <div className="small text-muted mb-1 fw-semibold">Predator (individual)</div>
              )}
              <table className={MODERN_TABLE_CLASS}>
                <thead className={MODERN_TABLE_HEAD_CLASS} style={MODERN_TABLE_HEAD_STYLE}>
                  <tr>
                    <th className="text-center">#</th>
                    <th className="text-center">Species</th>
                    <th className="text-center">Length</th>
                    <th className="text-center">Weight</th>
                    <th className="text-center">
                      <WsLabel />
                    </th>
                    <th className="text-center">
                      <WrLabel />
                    </th>
                    <th className="text-center">Tag</th>
                  </tr>
                </thead>
                <tbody>
                  {indivRows.map((row, idx) => (
                    <tr key={`${type}-indiv-${idx}-${normalizeSpecies(row)}`}>
                      <td className="text-center">{idx + 1}</td>
                      <td>
                      <SpeciesTableLabel
                        name={stockingRowSpeciesLabel(row)}
                        abbreviation={
                          row?.fishType?.Abbreviation ||
                          row?.FishTypeAbbreviation ||
                          row?.Abbreviation
                        }
                      />
                    </td>
                      <td className="text-center">
                        {formatValue(parseNum(row?.Length), "length", stockingDisplayUnit, "0")}
                      </td>
                      <td className="text-center">
                        {formatValue(parseNum(row?.Weight), "weight", stockingDisplayUnit, "0")}
                      </td>
                      <td className="text-center">
                        {formatValue(parseNum(row?.Ws), "Ws", stockingDisplayUnit, "0")}
                      </td>
                      <td className="text-center">
                        {formatValue(parseNum(row?.Wr), "Wr", stockingDisplayUnit, "0.000")}
                      </td>
                      <td className="text-center">
                        {(row?.Tag ?? "").toString().trim() || "N/A"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="card border-0">
        <div className="tab-content p-3">
          <div className="tab-pane fade show active" id="allTab">
            {selectedPondInfo && (
              <div className="card mb-4 mt-2 border-1">
                <div className="card-body">
                  <div
                    className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2 pb-2"
                    style={{ borderBottom: "1px solid var(--tv-header-divider, #e2e8f0)" }}
                  >
                    <h4 className="mb-0 donezo-tv-card-title" style={{ fontSize: "20px", fontWeight: 700 }}>Pond Information</h4>
                    <span className="donezo-tv-overview-badge">
                      Overview
                    </span>
                  </div>

                  <div className="row g-2">
                    {[
                      {
                        label: "Pond Name",
                        value: selectedPondInfo.Name || "N/A",
                      },
                      {
                        label: "Location",
                        value: selectedPondInfo.Location || "N/A",
                      },
                      {
                        label: "Pond Type",
                        value: selectedPondInfo.Type || "N/A",
                      },
                      {
                        label: "Water Supply",
                        value: formatForage(selectedPondInfo.WaterSupply),
                      },
                      {
                        label: "Age",
                        value: formatPondAge(selectedPondInfo),
                      },
                      {
                        label: "Size",
                        value: formatValueWithUnit(selectedPondInfo.Size, "acres"),
                      },
                      {
                        label: "Depth",
                        value: formatValueWithUnit(selectedPondInfo.Depth, "ft"),
                      },
                      {
                        label: "Estimated Volume",
                        value: formatEstimatedVolumeAcreFt(
                          selectedPondInfo.EstimatedVolume
                        ),
                      },
                    ].map((item) => (
                      <div className="col-12 col-md-6 col-xl-3" key={item.label}>
                        <div className="h-100 donezo-tv-info-tile">
                          <div className="donezo-tv-info-tile__label">
                            {item.label}
                          </div>
                          <div className="donezo-tv-info-tile__value">
                            {item.value}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="row g-2 mt-1">
                    <div className="col-12 col-md-6">
                      <div className="donezo-tv-info-tile donezo-tv-info-tile--block">
                        <div className="donezo-tv-info-tile__label">Forage</div>
                        <div className="donezo-tv-info-tile__value">
                          {formatForage(selectedPondInfo.Forage)}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="donezo-tv-info-tile donezo-tv-info-tile--block">
                        <div className="donezo-tv-info-tile__label">Other Forage</div>
                        <div className="donezo-tv-info-tile__value">
                          {selectedPondInfo.ForageType || "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="donezo-tv-info-tile donezo-tv-info-tile--block">
                        <div className="donezo-tv-info-tile__label">Predator Fish</div>
                        <div className="donezo-tv-info-tile__value">
                          {selectedPondInfo.PredatorFishNames?.length
                            ? selectedPondInfo.PredatorFishNames.join(", ")
                            : "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="donezo-tv-info-tile donezo-tv-info-tile--block">
                        <div className="donezo-tv-info-tile__label">
                          Aquatic Vegetation
                        </div>
                        <div className="donezo-tv-info-tile__value">
                          {formatAquaticVegetation(
                            selectedPondInfo.AquaticVegetationPresent
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {includeStockingPredators && pondDetails.length > 0 && (
              <>
                <div className="mb-3 mt-2 donezo-tv-section-banner">
                  <h4 className="donezo-tv-section-banner__title">
                    Stocking Events Data
                  </h4>
                </div>
                {stockingEventsSorted.length > 0 ? (
                  stockingEventsSorted.map((event, eventIdx) => {
                    const eventKey = event?.id || `stocking-event-${eventIdx}`;
                    const isCollapsed = collapsedStockingCards[eventKey] ?? false;
                    const predatorRows = Array.isArray(event?.predatorRows)
                      ? event.predatorRows
                      : [];
                    const preyRows = Array.isArray(event?.preyRows) ? event.preyRows : [];
                    const predatorSummary = computePredatorSummary(predatorRows);
                    return (
                      <div
                        key={eventKey}
                        className="card mb-3 border-1"
                      >
                        <div className="card-body">
                          <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                            <h5 className="mb-0 d-flex flex-wrap align-items-baseline gap-2 donezo-tv-card-title">
                              <span>Stocking Event {eventIdx + 1}</span>
                              <span
                                className="text-muted fw-normal"
                                style={{ fontSize: "0.92rem" }}
                              >
                                {formatDateTime(event?.StockingDate)}
                              </span>
                            </h5>
                            <button
                              type="button"
                              className="btn btn-sm donezo-tv-collapse-btn"
                              onClick={() =>
                                setCollapsedStockingCards((prev) => ({
                                  ...prev,
                                  [eventKey]: !isCollapsed,
                                }))
                              }
                              aria-expanded={!isCollapsed}
                              aria-label={isCollapsed ? "Expand stocking event card" : "Collapse stocking event card"}
                            >
                              {isCollapsed ? (
                                <ChevronDown size={18} strokeWidth={2.8} />
                              ) : (
                                <ChevronUp size={18} strokeWidth={2.8} />
                              )}
                            </button>
                          </div>
                          {!isCollapsed && (
                            <>
                              <div className="row g-2 mb-2">
                                {[
                                  { label: "Date", value: formatDateTime(event?.StockingDate) },
                                  {
                                    label: "Stocking Type",
                                    value: event?.IsInitial ? "Initial" : "Supplemental",
                                  },
                                  {
                                    label: "Length Unit",
                                    value: event?.lengthUnit?.Name || event?.lengthUnit?.name || "N/A",
                                  },
                                  {
                                    label: "Weight Unit",
                                    value: event?.weightUnit?.Name || event?.weightUnit?.name || "N/A",
                                  },
                                  {
                                    label: "Forage Entries",
                                    value: sumPreyHeadcountsFromRows(preyRows).toLocaleString(),
                                  },
                                  {
                                    label: "Predator Entries",
                                    value: sumPredatorHeadcountsFromRows(predatorRows).toLocaleString(),
                                  },
                                ].map((meta) => (
                                  <div className="col-12 col-md-6 col-xl-4" key={`${eventKey}-${meta.label}`}>
                                    <div className="donezo-tv-info-tile">
                                      <div className="donezo-tv-info-tile__label">
                                        {meta.label}
                                      </div>
                                      <div className="donezo-tv-info-tile__value">
                                        {meta.value}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                                <div className="col-12">
                                  <div className="donezo-tv-info-tile">
                                    <div className="donezo-tv-info-tile__label">
                                      Notes
                                    </div>
                                    <div className="donezo-tv-info-tile__value">
                                      {event?.Notes || event?.notes || "N/A"}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <h6 className="mt-3">Forage entries</h6>
                              {renderStockingEventEntriesTable(preyRows, "forage")}

                              <h6 className="mt-3">Predator entries</h6>
                              {renderStockingEventEntriesTable(predatorRows, "predator")}

                              <h6 className="mt-3">Predator summary by species</h6>
                              {predatorSummary.length > 0 ? (
                                <div className={MODERN_TABLE_SHELL_CLASS} style={MODERN_TABLE_WRAPPER_STYLE}>
                                  <table className={MODERN_TABLE_CLASS}>
                                    <thead className={MODERN_TABLE_HEAD_CLASS} style={MODERN_TABLE_HEAD_STYLE}>
                                      <tr>
                                        <th className="text-center">Species</th>
                                        <th className="text-center">{LABEL.NUM}</th>
                                        <th className="text-center">
                                          Avg <WrLabel />
                                        </th>
                                        <th className="text-center">
                                          {LABEL.STD_DEV} <WrLabel />
                                        </th>
                                        <th className="text-center">Avg Length</th>
                                        <th className="text-center">Avg Weight</th>
                                        <th className="text-center">Tagged</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {predatorSummary.map((row) => (
                                        <tr key={`pred-summary-${event?.id || eventIdx}-${row.species}`}>
                                          <td>
                                            <SpeciesTableLabel
                                              name={row.speciesFullName || row.species}
                                              abbreviation={row.speciesAbbreviation}
                                            />
                                          </td>
                                          <td className="text-center">{row.count}</td>
                                          <td className="text-center">
                                            {formatValue(row.avgWr, "Wr", stockingDisplayUnit, "0.000")}
                                          </td>
                                          <td className="text-center">
                                            {formatValue(
                                              row.stdevWr,
                                              "Wr",
                                              stockingDisplayUnit,
                                              "0.000"
                                            )}
                                          </td>
                                          <td className="text-center">
                                            {formatValue(
                                              row.avgLength,
                                              "length",
                                              stockingDisplayUnit,
                                              "0"
                                            )}
                                          </td>
                                          <td className="text-center">
                                            {formatValue(
                                              row.avgWeight,
                                              "weight",
                                              stockingDisplayUnit,
                                              "0"
                                            )}
                                          </td>
                                          <td className="text-center">{row.tagged}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <p className="text-muted mb-0">
                                  No predator summary rows for this event.
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="card mb-4 mt-2 border-1">
                    <div className="card-body">
                      <p className="text-muted mb-0">
                        No stocking events data found for the selected filters/date range.
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}

            {pondDetails.length === 0 && selectedPondInfo && (
              <div className="alert alert-info mt-3">
                <p className="mb-0">
                  {isSinglePondDetailMode
                    ? 'No Fishing/Sampling data loaded yet. Click "Fetch" to refresh.'
                    : 'No Fishing/Sampling data found. Click "Fetch" to load data for the selected pond and date range.'}
                </p>
              </div>
            )}

            {pondDetails.length > 0 && (
              <div className="mb-3 mt-2 donezo-tv-section-banner">
                <h4 className="donezo-tv-section-banner__title">
                  Fishing/Sampling Data
                </h4>
              </div>
            )}

            {pondDetails.map((detail, detailIndex) => {
              const cardKey = detail.id || `detail-${detailIndex}`;
              const isCollapsed = collapsedSamplingCards[cardKey] ?? false;
              const effectiveUnitSystem = selectedTableUnit.value;
              const filteredSampling = applySamplingTagFilter(
                detail.SamplingData || []
              );
              const binConv = getConversionsForDisplay(detail, selectedTableUnit);
              const displaySamplingForSummary = filteredSampling.map((fish) => ({
                ...fish,
                Length: binConv.convertLength(fish.Length),
                Weight: binConv.convertWeight(fish.Weight),
              }));
              return (
                <div
                  key={detail.id || detailIndex}
                  className="card mb-4 mt-2 border-1"
                >
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                      <h5 className="mb-0 donezo-tv-card-title">
                        Fishing/Sampling Data - {formatDateTime(detail.Date)}
                      </h5>
                      <button
                        type="button"
                        className="btn btn-sm donezo-tv-collapse-btn"
                        onClick={() =>
                          setCollapsedSamplingCards((prev) => ({
                            ...prev,
                            [cardKey]: !isCollapsed,
                          }))
                        }
                        aria-expanded={!isCollapsed}
                        aria-label={isCollapsed ? "Expand sampling card" : "Collapse sampling card"}
                      >
                        {isCollapsed ? (
                          <ChevronDown size={18} strokeWidth={2.8} />
                        ) : (
                          <ChevronUp size={18} strokeWidth={2.8} />
                        )}
                      </button>
                    </div>
                    {!isCollapsed && (
                      <>
                    <div className="row g-2">
                      {[
                        {
                          title: "Fishing/Sampling Data",
                          rows: [
                            { label: "Sampling Method", value: normalizeSamplingMethod(detail.SamplingMethod) || "N/A" },
                            {
                              label: "Time Fishing/Sampling Started",
                              value: (() => {
                                const onSite = resolveSessionOnSiteTimes(detail);
                                const raw = onSite.start || detail.TimeFishingStarted;
                                if (!raw) return "N/A";
                                return toClockTimeValue(raw) || formatTime(raw);
                              })(),
                            },
                            {
                              label: "Time Fishing/Sampling Stopped",
                              value: (() => {
                                const onSite = resolveSessionOnSiteTimes(detail);
                                const raw = onSite.stop || detail.TimeFishingStopped;
                                if (!raw) return "N/A";
                                return toClockTimeValue(raw) || formatTime(raw);
                              })(),
                            },
                            {
                              label: "Total Time Fishing/Sampling",
                              value: calculateFishingDuration(
                                formatTime(detail.TimeFishingStarted),
                                formatTime(detail.TimeFishingStopped),
                                detail
                              ),
                            },
                            { label: "Number of Anglers", value: detail.NumberOfAnglers ?? "N/A" },
                            { label: "Length Units", value: detail.LengthUnitName || "N/A" },
                            { label: "Weight Units", value: detail.WeightUnitName || "N/A" },
                            { label: "File Created", value: formatDateTime(detail.createdAt) },
                            { label: "File Updated", value: formatDateTime(detail.updatedAt) },
                          ],
                        },
                        {
                          title: "Water Quality Data",
                          rows: [
                            ...WATER_QUALITY_GRID_ROWS.flat().map(({ label, key }) => ({
                              label,
                              value: formatPondDetailWQValue(detail, key),
                            })),
                            {
                              label: "Fertilization Date",
                              value: formatDateTime(detail.FertilizationDate),
                            },
                            { label: "Fertilization Type", value: detail.FertilizationType || "N/A" },
                          ],
                        },
                        {
                          title: "Meteorological Data",
                          rows: [
                            { label: "Air Temperature, F", value: detail.AirTemperature || "N/A" },
                            { label: "Sky", value: detail.SkyClimate || "N/A" },
                            { label: "Moon Phase", value: detail.MoonPhase || "N/A" },
                            { label: "Barometric Pressure, mm Hg", value: detail.BaroMetricPressure || "N/A" },
                            { label: "Wind Speed, mph", value: detail.WindSpeed || "N/A" },
                            { label: "Wind Direction", value: detail.WindDirection || "N/A" },
                          ],
                        },
                      ].map((section) => (
                        <div className="col-12" key={section.title}>
                          <div className="h-100 donezo-tv-meta-panel">
                            <div className="donezo-tv-meta-panel__head">
                              <h6 className="donezo-tv-meta-panel__title">
                                {section.title}
                              </h6>
                            </div>
                            <div className="p-2">
                              <div className="row g-1">
                                {section.rows.map((row) => (
                                  <div
                                    className="col-12 col-md-6 col-xl-4"
                                    key={`${section.title}-${row.label}`}
                                  >
                                    <div className="donezo-tv-info-tile">
                                      <div
                                        className="donezo-tv-info-tile__label"
                                        style={{
                                          textTransform:
                                            section.title === "Water Quality Data" ||
                                            section.title === "Meteorological Data"
                                              ? "none"
                                              : "uppercase",
                                        }}
                                      >
                                        {row.label}
                                      </div>
                                      <div className="donezo-tv-info-tile__value">
                                        {row.value}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      <div className="col-12" key={`${cardKey}-pond-notes`}>
                        <div className="h-100 donezo-tv-meta-panel">
                          <div className="donezo-tv-meta-panel__head">
                            <h6 className="donezo-tv-meta-panel__title">
                              Notes
                            </h6>
                          </div>
                          <div className="p-2">{renderPondNote(detail.PondNotes)}</div>
                        </div>
                      </div>
                    </div>
                    <h5 className="mt-4">Fishing/Sampling Data</h5>
                    {renderSamplingDataTable(
                      filteredSampling,
                      effectiveUnitSystem,
                      detail.SamplingMethod,
                      { detail, displayUnit: selectedTableUnit }
                    )}

                    {/* Bin Tables for each fish species */}
                    {detail.FishType && detail.FishType.length > 0 && (
                      <>
                        <h5 className="mt-4">Bin Analysis by Species</h5>
                        {detail.FishType.map((fishType, fishTypeIndex) => {
                          const speciesFish = filteredSampling.filter(
                            (fish) =>
                              fish.FishTypeName === fishType.FishTypeName
                          );

                          if (speciesFish.length === 0) return null;

                          // Convert fish data for bin analysis
                          const binFishData = speciesFish.map((fish) => ({
                            ...fish,
                            length: binConv
                              ? binConv.convertLength(fish.Length)
                              : parseFloat(fish.Length || 0),
                            relativeWeight: parseFloat(fish.Wr || 0),
                            status: fish.Status || "Unknown",
                            species: fish.FishTypeName || "Unknown",
                          }));

                          const binData = processBinData(
                            binFishData,
                            effectiveUnitSystem
                          );

                          return (
                            <div key={fishTypeIndex} className="mb-3">
                              <h6 className="text-primary">
                                {fishType.FishTypeName} - Bin Summary
                              </h6>
                              <BinsTable
                                binData={binData}
                                unitSystem={effectiveUnitSystem}
                                showPopulationPct
                              />
                            </div>
                          );
                        })}
                      </>
                    )}

                    {(() => {
                      const psdRows = computePsdTableRowsForSession(
                        detail,
                        [],
                        selectedTableUnit?.value
                      );
                      if (!psdRows.length) return null;
                      return (
                        <>
                          <h5 className="mt-4">Proportional Size Distribution</h5>
                          <div className="table-responsive mt-2" style={MODERN_TABLE_WRAPPER_STYLE}>
                            <table className={MODERN_TABLE_CLASS}>
                              <thead className={MODERN_TABLE_HEAD_CLASS} style={MODERN_TABLE_HEAD_STYLE}>
                                <tr>
                                  <th className="text-center" style={MODERN_TABLE_CELL_STYLE}>Species</th>
                                  <th className="text-center" style={MODERN_TABLE_CELL_STYLE}>{LABEL.NUM_STOCK}</th>
                                  <th className="text-center" style={MODERN_TABLE_CELL_STYLE}>PSD-Q %</th>
                                  <th className="text-center" style={MODERN_TABLE_CELL_STYLE}>PSD-P %</th>
                                  <th className="text-center" style={MODERN_TABLE_CELL_STYLE}>PSD-M %</th>
                                  <th className="text-center" style={MODERN_TABLE_CELL_STYLE}>PSD-T %</th>
                                </tr>
                              </thead>
                              <tbody>
                                {psdRows.map((row) => (
                                  <tr key={row.species}>
                                    <td className="text-center fw-semibold" style={MODERN_TABLE_CELL_STYLE}>
                                      {row.species}
                                    </td>
                                    <td className="text-center" style={MODERN_TABLE_CELL_STYLE}>
                                      {row.metrics.nDenominator}
                                    </td>
                                    <td className="text-center" style={MODERN_TABLE_CELL_STYLE}>
                                      {row.metrics.psdQ == null ? "—" : row.metrics.psdQ.toFixed(1)}
                                    </td>
                                    <td className="text-center" style={MODERN_TABLE_CELL_STYLE}>
                                      {row.metrics.psdP == null ? "—" : row.metrics.psdP.toFixed(1)}
                                    </td>
                                    <td className="text-center" style={MODERN_TABLE_CELL_STYLE}>
                                      {row.metrics.psdM == null ? "—" : row.metrics.psdM.toFixed(1)}
                                    </td>
                                    <td className="text-center" style={MODERN_TABLE_CELL_STYLE}>
                                      {row.metrics.psdT == null ? "—" : row.metrics.psdT.toFixed(1)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      );
                    })()}

                    <SessionCpueTables detail={detail} />
                    <PerSpeciesCpueTables detail={detail} fishTypeData={detail.FishType} />

                    <h5 className="mt-4">Summary by Species</h5>
                    {renderFishTypeSummary(
                      detail.FishType,
                      effectiveUnitSystem,
                      displaySamplingForSummary,
                      formatTime(detail.TimeFishingStarted),
                      formatTime(detail.TimeFishingStopped),
                      detail.NumberOfAnglers,
                      detail
                    )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}

            {/*  Bin Summary Section — collapsed by default; data from TableAggregates API */}
            {pondDetails.length > 0 && (
              <div className="card mb-4 mt-2 border-1">
                <div className="card-body">
                  <div className="d-flex align-items-start justify-content-between gap-2">
                    <div>
                      <h4 className="mb-0">Bin Summary Across Sampling Sessions</h4>
                      <p className="text-muted mb-0 mt-1 small">
                        Across all sessions in the selected date range
                        {pondDetails.length
                          ? ` (${pondDetails.length} session${pondDetails.length === 1 ? "" : "s"})`
                          : ""}
                        .
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm donezo-tv-collapse-btn flex-shrink-0"
                      onClick={() =>
                        setCollapsedAggregateSections((prev) => ({
                          ...prev,
                          binSummary: !prev.binSummary,
                        }))
                      }
                      aria-expanded={!collapsedAggregateSections.binSummary}
                      aria-label={
                        collapsedAggregateSections.binSummary
                          ? "Expand bin summary"
                          : "Collapse bin summary"
                      }
                    >
                      {collapsedAggregateSections.binSummary ? (
                        <ChevronDown size={18} strokeWidth={2.8} />
                      ) : (
                        <ChevronUp size={18} strokeWidth={2.8} />
                      )}
                    </button>
                  </div>
                  {!collapsedAggregateSections.binSummary && (
                    <>
                      {aggregatesLoading ? (
                        <p className="text-muted mt-3 mb-0">Loading bin summary…</p>
                      ) : allSessionsSpeciesBins.length === 0 ? (
                        <p className="text-muted mt-3 mb-0">No bin data available.</p>
                      ) : (
                        allSessionsSpeciesBins.map((sb, index) => (
                          <div key={sb.species || index} className="mt-4">
                            <h5>{sb.species} - Bin Summary</h5>
                            <BinsTable
                              binData={sb.bins}
                              unitSystem={selectedTableUnit.value}
                              showPopulationPct
                            />
                          </div>
                        ))
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Overall Species Summary — collapsed by default */}
            {pondDetails.length > 0 && (
              <div className="card mb-4 mt-2 border-1">
                <div className="card-body">
                  <div className="d-flex align-items-start justify-content-between gap-2">
                    <div>
                      <h4 className="mb-0">Overall Species Summary Across Sampling Sessions</h4>
                      <p className="text-muted mb-0 mt-1 small">
                        Across all sessions in the selected date range.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm donezo-tv-collapse-btn flex-shrink-0"
                      onClick={() =>
                        setCollapsedAggregateSections((prev) => ({
                          ...prev,
                          overallSummary: !prev.overallSummary,
                        }))
                      }
                      aria-expanded={!collapsedAggregateSections.overallSummary}
                      aria-label={
                        collapsedAggregateSections.overallSummary
                          ? "Expand overall species summary"
                          : "Collapse overall species summary"
                      }
                    >
                      {collapsedAggregateSections.overallSummary ? (
                        <ChevronDown size={18} strokeWidth={2.8} />
                      ) : (
                        <ChevronUp size={18} strokeWidth={2.8} />
                      )}
                    </button>
                  </div>
                  {!collapsedAggregateSections.overallSummary && (
                  aggregatesLoading ? (
                    <p className="text-muted mt-3 mb-0">Loading overall species summary…</p>
                  ) : (
                  <div className="table-responsive mt-3" style={MODERN_TABLE_WRAPPER_STYLE}>
                    <table className={MODERN_TABLE_CLASS}>
                      <thead className={MODERN_TABLE_HEAD_CLASS} style={MODERN_TABLE_HEAD_STYLE}>
                        <tr>
                          <th>Specie</th>
                          <th>{LABEL.NUM}</th>
                          <th>
                            Avg <WrLabel />
                          </th>
                          <th>
                            {LABEL.STD_DEV} <WrLabel />
                          </th>
                          <th>Total Culled</th>
                          <th>Total Released</th>
                          <th>Longest Fish (L/W)</th>
                          <th>Heaviest Fish (L/W)</th>
                          <th>Shortest Fish (L/W)</th>
                          <th>Lightest Fish (L/W)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {overallSpeciesSummary.map(
                          (summary, index) => {
                            const biggest = summary.longest;
                            const smallest = summary.shortest;
                            const heaviest = summary.heaviest;
                            const lightest = summary.lightest;
                            const displayUnit = selectedTableUnit.value;
                            return (
                              <tr key={summary.FishTypeName || index}>
                                <td>
                                  <SpeciesTableLabel
                                    name={summary.FishTypeName}
                                    abbreviation={
                                      summary.FishTypeAbbreviation ||
                                      summary.Abbreviation
                                    }
                                  />
                                </td>
                                <td>{summary.NumberOfFishes || 0}</td>
                                <td>
                                  {formatValue(
                                    summary.AverageWr,
                                    "averageWr",
                                    displayUnit,
                                    "0.000"
                                  )}
                                </td>
                                <td>
                                  {formatValue(
                                    summary.StandardDeviationWr,
                                    "stdDevWr",
                                    displayUnit,
                                    "0.000"
                                  )}
                                </td>
                                <td>{summary.NumberOfFishesCulled || 0}</td>
                                <td>{summary.NumberOfFishesReleased || 0}</td>
                                <td>
                                  {biggest
                                    ? formatSpeciesExtremeLW(
                                        biggest.Length,
                                        biggest.Weight
                                      )
                                    : "-"}
                                </td>
                                <td>
                                  {heaviest
                                    ? formatSpeciesExtremeLW(
                                        heaviest.Length,
                                        heaviest.Weight
                                      )
                                    : "-"}
                                </td>
                                <td>
                                  {smallest
                                    ? formatSpeciesExtremeLW(
                                        smallest.Length,
                                        smallest.Weight
                                      )
                                    : "-"}
                                </td>
                                <td>
                                  {lightest
                                    ? formatSpeciesExtremeLW(
                                        lightest.Length,
                                        lightest.Weight
                                      )
                                    : "-"}
                                </td>
                              </tr>
                            );
                          }
                        )}
                      </tbody>
                    </table>
                  </div>
                  )
                  )}
                </div>
              </div>
            )}

            {/* Tagged fish growth tracking — collapsed by default (wide matrix) */}
            {pondDetails.length > 0 && (
              <div className="card mb-4 mt-2 border-1">
                <div className="card-body">
                  <div className="d-flex align-items-start justify-content-between gap-2">
                    <div>
                      <h4 className="mb-0">Tagged Fish Tracking</h4>
                      <p className="text-muted mb-0 mt-1 small">
                        Across all sessions in the selected date range. One row per sampling date
                        (and initial stocking date when stocking predators are included); each tag
                        has Lgt, Wgt, and <WrLabel />. Scroll horizontally if many tags are present.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm donezo-tv-collapse-btn flex-shrink-0"
                      onClick={() =>
                        setCollapsedAggregateSections((prev) => ({
                          ...prev,
                          taggedTracking: !prev.taggedTracking,
                        }))
                      }
                      aria-expanded={!collapsedAggregateSections.taggedTracking}
                      aria-label={
                        collapsedAggregateSections.taggedTracking
                          ? "Expand tagged fish tracking"
                          : "Collapse tagged fish tracking"
                      }
                    >
                      {collapsedAggregateSections.taggedTracking ? (
                        <ChevronDown size={18} strokeWidth={2.8} />
                      ) : (
                        <ChevronUp size={18} strokeWidth={2.8} />
                      )}
                    </button>
                  </div>
                  {!collapsedAggregateSections.taggedTracking && (
                    <div className="mt-3">
                      {aggregatesLoading ? (
                        <p className="text-muted mb-0">Loading tagged fish tracking…</p>
                      ) : (
                        renderTaggedFishTrackingTable()
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- Export logic for merged view ---
  async function handleExport(type) {
    let aggregates = tableAggregates || exportAggregatesRef.current;
    if (!aggregates && pondDetails.length > 0) {
      aggregates = await fetchTableAggregates(pondDetails.map((d) => d.id));
    }
    exportAggregatesRef.current = aggregates;
    if (type === "excel") {
      await generateMergedExcel();
    } else {
      await generateMergedPDF();
    }
  }

  // --- Merged Excel Export Function ---
  function generateMergedExcel() {
    try {
      const wb = XLSX.utils.book_new();
      const stockingExportUnitSystem = selectedTableUnit.value;
      const selectedFishSet = new Set((selectedFish || []).map((f) => f.label));
      const hasSelectedFishFilter = selectedFishSet.size > 0;
      const stockingSpeciesName = (row) => stockingRowSpeciesLabel(row);
      const matchesStockingSpeciesFilter = (row) => {
        if (!hasSelectedFishFilter) return true;
        return selectedFishSet.has(stockingSpeciesName(row));
      };
      const parseStockingNum = (value) => {
        const n = typeof value === "string" ? parseFloat(value) : Number(value);
        return Number.isFinite(n) ? n : null;
      };
      const summarizePredatorRows = (rows = []) => {
        const grouped = new Map();
        rows.forEach((row) => {
          if (!matchesStockingSpeciesFilter(row)) return;
          const species = stockingSpeciesName(row);
          if (!grouped.has(species)) {
            grouped.set(species, {
              species,
              count: 0,
              wrVals: [],
              lenVals: [],
              wtVals: [],
              tagged: 0,
              batchTotalWeight: 0,
              batchLenNumerator: 0,
              batchLenDen: 0,
            });
          }
          const item = grouped.get(species);
          if (stockingPredatorRowIsBatch(row)) {
            const n = stockingPredatorRowCountDelta(row);
            const tw = parseStockingNum(row?.TotalWeight);
            const len = parseStockingNum(row?.Length);
            item.count += n;
            if (tw !== null) item.batchTotalWeight += tw;
            if (len !== null && n > 0) {
              item.batchLenNumerator += len * n;
              item.batchLenDen += n;
            }
            return;
          }
          const wr = parseStockingNum(row?.Wr);
          const len = parseStockingNum(row?.Length);
          const wt = parseStockingNum(row?.Weight);
          const tag = (row?.Tag ?? "").toString().trim();
          item.count += stockingPredatorRowCountDelta(row);
          if (wr !== null) item.wrVals.push(wr);
          if (len !== null) item.lenVals.push(len);
          if (wt !== null) item.wtVals.push(wt);
          if (tag) item.tagged += 1;
        });
        const avg = (arr) =>
          arr.length ? arr.reduce((sum, x) => sum + x, 0) / arr.length : null;
        const stdev = (arr) => {
          if (!arr.length) return null;
          const mean = avg(arr);
          const variance =
            arr.reduce((sum, x) => sum + Math.pow(x - mean, 2), 0) / arr.length;
          return Math.sqrt(variance);
        };
        return Array.from(grouped.values()).map((item) => {
          const indivWtSum = item.wtVals.reduce((a, b) => a + b, 0);
          const totalWt = item.batchTotalWeight + indivWtSum;
          const avgWeight = item.count > 0 ? totalWt / item.count : null;
          const lenNum =
            item.batchLenNumerator +
            (item.lenVals.length ? item.lenVals.reduce((a, l) => a + l, 0) : 0);
          const lenDen = item.batchLenDen + item.lenVals.length;
          const avgLength = lenDen > 0 ? lenNum / lenDen : null;
          return {
            species: item.species,
            count: item.count,
            avgWr: avg(item.wrVals),
            stdevWr: stdev(item.wrVals),
            avgLength,
            avgWeight,
            tagged: item.tagged,
          };
        });
      };

      if (pondDetails.length > 0) {
        // 1. Pond Information Sheet (First Sheet)
        if (selectedPondInfo) {
          const pondInfoData = [
            ["FISH SAMPLING REPORT"],
            [""],
            ["POND INFORMATION"],
            [""],
            ["Pond Name:", selectedPondInfo.Name || "N/A"],
            ["Location:", selectedPondInfo.Location || "N/A"],
            ["Type:", selectedPondInfo.Type || "N/A"],
            ["Size:", (selectedPondInfo.Size || "N/A") + " (acres)"],
            ["Age:", formatPondAge(selectedPondInfo)],
            ["Depth:", (selectedPondInfo.Depth || "N/A") + " (Feet)"],
            [
              "Estimated Volume (acre-ft.):",
              formatEstimatedVolumeAcreFt(selectedPondInfo.EstimatedVolume),
            ],
            ["Forage:", selectedPondInfo.Forage || "N/A"],
            ["Other Forage:", selectedPondInfo.ForageType || "N/A"],
            [
              "Aquatic Vegetation:",
              formatAquaticVegetation(selectedPondInfo.AquaticVegetationPresent),
            ],
            ["Water Supply:", selectedPondInfo.WaterSupply || "N/A"],
            [
              "Predator Fish:",
              selectedPondInfo.PredatorFishNames?.length
                ? selectedPondInfo.PredatorFishNames.join(", ")
                : "N/A",
            ],
          ];

          const pondInfoFooterRow = appendExcelPondStatFooter(pondInfoData);
          const pondInfoSheet = XLSX.utils.aoa_to_sheet(pondInfoData);
          applyExcelPondStatFooterStyle(pondInfoSheet, pondInfoFooterRow);

          // Apply styling
          pondInfoSheet["!merges"] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }, // Title merge
            { s: { r: 2, c: 0 }, e: { r: 2, c: 5 } }, // Section header merge
          ];

          // Set column widths
          pondInfoSheet["!cols"] = [
            { width: 25 }, // Key column
            { width: 40 }, // Value column
            { width: 15 },
            { width: 15 },
            { width: 15 },
            { width: 15 },
          ];

          XLSX.utils.book_append_sheet(wb, pondInfoSheet, "Pond Information");
        }

        // 2. Stocking Events Sheet (event-wise)
        if (includeStockingPredators) {
          const stockingSheetData = [["Stocking Events Data"], [""]];
          if (stockingEventsSorted.length === 0) {
            stockingSheetData.push([
              "No stocking events data found for the selected filters/date range.",
            ]);
          } else {
            stockingEventsSorted.forEach((event, eventIdx) => {
              /* Forage/prey rows: always show every stocking row (not filtered by
                 sampling species). Predator rows still respect the species filter. */
              const preyRows = Array.isArray(event?.preyRows) ? event.preyRows : [];
              const predatorRows = (event?.predatorRows || []).filter(
                matchesStockingSpeciesFilter
              );
              const predatorSummaryRows = summarizePredatorRows(event?.predatorRows || []);

              stockingSheetData.push([`Stocking Event ${eventIdx + 1}`]);
              stockingSheetData.push(["Date:", formatDateTime(event?.StockingDate)]);
              stockingSheetData.push([
                "Stocking Type:",
                event?.IsInitial ? "Initial" : "Supplemental",
              ]);
              stockingSheetData.push([
                "Length Unit:",
                event?.lengthUnit?.Name || event?.lengthUnit?.name || "N/A",
              ]);
              stockingSheetData.push([
                "Weight Unit:",
                event?.weightUnit?.Name || event?.weightUnit?.name || "N/A",
              ]);
              stockingSheetData.push(["Notes:", event?.Notes || event?.notes || "N/A"]);
              stockingSheetData.push([""]);

              stockingSheetData.push(["Forage entries"]);
              stockingSheetData.push([
                "#",
                "Species / Forage",
                "Number",
                "Total weight",
                "Avg length",
                "Avg weight",
                "Tag",
              ]);
              if (preyRows.length > 0) {
                preyRows.forEach((row, idx) => {
                  stockingSheetData.push([
                    idx + 1,
                    stockingSpeciesName(row),
                    formatPreyHeadcountDisplayFromRow(row),
                    formatValue(
                      parseStockingNum(row?.TotalWeight ?? row?.totalWeight),
                      "weight",
                      stockingExportUnitSystem,
                      "—"
                    ),
                    formatValue(
                      parseStockingNum(row?.MinLength ?? row?.minLength),
                      "length",
                      stockingExportUnitSystem,
                      "—"
                    ),
                    formatValue(
                      parseStockingNum(row?.MaxLength ?? row?.maxLength),
                      "weight",
                      stockingExportUnitSystem,
                      "—"
                    ),
                    (row?.Tag ?? "").toString().trim() || "N/A",
                  ]);
                });
              } else {
                stockingSheetData.push(["", "No forage entries", "", "", "", "", ""]);
              }
              stockingSheetData.push([""]);

              stockingSheetData.push(["Predator entries"]);
              const predatorBatchRows = predatorRows.filter((r) => stockingPredatorRowIsBatch(r));
              const predatorIndivRows = predatorRows.filter((r) => !stockingPredatorRowIsBatch(r));
              if (predatorRows.length === 0) {
                stockingSheetData.push(["", "No predator entries", "", "", "", "", ""]);
              } else {
                if (predatorBatchRows.length > 0) {
                  stockingSheetData.push(["Predator (batch)"]);
                  stockingSheetData.push([
                    "#",
                    "Species",
                    "Total #",
                    "Total weight",
                    "Avg length",
                    "Avg weight",
                  ]);
                  predatorBatchRows.forEach((row, idx) => {
                    const n = parsePreyHeadcountFromStockingRow(row);
                    const tw = parseStockingNum(row?.TotalWeight);
                    const avgWt =
                      n != null && n > 0 && tw != null ? tw / n : null;
                    stockingSheetData.push([
                      idx + 1,
                      stockingSpeciesName(row),
                      formatPreyHeadcountDisplayFromRow(row),
                      formatValue(tw, "weight", stockingExportUnitSystem, "—"),
                      formatValue(
                        parseStockingNum(row?.Length),
                        "length",
                        stockingExportUnitSystem,
                        "—"
                      ),
                      formatValue(avgWt, "weight", stockingExportUnitSystem, "—"),
                    ]);
                  });
                }
                if (predatorIndivRows.length > 0) {
                  stockingSheetData.push(["Predator (individual)"]);
                  stockingSheetData.push(["#", "Species", "Length", "Weight", "Ws", "Wr", "Tag"]);
                  predatorIndivRows.forEach((row, idx) => {
                    stockingSheetData.push([
                      idx + 1,
                      stockingSpeciesName(row),
                      formatValue(
                        parseStockingNum(row?.Length),
                        "length",
                        stockingExportUnitSystem,
                        "0"
                      ),
                      formatValue(
                        parseStockingNum(row?.Weight),
                        "weight",
                        stockingExportUnitSystem,
                        "0"
                      ),
                      formatValue(
                        parseStockingNum(row?.Ws),
                        "Ws",
                        stockingExportUnitSystem,
                        "0"
                      ),
                      formatValue(
                        parseStockingNum(row?.Wr),
                        "Wr",
                        stockingExportUnitSystem,
                        "0.000"
                      ),
                      (row?.Tag ?? "").toString().trim() || "N/A",
                    ]);
                  });
                }
              }
              stockingSheetData.push([""]);

              stockingSheetData.push(["Predator summary by species"]);
              stockingSheetData.push([
                "Species",
                LABEL.NUM,
                LABEL.AVERAGE_WR_ASCII,
                LABEL.STD_DEV_WR_ASCII,
                "Avg Length",
                "Avg Weight",
                "Tagged",
              ]);
              if (predatorSummaryRows.length > 0) {
                predatorSummaryRows.forEach((row) => {
                  stockingSheetData.push([
                    row.species,
                    row.count,
                    formatValue(row.avgWr, "Wr", stockingExportUnitSystem, "0.000"),
                    formatValue(
                      row.stdevWr,
                      "Wr",
                      stockingExportUnitSystem,
                      "0.000"
                    ),
                    formatValue(
                      row.avgLength,
                      "length",
                      stockingExportUnitSystem,
                      "0"
                    ),
                    formatValue(
                      row.avgWeight,
                      "weight",
                      stockingExportUnitSystem,
                      "0"
                    ),
                    row.tagged,
                  ]);
                });
              } else {
                stockingSheetData.push(["No predator summary rows", "", "", "", "", "", ""]);
              }
              stockingSheetData.push([""]);
            });
          }

          const stockingFooterRow = appendExcelPondStatFooter(stockingSheetData);
          const stockingSheet = XLSX.utils.aoa_to_sheet(stockingSheetData);
          applyExcelPondStatFooterStyle(stockingSheet, stockingFooterRow);
          stockingSheet["!cols"] = [
            { width: 14 },
            { width: 24 },
            { width: 12 },
            { width: 12 },
            { width: 12 },
            { width: 12 },
            { width: 14 },
            { width: 18 },
            { width: 28 },
          ];
          XLSX.utils.book_append_sheet(wb, stockingSheet, "Stocking Events");
        }

        // 3. Individual Sampling Sheets
        pondDetails.forEach((detail, index) => {
          const unitSystem = selectedTableUnit.value;
          const conv = getConversionsForDisplay(detail, selectedTableUnit);
          const sheetName = formatDate(detail.Date).substring(0, 31);

          const sheetData = [];

          // Header
          sheetData.push([
            `${index + 1}. SAMPLING - ${formatDateTime(detail.Date)}`,
          ]);
          sheetData.push([""]);

          // Water quality (same 4×3 order as pond detail form), then other environmental fields
          sheetData.push(["Water Quality Data"]);
          WATER_QUALITY_GRID_ROWS.forEach((triplet) => {
            sheetData.push([
              `${triplet[0].label}:`,
              formatPondDetailWQValue(detail, triplet[0].key),
              "",
              `${triplet[1].label}:`,
              formatPondDetailWQValue(detail, triplet[1].key),
              "",
              `${triplet[2].label}:`,
              formatPondDetailWQValue(detail, triplet[2].key),
            ]);
          });
          sheetData.push([
            "Fertilization Date:",
            detail.FertilizationDate ? formatDateTime(detail.FertilizationDate) : "N/A",
            "",
            "Fertilization Type:",
            detail.FertilizationType || "N/A",
          ]);
          sheetData.push([""]);

          sheetData.push(["Environmental Data"]);
          sheetData.push([
            "Sampling Method:",
            normalizeSamplingMethod(detail.SamplingMethod) || "N/A",
            "",
            "Air Temperature:",
            detail.AirTemperature ? `${detail.AirTemperature}°F` : "N/A",
          ]);
          sheetData.push([
            "Wind Speed:",
            detail.WindSpeed ? `${detail.WindSpeed} mph` : "N/A",
            "",
            "Wind Direction:",
            detail.WindDirection || "N/A",
          ]);
          sheetData.push([
            "Sky:",
            detail.SkyClimate || "N/A",
            "",
            "Moon Phase:",
            detail.MoonPhase || "N/A",
          ]);
          sheetData.push([
            "Barometric Pressure:",
            detail.BaroMetricPressure
              ? `${detail.BaroMetricPressure} mmHg`
              : "N/A",
            "",
            "Fishing Time:",
            (() => {
              const onSite = resolveSessionOnSiteTimes(detail);
              const start = onSite.start || detail.TimeFishingStarted;
              const stop = onSite.stop || detail.TimeFishingStopped;
              return `${formatTime(start)} - ${formatTime(stop)}`;
            })(),
          ]);
          sheetData.push([
            "Length Unit:",
            detail.LengthUnitName || "N/A",
            "",
            "Fishing Duration:",
            calculateFishingDuration(
              formatTime(detail.TimeFishingStarted),
              formatTime(detail.TimeFishingStopped),
              detail
            ),
          ]);
          sheetData.push(["Weight Unit:", detail.WeightUnitName || "N/A"]);
          sheetData.push([""]);

          // Sampling Fish Data
          if (detail.SamplingData && detail.SamplingData.length > 0) {
            sheetData.push(["Sampling Fish Data"]);
            sheetData.push([
              "#",
              "Species",
              "Length",
              "Weight",
              "Ws",
              "Wr",
              "Tag Number",
              "Capture Method",
              "Culled",
              "Released",
            ]);

            detail.SamplingData.forEach((fish, fishIndex) => {
              sheetData.push([
                fishIndex + 1,
                displaySpeciesTableLabel(
                  fish.FishTypeName,
                  fish.FishTypeAbbreviation || fish.Abbreviation
                ),
                formatValue(conv.convertLength(fish.Length), "length", unitSystem, "0"),
                formatValue(conv.convertWeight(fish.Weight), "weight", unitSystem, "0"),
                formatValue(fish.Ws, "Ws", unitSystem, "0"),
                formatValue(fish.Wr, "Wr", unitSystem, "0.000"),
                fish.TagNumber || "N/A",
                fish.CaptureMethod || fish.captureMethod || "N/A",
                fish.Status === "Culled" ? "Yes" : "",
                fish.Status === "Released" ? "Yes" : "",
              ]);
            });
            sheetData.push([""]);
          }

          // Bin Tables for each species
          if (detail.FishType && detail.FishType.length > 0) {
            detail.FishType.forEach((fishType) => {
              const speciesFish = (detail.SamplingData || []).filter(
                (fish) => fish.FishTypeName === fishType.FishTypeName
              );

              if (speciesFish.length > 0) {
                const binFishData = speciesFish.map((fish) => ({
                  ...fish,
                  length: conv.convertLength(fish.Length),
                  relativeWeight: parseFloat(fish.Wr || 0),
                  status: fish.Status || "Unknown",
                  species: fish.FishTypeName || "Unknown",
                }));

                const binData = processBinData(binFishData, unitSystem);

                if (binData.length > 0) {
                  sheetData.push([`${fishType.FishTypeName} - Bin Summary`]);
                  sheetData.push([
                    "Bin",
                    LABEL.NUM,
                    "Culled",
                    "Released",
                    LABEL.AVERAGE_WR_ASCII,
                    LABEL.STD_DEV_WR_ASCII,
                    "% Pop",
                    LABEL.CUM_PCT,
                  ]);

                  augmentBinDataWithPopulationPct(binData).forEach((bin) => {
                    sheetData.push([
                      bin.binLabel,
                      bin.number,
                      bin.culled,
                      bin.released,
                      formatBinWrPercent(bin.averageWr),
                      formatBinWrPercent(bin.stdev),
                      bin.pctPop != null ? bin.pctPop.toFixed(2) : "",
                      bin.cumulativePct != null ? bin.cumulativePct.toFixed(2) : "",
                    ]);
                  });
                  sheetData.push([""]);
                }
              }
            });
          }

          const psdExportRows = computePsdTableRowsForSession(
            detail,
            [],
            selectedTableUnit?.value
          );
          if (psdExportRows.length > 0) {
            sheetData.push(["Proportional Size Distribution"]);
            sheetData.push(["Species", LABEL.NUM_STOCK, "PSD-Q %", "PSD-P %", "PSD-M %", "PSD-T %"]);
            psdExportRows.forEach(({ species, metrics }) => {
              sheetData.push([
                species,
                metrics.nDenominator,
                metrics.psdQ == null ? "" : metrics.psdQ.toFixed(1),
                metrics.psdP == null ? "" : metrics.psdP.toFixed(1),
                metrics.psdM == null ? "" : metrics.psdM.toFixed(1),
                metrics.psdT == null ? "" : metrics.psdT.toFixed(1),
              ]);
            });
            sheetData.push([""]);
          }

          appendCpueExportRows(sheetData, detail);

          // Species Summary
          if (detail.FishType && detail.FishType.length > 0) {
            sheetData.push(["Species Summary"]);
            sheetData.push([
              "Species",
              LABEL.NUM,
              LABEL.AVERAGE_WR_ASCII,
              LABEL.STD_DEV_WR_ASCII,
              "Culled",
              "Released",
              "Longest Fish (L/W)",
              "Heaviest Fish (L/W)",
              "Shortest Fish (L/W)",
              "Lightest Fish (L/W)",
            ]);

            detail.FishType.forEach((summary) => {
              const fishes = (detail.SamplingData || []).filter(
                (f) =>
                  f.FishTypeName === summary.FishTypeName &&
                  f.Length != null &&
                  f.Weight != null
              );
              let biggest = null,
                smallest = null,
                heaviest = null,
                lightest = null;
              if (fishes.length) {
                biggest = fishes[0];
                smallest = fishes[0];
                heaviest = fishes[0];
                lightest = fishes[0];
                fishes.forEach((f) => {
                  if (parseFloat(f.Length) > parseFloat(biggest.Length))
                    biggest = f;
                  if (parseFloat(f.Length) < parseFloat(smallest.Length))
                    smallest = f;
                  if (parseFloat(f.Weight) > parseFloat(heaviest.Weight))
                    heaviest = f;
                  if (parseFloat(f.Weight) < parseFloat(lightest.Weight))
                    lightest = f;
                });
              }

              summary.NumberOfFishes > 0 &&
                sheetData.push([
                  displaySpeciesTableLabel(
                    summary.FishTypeName,
                    summary.FishTypeAbbreviation || summary.Abbreviation
                  ),
                  summary.NumberOfFishes || 0,
                  formatValue(
                    summary.AverageWr,
                    "averageWr",
                    unitSystem,
                    "0.000"
                  ),
                  formatValue(
                    summary.StandardDeviationWr,
                    "stdDevWr",
                    unitSystem,
                    "0.000"
                  ),
                  summary.NumberOfFishesCulled || 0,
                  summary.NumberOfFishesReleased || 0,
                  biggest
                    ? compactFishValue(biggest.Length, biggest.Weight)
                    : "-",
                  heaviest
                    ? compactFishValue(heaviest.Length, heaviest.Weight)
                    : "-",
                  smallest
                    ? compactFishValue(smallest.Length, smallest.Weight)
                    : "-",
                  lightest
                    ? compactFishValue(lightest.Length, lightest.Weight)
                    : "-",
                ]);
            });
            sheetData.push([""]);
          }

          // Notes
          if (detail.PondNotes && detail.PondNotes.length > 0) {
            sheetData.push(["Notes"]);
            detail.PondNotes.forEach((note) => {
              const body = extractPondNoteBodyFromApiRow(note);
              sheetData.push([body || "N/A"]);
            });
            sheetData.push([""]);
          }

          const samplingFooterRow = appendExcelPondStatFooter(sheetData);
          const samplingSheet = XLSX.utils.aoa_to_sheet(sheetData);
          applyExcelPondStatFooterStyle(samplingSheet, samplingFooterRow);

          // Apply styling and merges
          const merges = [];

          // Title merge
          merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 9 } });

          // Section headers
          sheetData.forEach((row, rowIndex) => {
            if (
              row.length === 1 &&
              row[0] &&
              typeof row[0] === "string" &&
              (row[0].includes("Environmental Data") ||
                row[0].includes("Water Quality Data") ||
                row[0].includes("Fishing/Sampling Data") ||
                row[0].includes("Stocking predator records") ||
                row[0].includes("Species Summary") ||
                row[0].includes("Catch per Unit Effort") ||
                row[0].includes("CPUE by Species") ||
                row[0].includes("Notes") ||
                row[0].includes("Bin Summary"))
            ) {
              merges.push({
                s: { r: rowIndex, c: 0 },
                e: { r: rowIndex, c: 9 },
              });
            }
          });

          samplingSheet["!merges"] = merges;

          // Apply cell styling for headers
          samplingSheet["!rows"] = [];
          sheetData.forEach((row, rowIndex) => {
            if (
              row.length === 1 &&
              row[0] &&
              typeof row[0] === "string" &&
              (row[0].includes("Environmental Data") ||
                row[0].includes("Water Quality Data") ||
                row[0].includes("Sampling Fish Data") ||
                row[0].includes("Stocking predator records") ||
                row[0].includes("Species Summary") ||
                row[0].includes("Catch per Unit Effort") ||
                row[0].includes("CPUE by Species") ||
                row[0].includes("Notes") ||
                row[0].includes("Bin Summary"))
            ) {
              // Make section headers bold and larger
              samplingSheet["!rows"][rowIndex] = { hpt: 20 }; // Height in points
            }
          });

          // Apply cell styles for specific headers
          Object.keys(samplingSheet).forEach((cellKey) => {
            if (
              cellKey !== "!ref" &&
              cellKey !== "!merges" &&
              cellKey !== "!cols" &&
              cellKey !== "!rows"
            ) {
              const cell = samplingSheet[cellKey];
              const rowIndex = parseInt(cellKey.replace(/[A-Z]/g, ""));
              const colIndex = cellKey.replace(/\d+/g, "");

              // Style the main title (row 0)
              if (rowIndex === 0) {
                cell.s = {
                  font: { bold: true, sz: 16 },
                  alignment: { horizontal: "center", vertical: "center" },
                };
              }

              // Style section headers
              if (
                sheetData[rowIndex] &&
                sheetData[rowIndex].length === 1 &&
                typeof sheetData[rowIndex][0] === "string" &&
                (sheetData[rowIndex][0].includes("Environmental Data") ||
                  sheetData[rowIndex][0].includes("Water Quality Data") ||
                  sheetData[rowIndex][0].includes("Sampling Fish Data") ||
                  sheetData[rowIndex][0].includes("Stocking predator records") ||
                  sheetData[rowIndex][0].includes("Species Summary") ||
                  sheetData[rowIndex][0].includes("Catch per Unit Effort") ||
                  sheetData[rowIndex][0].includes("CPUE by Species") ||
                  sheetData[rowIndex][0].includes("Notes") ||
                  sheetData[rowIndex][0].includes("Bin Summary"))
              ) {
                cell.s = {
                  font: { bold: true, sz: 14 },
                  alignment: { horizontal: "center", vertical: "center" },
                };
              }

              // Style table headers (row after section headers)
              if (
                sheetData[rowIndex] &&
                Array.isArray(sheetData[rowIndex]) &&
                sheetData[rowIndex].length > 1 &&
                (sheetData[rowIndex].includes("#") ||
                  sheetData[rowIndex].includes("Species") ||
                  sheetData[rowIndex].includes("CPUE") ||
                  sheetData[rowIndex].includes("Area") ||
                  sheetData[rowIndex].includes("Haul #") ||
                  sheetData[rowIndex].includes("Bin"))
              ) {
                cell.s = {
                  font: { bold: true, sz: 12 },
                  alignment: { horizontal: "center", vertical: "center" },
                  fill: { fgColor: { rgb: "E6E6E6" } }, // Light gray background
                };
              }
            }
          });

          // Set column widths
          samplingSheet["!cols"] = [
            { width: 8 }, // #
            { width: 20 }, // Species
            { width: 12 }, // Length
            { width: 12 }, // Weight
            { width: 12 }, // Ws
            { width: 12 }, // Wr
            { width: 12 }, // Tag Number
            { width: 16 }, // Capture Method
            { width: 10 }, // Culled
            { width: 12 }, // Released
            { width: 15 }, // Extra
          ];

          XLSX.utils.book_append_sheet(wb, samplingSheet, sheetName);
        });

        // 3. Overall Species Summary Sheet (from TableAggregates API)
        const aggregatesForExport =
          exportAggregatesRef.current || tableAggregates;
        const overallSummary = Array.isArray(
          aggregatesForExport?.overallSpeciesSummary
        )
          ? aggregatesForExport.overallSpeciesSummary
          : [];
        if (overallSummary.length > 0) {
          const overallData = [
            ["Overall Species Summary"],
            [""],
            [
              "Species",
              LABEL.NUM,
              LABEL.AVERAGE_WR_ASCII,
              LABEL.STD_DEV_WR_ASCII,
              "Culled",
              "Released",
              "Longest Fish (L/W)",
              "Heaviest Fish (L/W)",
              "Shortest Fish (L/W)",
              "Lightest Fish (L/W)",
            ],
          ];

          overallSummary.forEach((summary) => {
            const biggest = summary.longest;
            const smallest = summary.shortest;
            const heaviest = summary.heaviest;
            const lightest = summary.lightest;
            overallData.push([
              displaySpeciesTableLabel(
                summary.FishTypeName,
                summary.FishTypeAbbreviation || summary.Abbreviation
              ),
              summary.NumberOfFishes || 0,
              formatValue(
                summary.AverageWr,
                "averageWr",
                selectedTableUnit.value,
                "0.000"
              ),
              formatValue(
                summary.StandardDeviationWr,
                "stdDevWr",
                selectedTableUnit.value,
                "0.000"
              ),
              summary.NumberOfFishesCulled || 0,
              summary.NumberOfFishesReleased || 0,
              biggest ? compactFishValue(biggest.Length, biggest.Weight) : "-",
              heaviest
                ? compactFishValue(heaviest.Length, heaviest.Weight)
                : "-",
              smallest
                ? compactFishValue(smallest.Length, smallest.Weight)
                : "-",
              lightest
                ? compactFishValue(lightest.Length, lightest.Weight)
                : "-",
            ]);
          });

          // Add overall bin summaries for each species (from TableAggregates API)
          overallData.push([""]);
          overallData.push(["Overall Bin Summaries by Species"]);
          overallData.push([""]);

          const speciesBinsList = Array.isArray(aggregatesForExport?.speciesBins)
            ? aggregatesForExport.speciesBins
            : [];
          speciesBinsList.forEach((sb) => {
            const speciesBins = Array.isArray(sb.bins) ? sb.bins : [];
            if (speciesBins.length > 0) {
              overallData.push([`${sb.species} - Overall Bin Summary`]);
              overallData.push([
                "Bin",
                LABEL.NUM,
                "Culled",
                "Released",
                LABEL.AVERAGE_WR_ASCII,
                LABEL.STD_DEV_WR_ASCII,
                "% Pop",
                LABEL.CUM_PCT,
              ]);

              speciesBins.forEach((bin) => {
                overallData.push([
                  bin.binLabel,
                  bin.number,
                  bin.culled,
                  bin.released,
                  formatBinWrPercent(bin.averageWr),
                  formatBinWrPercent(bin.stdev),
                  bin.pctPop != null ? Number(bin.pctPop).toFixed(2) : "",
                  bin.cumulativePct != null
                    ? Number(bin.cumulativePct).toFixed(2)
                    : "",
                ]);
              });
              overallData.push([""]);
            }
          });

          const overallFooterRow = appendExcelPondStatFooter(overallData);
          const overallSheet = XLSX.utils.aoa_to_sheet(overallData);
          applyExcelPondStatFooterStyle(overallSheet, overallFooterRow);

          // Apply styling and merges
          const overallMerges = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: 9 } }, // Title merge
            { s: { r: 2, c: 0 }, e: { r: 2, c: 9 } }, // Header merge
          ];

          // Add merges for section headers
          overallData.forEach((row, rowIndex) => {
            if (
              row.length === 1 &&
              row[0] &&
              typeof row[0] === "string" &&
              (row[0].includes("Overall Bin Summaries") ||
                row[0].includes("Bin Summary"))
            ) {
              overallMerges.push({
                s: { r: rowIndex, c: 0 },
                e: { r: rowIndex, c: 9 },
              });
            }
          });

          overallSheet["!merges"] = overallMerges;

          // Apply cell styling for headers
          overallSheet["!rows"] = [];
          overallData.forEach((row, rowIndex) => {
            if (
              row.length === 1 &&
              row[0] &&
              typeof row[0] === "string" &&
              (row[0].includes("Overall Species Summary") ||
                row[0].includes("Overall Bin Summaries") ||
                row[0].includes("Bin Summary"))
            ) {
              // Make section headers bold and larger
              overallSheet["!rows"][rowIndex] = { hpt: 20 }; // Height in points
            }
          });

          // Apply cell styles for specific headers
          Object.keys(overallSheet).forEach((cellKey) => {
            if (
              cellKey !== "!ref" &&
              cellKey !== "!merges" &&
              cellKey !== "!cols" &&
              cellKey !== "!rows"
            ) {
              const cell = overallSheet[cellKey];
              const rowIndex = parseInt(cellKey.replace(/[A-Z]/g, ""));
              const colIndex = cellKey.replace(/\d+/g, "");

              // Style the main title (row 0)
              if (rowIndex === 0) {
                cell.s = {
                  font: { bold: true, sz: 16 },
                  alignment: { horizontal: "center", vertical: "center" },
                };
              }

              // Style section headers
              if (
                overallData[rowIndex] &&
                overallData[rowIndex].length === 1 &&
                typeof overallData[rowIndex][0] === "string" &&
                (overallData[rowIndex][0].includes("Overall Species Summary") ||
                  overallData[rowIndex][0].includes("Overall Bin Summaries") ||
                  overallData[rowIndex][0].includes("Bin Summary"))
              ) {
                cell.s = {
                  font: { bold: true, sz: 14 },
                  alignment: { horizontal: "center", vertical: "center" },
                };
              }

              // Style table headers (row after section headers)
              if (
                overallData[rowIndex] &&
                Array.isArray(overallData[rowIndex]) &&
                overallData[rowIndex].length > 1 &&
                (overallData[rowIndex].includes("Species") ||
                  overallData[rowIndex].includes("Bin"))
              ) {
                cell.s = {
                  font: { bold: true, sz: 12 },
                  alignment: { horizontal: "center", vertical: "center" },
                  fill: { fgColor: { rgb: "E6E6E6" } }, // Light gray background
                };
              }
            }
          });

          // Set column widths
          overallSheet["!cols"] = [
            { width: 20 }, // Species
            { width: 10 }, // Count
            { width: 12 }, // Avg Wr
            { width: 12 }, // Std Dev
            { width: 10 }, // Culled
            { width: 12 }, // Released
            { width: 18 }, // Longest
            { width: 18 }, // Heaviest
            { width: 18 }, // Shortest
            { width: 18 }, // Lightest
          ];

          XLSX.utils.book_append_sheet(wb, overallSheet, "Overall Summary");
        }
      }

      // Tagged Fish Tracking (from TableAggregates API)
      const taggedTrackingExport =
        (exportAggregatesRef.current || tableAggregates)?.taggedFishTracking;
      const tagCols = Array.isArray(taggedTrackingExport?.sortedTags)
        ? taggedTrackingExport.sortedTags
        : [];
      const tagDates = Array.isArray(taggedTrackingExport?.orderedDates)
        ? taggedTrackingExport.orderedDates
        : [];
      const tagCells = taggedTrackingExport?.cells || {};
      if (tagCols.length > 0) {
        const taggedSheetRows = [];
        taggedSheetRows.push(["Tagged Fish Tracking"]);
        taggedSheetRows.push([]);
        const header = ["Date"];
        tagCols.forEach((tag) => {
          header.push(`${tag} LGT`, `${tag} WGT`, `${tag} Wr`);
        });
        taggedSheetRows.push(header);
        tagDates.forEach((d) => {
          const line = [d.label];
          tagCols.forEach((tag) => {
            const v = tagCells[tag]?.[d.key];
            if (!v) {
              line.push("-", "-", "-");
            } else {
              line.push(
                formatTaggedLgtWgtCell(v.length),
                formatTaggedLgtWgtCell(v.weight),
                formatValue(v.wr, "Wr", v.unitSystem, "-")
              );
            }
          });
          taggedSheetRows.push(line);
        });
        const taggedFooterRow = appendExcelPondStatFooter(taggedSheetRows);
        const taggedSheet = XLSX.utils.aoa_to_sheet(taggedSheetRows);
        applyExcelPondStatFooterStyle(taggedSheet, taggedFooterRow);
        taggedSheet["!merges"] = [
          { s: { r: 0, c: 0 }, e: { r: 0, c: Math.max(header.length - 1, 1) } },
        ];
        taggedSheet["!cols"] = header.map((_, i) => ({
          width: i === 0 ? 22 : 12,
        }));
        XLSX.utils.book_append_sheet(wb, taggedSheet, "Tagged Fish Tracking");
      }

      const excelBuffer = XLSX.write(wb, {
        bookType: "xlsx",
        type: "array",
      });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      downloadFile(
        blob,
        `Sampling_Report_${Date.now()}.xlsx`,
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      );

      enqueueSnackbar("Excel report generated successfully!", {
        variant: "success",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 2000,
      });
    } catch (error) {
      console.error("Error generating Excel:", error);
      enqueueSnackbar("Error generating Excel file.", { variant: "error" });
    }
  }

  // --- Merged PDF Export Function ---
  function generateMergedPDF() {
    if (!selectedPondInfo || pondDetails.length === 0) {
      enqueueSnackbar("No data available to generate PDF", {
        variant: "warning",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 2000,
      });
      return;
    }

    try {
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 10;
      let yPosition = margin;

      const primaryColor = [41, 128, 185];
      const secondaryColor = [39, 174, 96];
      const lightGray = [240, 240, 240];
      const darkGray = [100, 100, 100];
      const white = [255, 255, 255];
      const black = [0, 0, 0];

      const addSectionTitle = (title, y) => {
        doc.setFillColor(...primaryColor);
        doc.setDrawColor(...primaryColor);
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...white);

        const textWidth =
          (doc.getStringUnitWidth(title) * doc.internal.getFontSize()) /
          doc.internal.scaleFactor;
        doc.roundedRect(margin, y, textWidth + 6, 8, 2, 2, "F");
        doc.text(title, margin + 3, y + 5.5);

        doc.setTextColor(...black);
        /* Chip height is 8mm; return y below chip plus clearance so the next
           line (e.g. subsection titles at font 12) does not draw through the banner */
        return y + 8 + 8;
      };

      const addSubsectionTitle = (title, y) => {
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...primaryColor);
        doc.text(title, margin, y);
        doc.setTextColor(...black);
        return y + 7;
      };

      const addKeyValuePair = (key, value, y, indent = 0) => {
        doc.setFontSize(10);
        const keyStr = String(key);
        const valueStr = String(value ?? "N/A");

        doc.setFont("helvetica", "bold");
        const keyWidth =
          (doc.getStringUnitWidth(keyStr) * doc.internal.getFontSize()) /
          doc.internal.scaleFactor;
        const gapAfterKey = 2;
        const valueX = margin + indent + keyWidth + gapAfterKey;
        const valueWidth = Math.max(pageWidth - margin - valueX, 24);

        doc.setFont("helvetica", "normal");
        const lines = doc.splitTextToSize(valueStr, valueWidth);
        const lineH = 5.5;
        const blockHeight = Math.max(lines.length * lineH, 6);

        let workY = y;
        if (workY + blockHeight > pageHeight - margin) {
          doc.addPage();
          workY = margin;
          yPosition = margin;
        }

        doc.setFont("helvetica", "bold");
        doc.text(keyStr, margin + indent, workY);
        doc.setFont("helvetica", "normal");
        doc.text(lines, valueX, workY);

        return workY + blockHeight;
      };

      const checkPageBreak = (requiredHeight) => {
        if (yPosition + requiredHeight > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      const checkSectionPageBreak = (
        sectionHeaderHeight,
        minContentHeight = 30
      ) => {
        const totalRequiredHeight = sectionHeaderHeight + minContentHeight;
        if (yPosition + totalRequiredHeight > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      const checkTableSectionPageBreak = (headerHeight, minRowsToShow = 3) => {
        const estimatedRowHeight = 6;
        const minTableHeight = minRowsToShow * estimatedRowHeight + 15;
        const totalRequiredHeight = headerHeight + minTableHeight;

        if (yPosition + totalRequiredHeight > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      /** Matches Table View cards: subsection title + 3-column label/value rows. */
      const renderPdfSubsectionThreeColGrid = (title, fieldRows) => {
        yPosition = addSubsectionTitle(title, yPosition);
        yPosition += 1;
        const colW = (pageWidth - 2 * margin) / 3;
        fieldRows.forEach((row) => {
          const rowHeights = row.map((cell) => {
            const labelLines = doc.splitTextToSize(String(cell.label || ""), colW - 1);
            const valueLines = doc.splitTextToSize(
              String(cell.v != null ? cell.v : "N/A"),
              colW - 1
            );
            return Math.max(labelLines.length + valueLines.length, 2);
          });
          const rowHeight = Math.max(...rowHeights) * 3.3 + 1.5;
          checkPageBreak(rowHeight + 1);
          let xCol = margin;
          row.forEach((cell) => {
            const labelLines = doc.splitTextToSize(String(cell.label || ""), colW - 1);
            const valueLines = doc.splitTextToSize(
              String(cell.v != null ? cell.v : "N/A"),
              colW - 1
            );
            doc.setFont("helvetica", "bold");
            doc.setFontSize(7);
            doc.setTextColor(...black);
            doc.text(labelLines, xCol, yPosition, { maxWidth: colW - 1 });
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.text(valueLines, xCol, yPosition + labelLines.length * 3.3, { maxWidth: colW - 1 });
            xCol += colW;
          });
          yPosition += rowHeight;
        });
        doc.setFontSize(10);
        yPosition += 3;
      };

      const renderCpueExportBlock = (block) => {
        if (!block) return;
        checkTableSectionPageBreak(15, 2);
        yPosition = addSubsectionTitle(block.title, yPosition);
        autoTable(doc, {
          head: [block.headers],
          body: [...block.bodyRows, block.footerRow],
          startY: yPosition,
          styles: {
            fontSize: 8,
            cellPadding: 3,
            overflow: "linebreak",
            halign: "center",
          },
          headStyles: {
            fillColor: secondaryColor,
            textColor: white,
            fontStyle: "bold",
            halign: "center",
          },
          alternateRowStyles: { fillColor: lightGray },
          margin: { left: margin, right: margin },
          tableWidth: pageWidth - 2 * margin,
          didParseCell: (data) => {
            if (
              data.section === "body" &&
              data.row.index === block.bodyRows.length
            ) {
              data.cell.styles.fontStyle = "bold";
            }
          },
        });
        yPosition = doc.lastAutoTable.finalY + 10;
      };

      const renderCpueExportSections = (detail) => {
        const sessionBlock = getSessionCpueExportBlock(detail);
        if (sessionBlock) renderCpueExportBlock(sessionBlock);

        const speciesBlocks = getPerSpeciesCpueExportBlocks(
          detail,
          detail?.FishType
        );
        if (speciesBlocks.length > 0) {
          checkTableSectionPageBreak(15, 2);
          yPosition = addSubsectionTitle("CPUE by Species", yPosition);
          speciesBlocks.forEach((block) => renderCpueExportBlock(block));
        }
      };

      const stockingExportUnitSystem = selectedTableUnit.value;
      const selectedFishSet = new Set((selectedFish || []).map((f) => f.label));
      const hasSelectedFishFilter = selectedFishSet.size > 0;
      const stockingSpeciesName = (row) => stockingRowSpeciesLabel(row);
      const matchesStockingSpeciesFilter = (row) => {
        if (!hasSelectedFishFilter) return true;
        return selectedFishSet.has(stockingSpeciesName(row));
      };
      const parseStockingNum = (value) => {
        const n = typeof value === "string" ? parseFloat(value) : Number(value);
        return Number.isFinite(n) ? n : null;
      };
      const summarizePredatorRows = (rows = []) => {
        const grouped = new Map();
        rows.forEach((row) => {
          if (!matchesStockingSpeciesFilter(row)) return;
          const species = stockingSpeciesName(row);
          if (!grouped.has(species)) {
            grouped.set(species, {
              species,
              count: 0,
              wrVals: [],
              lenVals: [],
              wtVals: [],
              tagged: 0,
              batchTotalWeight: 0,
              batchLenNumerator: 0,
              batchLenDen: 0,
            });
          }
          const item = grouped.get(species);
          if (stockingPredatorRowIsBatch(row)) {
            const n = stockingPredatorRowCountDelta(row);
            const tw = parseStockingNum(row?.TotalWeight);
            const len = parseStockingNum(row?.Length);
            item.count += n;
            if (tw !== null) item.batchTotalWeight += tw;
            if (len !== null && n > 0) {
              item.batchLenNumerator += len * n;
              item.batchLenDen += n;
            }
            return;
          }
          const wr = parseStockingNum(row?.Wr);
          const len = parseStockingNum(row?.Length);
          const wt = parseStockingNum(row?.Weight);
          const tag = (row?.Tag ?? "").toString().trim();
          item.count += stockingPredatorRowCountDelta(row);
          if (wr !== null) item.wrVals.push(wr);
          if (len !== null) item.lenVals.push(len);
          if (wt !== null) item.wtVals.push(wt);
          if (tag) item.tagged += 1;
        });
        const avg = (arr) =>
          arr.length ? arr.reduce((sum, x) => sum + x, 0) / arr.length : null;
        const stdev = (arr) => {
          if (!arr.length) return null;
          const mean = avg(arr);
          const variance =
            arr.reduce((sum, x) => sum + Math.pow(x - mean, 2), 0) / arr.length;
          return Math.sqrt(variance);
        };
        return Array.from(grouped.values()).map((item) => {
          const indivWtSum = item.wtVals.reduce((a, b) => a + b, 0);
          const totalWt = item.batchTotalWeight + indivWtSum;
          const avgWeight = item.count > 0 ? totalWt / item.count : null;
          const lenNum =
            item.batchLenNumerator +
            (item.lenVals.length ? item.lenVals.reduce((a, l) => a + l, 0) : 0);
          const lenDen = item.batchLenDen + item.lenVals.length;
          const avgLength = lenDen > 0 ? lenNum / lenDen : null;
          return {
            species: item.species,
            count: item.count,
            avgWr: avg(item.wrVals),
            stdevWr: stdev(item.wrVals),
            avgLength,
            avgWeight,
            tagged: item.tagged,
          };
        });
      };

      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...primaryColor);
      doc.text("FISH SAMPLING REPORT", pageWidth / 2, yPosition, {
        align: "center",
      });
      yPosition += 12;

      doc.setFontSize(7);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...black);

      doc.setDrawColor(200, 200, 200);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 10;

      checkSectionPageBreak(20, 50);
      yPosition = addSectionTitle("POND INFORMATION", yPosition);
      yPosition += 10;

      const pondInfoLeft = [
        { key: "Name:", value: selectedPondInfo.Name || "N/A" },
        { key: "Type:", value: selectedPondInfo.Type || "N/A" },
        { key: "Size (acres):", value: selectedPondInfo.Size || "N/A" },
        {
          key: "Estimated Volume (acre-feet):",
          value: formatEstimatedVolumeAcreFt(selectedPondInfo.EstimatedVolume),
        },
        { key: "Forage:", value: formatForage(selectedPondInfo.Forage) },
        {
          key: "Other Forage:",
          value: selectedPondInfo.ForageType || "N/A",
        },
      ];

      const pondInfoRight = [
        { key: "Location:", value: selectedPondInfo.Location || "N/A" },
        { key: "Age:", value: formatPondAge(selectedPondInfo) },
        { key: "Depth (ft):", value: selectedPondInfo.Depth || "N/A" },
        {
          key: "Water Supply:",
          value: selectedPondInfo.WaterSupply || "N/A",
        },
        {
          key: "Aquatic Vegetation:",
          value: formatAquaticVegetation(selectedPondInfo.AquaticVegetationPresent),
        },
      ];

      let tempY = yPosition;
      const maxLines = Math.max(pondInfoLeft.length, pondInfoRight.length);
      const leftColWidth = 85;
      const rightColStart = margin + leftColWidth + 10;

      for (let i = 0; i < maxLines; i++) {
        checkPageBreak(6);
        let leftBlockHeight = 6;
        let rightBlockHeight = 6;

        if (pondInfoLeft[i]) {
          doc.setFontSize(8); // Changed from 10 to 8
          doc.setFont("helvetica", "bold");
          doc.text(String(pondInfoLeft[i].key), margin, tempY);
          doc.setFont("helvetica", "normal");
          const keyWidth =
            (doc.getStringUnitWidth(String(pondInfoLeft[i].key)) *
              doc.internal.getFontSize()) /
            doc.internal.scaleFactor;
          const valueX = margin + keyWidth + 3;
          const valueWidth = leftColWidth - keyWidth - 3;
          const splittedText = doc.splitTextToSize(
            String(pondInfoLeft[i].value || "N/A"),
            valueWidth
          );
          doc.text(splittedText, valueX, tempY);
          leftBlockHeight = splittedText.length * 5.5;
        }

        if (pondInfoRight[i]) {
          doc.setFontSize(8); // Changed from 10 to 8
          doc.setFont("helvetica", "bold");
          doc.text(String(pondInfoRight[i].key), rightColStart, tempY);
          doc.setFont("helvetica", "normal");
          const keyWidth =
            (doc.getStringUnitWidth(String(pondInfoRight[i].key)) *
              doc.internal.getFontSize()) /
            doc.internal.scaleFactor;
          const valueX = rightColStart + keyWidth + 3;
          const valueWidth = pageWidth - valueX - margin;
          const splittedText = doc.splitTextToSize(
            String(pondInfoRight[i].value || "N/A"),
            valueWidth
          );
          doc.text(splittedText, valueX, tempY);
          rightBlockHeight = splittedText.length * 5.5;
        }

        const lineHeight = Math.max(leftBlockHeight, rightBlockHeight);
        tempY += lineHeight > 6 ? lineHeight : 6;
      }

      const appendFullWidthPondField = (label, text) => {
        checkPageBreak(14);
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...black);
        doc.text(String(label), margin, tempY);
        doc.setFont("helvetica", "normal");
        const labelWidth =
          (doc.getStringUnitWidth(String(label)) * doc.internal.getFontSize()) /
          doc.internal.scaleFactor;
        const valueX = margin + labelWidth + 3;
        const valueWidth = pageWidth - valueX - margin;
        const lines = doc.splitTextToSize(String(text || "N/A"), valueWidth);
        doc.text(lines, valueX, tempY);
        tempY += Math.max(lines.length * 5.5, 6);
      };

      appendFullWidthPondField(
        "Predator Fish:",
        selectedPondInfo.PredatorFishNames?.length
          ? selectedPondInfo.PredatorFishNames.join(", ")
          : "N/A"
      );

      yPosition = tempY + 10;

      doc.setDrawColor(200, 200, 200);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 10;

      if (includeStockingPredators) {
        checkSectionPageBreak(20, 40);
        yPosition = addSectionTitle("STOCKING EVENTS DATA", yPosition);
        yPosition += 4;
        if (stockingEventsSorted.length === 0) {
          doc.setFontSize(9);
          doc.setTextColor(...darkGray);
          doc.text(
            "No stocking events data found for the selected filters/date range.",
            margin,
            yPosition
          );
          doc.setTextColor(...black);
          yPosition += 10;
        } else {
          stockingEventsSorted.forEach((event, eventIdx) => {
            /* Forage/prey rows: always show every stocking row (not filtered by
               sampling species). Predator rows still respect the species filter. */
            const preyRows = Array.isArray(event?.preyRows) ? event.preyRows : [];
            const predatorRows = (event?.predatorRows || []).filter(
              matchesStockingSpeciesFilter
            );
            const predatorSummaryRows = summarizePredatorRows(event?.predatorRows || []);

            checkSectionPageBreak(14, 22);
            yPosition = addSubsectionTitle(
              `Stocking Event ${eventIdx + 1}`,
              yPosition
            );
            yPosition = addKeyValuePair("Date:", formatDateTime(event?.StockingDate), yPosition);
            yPosition = addKeyValuePair(
              "Stocking Type:",
              event?.IsInitial ? "Initial" : "Supplemental",
              yPosition
            );
            yPosition = addKeyValuePair(
              "Length Unit:",
              event?.lengthUnit?.Name || event?.lengthUnit?.name || "N/A",
              yPosition
            );
            yPosition = addKeyValuePair(
              "Weight Unit:",
              event?.weightUnit?.Name || event?.weightUnit?.name || "N/A",
              yPosition
            );
            yPosition = addKeyValuePair(
              "Notes:",
              event?.Notes || event?.notes || "N/A",
              yPosition
            );

            checkTableSectionPageBreak(15, 2);
            yPosition = addSubsectionTitle("Forage entries", yPosition);
            autoTable(doc, {
              head: [[
                "#",
                "Species / Forage",
                "Number",
                "Total wt",
                "Avg len",
                "Avg wt",
                "Tag",
              ]],
              body:
                preyRows.length > 0
                  ? preyRows.map((row, idx) => [
                    String(idx + 1),
                    String(stockingSpeciesName(row)),
                    String(formatPreyHeadcountDisplayFromRow(row)),
                    String(
                      formatValue(
                        parseStockingNum(row?.TotalWeight ?? row?.totalWeight),
                        "weight",
                        stockingExportUnitSystem,
                        "—"
                      )
                    ),
                    String(
                      formatValue(
                        parseStockingNum(row?.MinLength ?? row?.minLength),
                        "length",
                        stockingExportUnitSystem,
                        "—"
                      )
                    ),
                    String(
                      formatValue(
                        parseStockingNum(row?.MaxLength ?? row?.maxLength),
                        "weight",
                        stockingExportUnitSystem,
                        "—"
                      )
                    ),
                    String((row?.Tag ?? "").toString().trim() || "N/A"),
                  ])
                  : [["", "No forage entries", "", "", "", "", ""]],
              startY: yPosition,
              styles: { fontSize: 8, cellPadding: 2.5, halign: "center" },
              headStyles: { fillColor: primaryColor, textColor: white, fontStyle: "bold" },
              columnStyles: { 1: { halign: "left" } },
              alternateRowStyles: { fillColor: lightGray },
              margin: { left: margin, right: margin },
            });
            yPosition = doc.lastAutoTable.finalY + 6;

            checkTableSectionPageBreak(15, 2);
            yPosition = addSubsectionTitle("Predator entries", yPosition);
            const predatorBatchRowsPdf = predatorRows.filter((r) => stockingPredatorRowIsBatch(r));
            const predatorIndivRowsPdf = predatorRows.filter((r) => !stockingPredatorRowIsBatch(r));
            const predatorTableOpts = {
              styles: { fontSize: 8, cellPadding: 2.5, halign: "center" },
              headStyles: { fillColor: primaryColor, textColor: white, fontStyle: "bold" },
              columnStyles: { 1: { halign: "left" } },
              alternateRowStyles: { fillColor: lightGray },
              margin: { left: margin, right: margin },
            };
            if (predatorRows.length === 0) {
              autoTable(doc, {
                head: [["#", "Species", "Length", "Weight", "Ws", "Wr", "Tag"]],
                body: [["", "No predator entries", "", "", "", "", ""]],
                startY: yPosition,
                ...predatorTableOpts,
              });
              yPosition = doc.lastAutoTable.finalY + 6;
            } else {
              if (predatorBatchRowsPdf.length > 0) {
                autoTable(doc, {
                  head: [[
                    "#",
                    "Species",
                    "Total #",
                    "Total wt",
                    "Avg len",
                    "Avg wt",
                  ]],
                  body: predatorBatchRowsPdf.map((row, idx) => {
                    const n = parsePreyHeadcountFromStockingRow(row);
                    const tw = parseStockingNum(row?.TotalWeight);
                    const avgWt =
                      n != null && n > 0 && tw != null ? tw / n : null;
                    return [
                      String(idx + 1),
                      String(stockingSpeciesName(row)),
                      String(formatPreyHeadcountDisplayFromRow(row)),
                      String(
                        formatValue(tw, "weight", stockingExportUnitSystem, "—")
                      ),
                      String(
                        formatValue(
                          parseStockingNum(row?.Length),
                          "length",
                          stockingExportUnitSystem,
                          "—"
                        )
                      ),
                      String(
                        formatValue(avgWt, "weight", stockingExportUnitSystem, "—")
                      ),
                    ];
                  }),
                  startY: yPosition,
                  ...predatorTableOpts,
                });
                yPosition = doc.lastAutoTable.finalY + 6;
              }
              if (predatorIndivRowsPdf.length > 0) {
                checkTableSectionPageBreak(15, 2);
                autoTable(doc, {
                  head: [["#", "Species", "Length", "Weight", "Ws", "Wr", "Tag"]],
                  body: predatorIndivRowsPdf.map((row, idx) => [
                    String(idx + 1),
                    String(stockingSpeciesName(row)),
                    String(
                      formatValue(
                        parseStockingNum(row?.Length),
                        "length",
                        stockingExportUnitSystem,
                        "0"
                      )
                    ),
                    String(
                      formatValue(
                        parseStockingNum(row?.Weight),
                        "weight",
                        stockingExportUnitSystem,
                        "0"
                      )
                    ),
                    String(
                      formatValue(
                        parseStockingNum(row?.Ws),
                        "Ws",
                        stockingExportUnitSystem,
                        "0"
                      )
                    ),
                    String(
                      formatValue(
                        parseStockingNum(row?.Wr),
                        "Wr",
                        stockingExportUnitSystem,
                        "0.000"
                      )
                    ),
                    String((row?.Tag ?? "").toString().trim() || "N/A"),
                  ]),
                  startY: yPosition,
                  ...predatorTableOpts,
                });
                yPosition = doc.lastAutoTable.finalY + 6;
              }
            }

            checkTableSectionPageBreak(15, 2);
            yPosition = addSubsectionTitle("Predator summary by species", yPosition);
            autoTable(doc, {
              head: [[
                "Species",
                LABEL.NUM,
                LABEL.AVERAGE_WR_ASCII,
                LABEL.STD_DEV_WR_ASCII,
                "Avg Length",
                "Avg Weight",
                "Tagged",
              ]],
              body:
                predatorSummaryRows.length > 0
                  ? predatorSummaryRows.map((row) => [
                    String(row.species),
                    String(row.count),
                    String(
                      formatValue(row.avgWr, "Wr", stockingExportUnitSystem, "0.000")
                    ),
                    String(
                      formatValue(row.stdevWr, "Wr", stockingExportUnitSystem, "0.000")
                    ),
                    String(
                      formatValue(
                        row.avgLength,
                        "length",
                        stockingExportUnitSystem,
                        "0"
                      )
                    ),
                    String(
                      formatValue(
                        row.avgWeight,
                        "weight",
                        stockingExportUnitSystem,
                        "0"
                      )
                    ),
                    String(row.tagged),
                  ])
                  : [["No predator summary rows", "", "", "", "", "", ""]],
              startY: yPosition,
              styles: { fontSize: 8, cellPadding: 2.5, halign: "center" },
              headStyles: { fillColor: secondaryColor, textColor: white, fontStyle: "bold" },
              columnStyles: { 0: { halign: "left" } },
              alternateRowStyles: { fillColor: lightGray },
              margin: { left: margin, right: margin },
            });
            yPosition = doc.lastAutoTable.finalY + 10;
          });
        }

        doc.setDrawColor(200, 200, 200);
        doc.line(margin, yPosition, pageWidth - margin, yPosition);
        yPosition += 10;
      }

      pondDetails.forEach((detail, index) => {

        const unitSystem = selectedTableUnit.value;
        const conv = getConversionsForDisplay(detail, selectedTableUnit);
        checkSectionPageBreak(20, 60);

        yPosition = addSectionTitle(
          `${index + 1}. SAMPLING - ${formatDateTime(detail.Date)}`,
          yPosition
        );
        yPosition += 10;

        checkSectionPageBreak(28, 50);

        const fishingSamplingPdfRows = [
          [
            { label: "Sampling Method", v: normalizeSamplingMethod(detail.SamplingMethod) || "N/A" },
            { label: "Length Units", v: detail.LengthUnitName || "N/A" },
            { label: "Weight Units", v: detail.WeightUnitName || "N/A" },
          ],
          [
            {
              label: "Time Fishing/Sampling Started",
              v: (() => {
                const onSite = resolveSessionOnSiteTimes(detail);
                const raw = onSite.start || detail.TimeFishingStarted;
                if (!raw) return "N/A";
                return toClockTimeValue(raw) || formatTime(raw);
              })(),
            },
            {
              label: "Time Fishing/Sampling Stopped",
              v: (() => {
                const onSite = resolveSessionOnSiteTimes(detail);
                const raw = onSite.stop || detail.TimeFishingStopped;
                if (!raw) return "N/A";
                return toClockTimeValue(raw) || formatTime(raw);
              })(),
            },
            {
              label: "Total Time Fishing/Sampling",
              v: calculateFishingDuration(
                formatTime(detail.TimeFishingStarted),
                formatTime(detail.TimeFishingStopped),
                detail
              ),
            },
          ],
          [
            {
              label: "Number of Anglers",
              v:
                detail.NumberOfAnglers != null && detail.NumberOfAnglers !== ""
                  ? String(detail.NumberOfAnglers)
                  : "N/A",
            },
            { label: "File Created", v: formatDateTime(detail.createdAt) },
            { label: "File Updated", v: formatDateTime(detail.updatedAt) },
          ],
        ];
        renderPdfSubsectionThreeColGrid("Fishing/Sampling Data", fishingSamplingPdfRows);

        checkSectionPageBreak(28, 55);
        const waterQualityPdfRows = [
          ...WATER_QUALITY_GRID_ROWS.map((wqRow) =>
            wqRow.map((cell) => ({
              label: cell.label,
              v: formatPondDetailWQValue(detail, cell.key),
            }))
          ),
          [
            {
              label: "Fertilization Date",
              v: detail.FertilizationDate ? formatDateTime(detail.FertilizationDate) : "N/A",
            },
            { label: "Fertilization Type", v: detail.FertilizationType || "N/A" },
            { label: "\u00a0", v: "\u00a0" },
          ],
        ];
        renderPdfSubsectionThreeColGrid("Water Quality Data", waterQualityPdfRows);

        checkSectionPageBreak(18, 28);
        const meteorologicalPdfRows = [
          [
            {
              label: "Air Temperature, F",
              v:
                detail.AirTemperature != null && detail.AirTemperature !== ""
                  ? String(detail.AirTemperature)
                  : "N/A",
            },
            { label: "Sky", v: detail.SkyClimate || "N/A" },
            { label: "Moon Phase", v: detail.MoonPhase || "N/A" },
          ],
          [
            {
              label: "Barometric Pressure, mm Hg",
              v:
                detail.BaroMetricPressure != null && detail.BaroMetricPressure !== ""
                  ? String(detail.BaroMetricPressure)
                  : "N/A",
            },
            {
              label: "Wind Speed, mph",
              v:
                detail.WindSpeed != null && detail.WindSpeed !== ""
                  ? String(detail.WindSpeed)
                  : "N/A",
            },
            { label: "Wind Direction", v: detail.WindDirection || "N/A" },
          ],
        ];
        renderPdfSubsectionThreeColGrid("Meteorological Data", meteorologicalPdfRows);

        const samplingDetailNotesWidth = pageWidth - 2 * margin;
        const noteRows = Array.isArray(detail?.PondNotes) ? detail.PondNotes : [];
        const noteBodies = noteRows
          .map((row) => extractPondNoteBodyFromApiRow(row))
          .filter(Boolean);
        const estimatedNotesHeight =
          (noteBodies.length
            ? noteBodies.reduce((acc, text) => {
                const wrapped = doc.splitTextToSize(text, samplingDetailNotesWidth);
                return acc + wrapped.length * 10;
              }, 0)
            : 12) + 28;
        checkSectionPageBreak(15, Math.min(estimatedNotesHeight, 80));
        yPosition = addSubsectionTitle("Notes", yPosition);
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...black);
        if (noteBodies.length > 0) {
          noteBodies.forEach((text) => {
            const wrappedText = doc.splitTextToSize(text, samplingDetailNotesWidth);
            doc.text(wrappedText, margin, yPosition);
            yPosition += wrappedText.length * 10;
          });
        } else {
          doc.text("No notes for this sampling.", margin, yPosition);
          yPosition += 6;
        }
        yPosition += 8;

        if (detail.SamplingData && detail.SamplingData.length > 0) {
          checkTableSectionPageBreak(15, 3);
          yPosition = addSubsectionTitle("Sampling Fish Data", yPosition);

          const fishTableData = detail.SamplingData.map((fish, fishIndex) => [
            String(fishIndex + 1),
            String(
              displaySpeciesTableLabel(
                fish.FishTypeName,
                fish.FishTypeAbbreviation || fish.Abbreviation
              )
            ),
            String(formatValue(conv.convertLength(fish.Length), "length", unitSystem, "0")),
            String(formatValue(conv.convertWeight(fish.Weight), "weight", unitSystem, "0")),
            String(formatValue(fish.Ws, "Ws", unitSystem, "0")),
            String(formatValue(fish.Wr, "Wr", unitSystem, "0.000")),
            String(fish.TagNumber || "N/A"),
            String(fish.CaptureMethod || fish.captureMethod || "N/A"),
            fish.Status === "Culled" ? "Yes" : "",
            fish.Status === "Released" ? "Yes" : "",
          ]);

          autoTable(doc, {
            head: [
              [
                "#",
                "Species",
                "Length",
                "Weight",
                "Ws",
                "Wr",
                "Tag Number",
                "Capture Method",
                "Culled",
                "Released",
              ],
            ],
            body: fishTableData,
            startY: yPosition,
            styles: {
              fontSize: 7,
              cellPadding: 2,
              overflow: "linebreak",
              halign: "center",
            },
            bodyStyles: {
              fontSize: 6.5,
              cellPadding: 2,
              valign: "middle",
            },
            headStyles: {
              fillColor: primaryColor,
              textColor: white,
              fontStyle: "bold",
              fontSize: 7,
              cellPadding: 1.8,
              overflow: "visible",
              halign: "center",
            },
            columnStyles: {
              0: { halign: "center", cellWidth: 8 },
              1: { halign: "left", cellWidth: 32 },
              2: { halign: "center", cellWidth: 17 },
              3: { halign: "center", cellWidth: 17 },
              4: { halign: "center", cellWidth: 12 },
              5: { halign: "center", cellWidth: 12 },
              6: { halign: "center", cellWidth: 20 },
              7: { halign: "center", cellWidth: 28 },
              8: { halign: "center", cellWidth: 12 },
              9: { halign: "center", cellWidth: 16 },
            },
            alternateRowStyles: { fillColor: lightGray },
            margin: { left: margin, right: margin },
            tableWidth: pageWidth - 2 * margin,
          });

          yPosition = doc.lastAutoTable.finalY + 10;
        }

        // Bin Tables for each species
        if (detail.FishType && detail.FishType.length > 0) {
          detail.FishType.forEach((fishType) => {
            const speciesFish = (detail.SamplingData || []).filter(
              (fish) => fish.FishTypeName === fishType.FishTypeName
            );

            if (speciesFish.length > 0) {
              const binFishData = speciesFish.map((fish) => ({
                ...fish,
                length: conv.convertLength(fish.Length),
                relativeWeight: parseFloat(fish.Wr || 0),
                status: fish.Status || "Unknown",
                species: fish.FishTypeName || "Unknown",
              }));

              const binData = processBinData(binFishData, unitSystem);

              if (binData.length > 0) {
                checkTableSectionPageBreak(15, 2);
                yPosition = addSubsectionTitle(
                  `${fishType.FishTypeName} - Bin Summary`,
                  yPosition
                );

                const binRowsAug = augmentBinDataWithPopulationPct(binData);

                autoTable(doc, {
                  head: [
                    [
                      "Bin",
                      LABEL.NUM,
                      "Culled",
                      "Released",
                      LABEL.AVERAGE_WR_ASCII,
                      LABEL.STD_DEV_WR_ASCII,
                      "% Pop",
                      LABEL.CUM_PCT,
                    ],
                  ],
                  body: binRowsAug.map((bin) => [
                    bin.binLabel,
                    bin.number,
                    bin.culled,
                    bin.released,
                    formatBinWrPercent(bin.averageWr),
                    formatBinWrPercent(bin.stdev),
                    bin.pctPop != null ? bin.pctPop.toFixed(2) : "",
                    bin.cumulativePct != null ? bin.cumulativePct.toFixed(2) : "",
                  ]),
                  startY: yPosition,
                  styles: {
                    fontSize: 8,
                    cellPadding: 3,
                    overflow: "linebreak",
                    halign: "center",
                  },
                  headStyles: {
                    fillColor: secondaryColor,
                    textColor: white,
                    fontStyle: "bold",
                    halign: "center",
                  },
                  alternateRowStyles: { fillColor: lightGray },
                  margin: { left: margin, right: margin },
                  tableWidth: pageWidth - 2 * margin,
                });

                yPosition = doc.lastAutoTable.finalY + 10;
              }
            }
          });
        }

        const psdPdfRows = computePsdTableRowsForSession(
          detail,
          [],
          selectedTableUnit?.value
        );
        if (psdPdfRows.length > 0) {
          checkTableSectionPageBreak(12, 2);
          yPosition = addSubsectionTitle("Proportional Size Distribution", yPosition);
          autoTable(doc, {
            head: [["Species", LABEL.NUM_STOCK, "PSD-Q %", "PSD-P %", "PSD-M %", "PSD-T %"]],
            body: psdPdfRows.map(({ species, metrics }) => [
              species,
              metrics.nDenominator,
              metrics.psdQ == null ? "—" : metrics.psdQ.toFixed(1),
              metrics.psdP == null ? "—" : metrics.psdP.toFixed(1),
              metrics.psdM == null ? "—" : metrics.psdM.toFixed(1),
              metrics.psdT == null ? "—" : metrics.psdT.toFixed(1),
            ]),
            startY: yPosition,
            styles: {
              fontSize: 8,
              cellPadding: 3,
              overflow: "linebreak",
              halign: "center",
            },
            headStyles: {
              fillColor: secondaryColor,
              textColor: white,
              fontStyle: "bold",
              halign: "center",
            },
            alternateRowStyles: { fillColor: lightGray },
            margin: { left: margin, right: margin },
            tableWidth: pageWidth - 2 * margin,
          });
          yPosition = doc.lastAutoTable.finalY + 10;
        }

        renderCpueExportSections(detail);

        if (detail.FishType && detail.FishType.length > 0) {
          checkTableSectionPageBreak(15, 2);
          yPosition = addSubsectionTitle("Species Summary", yPosition);

          const summaryTableData = detail.FishType.map((summary) => {
            // Find biggest/smallest/heaviest/lightest for this species in this sampling
            const fishes = (detail.SamplingData || []).filter(
              (f) =>
                f.FishTypeName === summary.FishTypeName &&
                f.Length != null &&
                f.Weight != null
            );
            let biggest = null,
              smallest = null,
              heaviest = null,
              lightest = null;
            if (fishes.length) {
              biggest = fishes[0];
              smallest = fishes[0];
              heaviest = fishes[0];
              lightest = fishes[0];
              fishes.forEach((f) => {
                if (parseFloat(f.Length) > parseFloat(biggest.Length))
                  biggest = f;
                if (parseFloat(f.Length) < parseFloat(smallest.Length))
                  smallest = f;
                if (parseFloat(f.Weight) > parseFloat(heaviest.Weight))
                  heaviest = f;
                if (parseFloat(f.Weight) < parseFloat(lightest.Weight))
                  lightest = f;
              });
            }
            return [
              displaySpeciesTableLabel(
                summary.FishTypeName,
                summary.FishTypeAbbreviation || summary.Abbreviation
              ),
              String(summary.NumberOfFishes || 0),
              String(
                formatValue(summary.AverageWr, "averageWr", unitSystem, "0.000")
              ),
              String(
                formatValue(
                  summary.StandardDeviationWr,
                  "stdDevWr",
                  unitSystem,
                  "0.000"
                )
              ),
              String(summary.NumberOfFishesCulled || 0),
              String(summary.NumberOfFishesReleased || 0),
              biggest ? compactFishValueForPdf(biggest.Length, biggest.Weight) : "-",
              heaviest
                ? compactFishValueForPdf(heaviest.Length, heaviest.Weight)
                : "-",
              smallest
                ? compactFishValueForPdf(smallest.Length, smallest.Weight)
                : "-",
              lightest
                ? compactFishValueForPdf(lightest.Length, lightest.Weight)
                : "-",
            ];
          });

          autoTable(doc, {
            head: [
              [
                "Species",
                LABEL.NUM,
                LABEL.AVERAGE_WR_ASCII,
                LABEL.STD_DEV_WR_ASCII,
                "Culled",
                "Released",
                "Longest Fish (L/W)",
                "Heaviest Fish (L/W)",
                "Shortest Fish (L/W)",
                "Lightest Fish (L/W)",
              ],
            ],
            body: summaryTableData,
            startY: yPosition,
            styles: {
              fontSize: 7, // smaller font for better fit
              cellPadding: 2,
              overflow: "linebreak",
              halign: "center",
            },
            headStyles: {
              fillColor: secondaryColor,
              textColor: white,
              fontStyle: "bold",
              halign: "center",
            },
            columnStyles: {
              0: { halign: "left", cellWidth: 29 }, // Species
              1: { halign: "center", cellWidth: 12 }, // count
              2: { halign: "center", cellWidth: 15 }, // Avg Wr
              3: { halign: "center", cellWidth: 15 }, // Std Dev
              4: { halign: "center", cellWidth: 13 }, // Culled
              5: { halign: "center", cellWidth: 16 }, // Released
              6: { halign: "center", cellWidth: 20 }, // Longest
              7: { halign: "center", cellWidth: 20 }, // Heaviest
              8: { halign: "center", cellWidth: 20 }, // Shortest
              9: { halign: "center", cellWidth: 20 }, // Lightest
            },
            alternateRowStyles: { fillColor: lightGray },
            margin: { left: margin, right: margin },
            tableWidth: pageWidth - 2 * margin,
          });

          yPosition = doc.lastAutoTable.finalY + 15;

          // Keep effort/method columns in a dedicated table to avoid
          // squeezing the full species summary into unreadable column widths.
          checkTableSectionPageBreak(15, 2);
          yPosition = addSubsectionTitle(
            "Species Effort & Capture Method Summary",
            yPosition
          );

          const fishingDurationHours = resolveSessionOnSiteTimes(detail).hours;
          const anglersCount = Number(detail.NumberOfAnglers) || 0;

          const effortSummaryData = detail.FishType.filter(
            (summary) => Number(summary.NumberOfFishes) > 0
          ).map((summary) => {
            const captureMethodCounts = getCaptureMethodCounts(
              detail.SamplingData,
              summary.FishTypeName || "Unknown"
            );
            const anglingFishCount = captureMethodCounts.angling;
            const fishCaughtPerHourAngler =
              anglersCount > 0 && fishingDurationHours > 0 && anglingFishCount > 0
                ? (
                    anglingFishCount /
                    fishingDurationHours /
                    anglersCount
                  ).toFixed(2)
                : "-";

            return [
              displaySpeciesTableLabel(
                summary.FishTypeName,
                summary.FishTypeAbbreviation || summary.Abbreviation
              ),
              String(anglersCount),
              String(fishCaughtPerHourAngler),
              String(captureMethodCounts.seine),
              String(captureMethodCounts.gillnet),
              String(captureMethodCounts.fykeNet),
              String(captureMethodCounts.electrofishing),
            ];
          });

          if (effortSummaryData.length > 0) {
            autoTable(doc, {
              head: [
                [
                  "Species",
                  LABEL.NUM_ANGLERS,
                  LABEL.FISH_CAUGHT_PER_ANGLER_HR,
                  "Seine",
                  "Gillnet",
                  "Fyke Net",
                  displayMethodLabel("Electrofishing"),
                ],
              ],
              body: effortSummaryData,
              startY: yPosition,
              styles: {
                fontSize: 7,
                cellPadding: 2,
                overflow: "linebreak",
                halign: "center",
              },
              headStyles: {
                fillColor: secondaryColor,
                textColor: white,
                fontStyle: "bold",
                halign: "center",
              },
              columnStyles: {
                0: { halign: "left", cellWidth: 34 },
                1: { halign: "center", cellWidth: 22 },
                2: { halign: "center", cellWidth: 27 },
                3: { halign: "center", cellWidth: 20 },
                4: { halign: "center", cellWidth: 20 },
                5: { halign: "center", cellWidth: 15 },
                6: { halign: "center", cellWidth: 27 },
              },
              alternateRowStyles: { fillColor: lightGray },
              margin: { left: margin, right: margin },
              tableWidth: pageWidth - 2 * margin,
            });

            yPosition = doc.lastAutoTable.finalY + 15;
          }
        }

        if (index < pondDetails.length - 1) {
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(0.5);
          doc.line(margin, yPosition, pageWidth - margin, yPosition);
          doc.setLineWidth(0.2);
          yPosition += 15;
        }
      });

      if (pondDetails.length > 0) {
        const aggregatesForPdf =
          exportAggregatesRef.current || tableAggregates;
        const overallSummary = Array.isArray(
          aggregatesForPdf?.overallSpeciesSummary
        )
          ? aggregatesForPdf.overallSpeciesSummary
          : [];
        if (overallSummary.length > 0) {
          checkTableSectionPageBreak(20, Math.min(3, overallSummary.length));
          yPosition = addSectionTitle("Overall Species Summary", yPosition);

          const summaryTableData = overallSummary.map((summary) => {
            const biggest = summary.longest;
            const smallest = summary.shortest;
            const heaviest = summary.heaviest;
            const lightest = summary.lightest;
            return [
              displaySpeciesTableLabel(
                summary.FishTypeName,
                summary.FishTypeAbbreviation || summary.Abbreviation
              ),
              String(summary.NumberOfFishes || 0),
              String(
                formatValue(
                  summary.AverageWr,
                  "averageWr",
                  selectedTableUnit.value,
                  "0.000"
                )
              ),
              String(
                formatValue(
                  summary.StandardDeviationWr,
                  "stdDevWr",
                  selectedTableUnit.value,
                  "0.000"
                )
              ),
              String(summary.NumberOfFishesCulled || 0),
              String(summary.NumberOfFishesReleased || 0),
              biggest ? compactFishValueForPdf(biggest.Length, biggest.Weight) : "-",
              heaviest
                ? compactFishValueForPdf(heaviest.Length, heaviest.Weight)
                : "-",
              smallest
                ? compactFishValueForPdf(smallest.Length, smallest.Weight)
                : "-",
              lightest
                ? compactFishValueForPdf(lightest.Length, lightest.Weight)
                : "-",
            ];
          });

          autoTable(doc, {
            head: [
              [
                "Species",
                LABEL.NUM,
                LABEL.AVERAGE_WR_ASCII,
                LABEL.STD_DEV_WR_ASCII,
                "Culled",
                "Released",
                "Biggest Fish (L/W)",
                "Heaviest Fish (L/W)",
                "Smallest Fish (L/W)",
                "Lightest Fish (L/W)",
              ],
            ],
            body: summaryTableData,
            startY: yPosition,
            styles: {
              fontSize: 7,
              cellPadding: 2,
              overflow: "linebreak",
              halign: "center",
            },
            headStyles: {
              fillColor: secondaryColor,
              textColor: white,
              fontStyle: "bold",
              halign: "center",
              cellWidth: "wrap", // allow header wrapping
            },
            columnStyles: {
              0: { halign: "left", cellWidth: 29 }, // Species
              1: { halign: "center", cellWidth: 12 }, // count
              2: { halign: "center", cellWidth: 15 }, // Avg Wr
              3: { halign: "center", cellWidth: 15 }, // Std Dev
              4: { halign: "center", cellWidth: 13 }, // Culled
              5: { halign: "center", cellWidth: 16 }, // Released
              6: { halign: "center", cellWidth: 20 }, // Longest
              7: { halign: "center", cellWidth: 20 }, // Heaviest
              8: { halign: "center", cellWidth: 20 }, // Shortest
              9: { halign: "center", cellWidth: 20 }, // Lightest
            },
            alternateRowStyles: { fillColor: lightGray },
            margin: { left: margin, right: margin },
            tableWidth: "wrap", // wrap instead of forcing overflow
          });

          yPosition = doc.lastAutoTable.finalY + 15;
        }

        // Add Overall Summary Bin Size Report (from TableAggregates API)
        if (pondDetails.length > 0) {
          const speciesBinsListPdf = Array.isArray(aggregatesForPdf?.speciesBins)
            ? aggregatesForPdf.speciesBins
            : [];
          const hasBinData = speciesBinsListPdf.some(
            (sb) => Array.isArray(sb.bins) && sb.bins.length > 0
          );

          if (hasBinData) {
            checkTableSectionPageBreak(20, 3);
            yPosition = addSectionTitle("Overall Summary Bin Size Report", yPosition);

            speciesBinsListPdf.forEach((sb) => {
              const speciesBins = Array.isArray(sb.bins) ? sb.bins : [];
              if (speciesBins.length > 0) {
                checkTableSectionPageBreak(15, speciesBins.length + 2);
                yPosition = addSubsectionTitle(`${sb.species} - Overall Bin Summary`, yPosition);

                autoTable(doc, {
                  head: [
                    [
                      "Bin",
                      LABEL.NUM,
                      "Culled",
                      "Released",
                      LABEL.AVERAGE_WR_ASCII,
                      LABEL.STD_DEV_WR_ASCII,
                      "% Pop",
                      LABEL.CUM_PCT,
                    ],
                  ],
                  body: speciesBins.map((bin) => [
                    bin.binLabel,
                    bin.number,
                    bin.culled,
                    bin.released,
                    formatBinWrPercent(bin.averageWr),
                    formatBinWrPercent(bin.stdev),
                    bin.pctPop != null ? Number(bin.pctPop).toFixed(2) : "",
                    bin.cumulativePct != null
                      ? Number(bin.cumulativePct).toFixed(2)
                      : "",
                  ]),
                  startY: yPosition,
                  styles: {
                    fontSize: 8,
                    cellPadding: 3,
                    overflow: "linebreak",
                    halign: "center",
                  },
                  headStyles: {
                    fillColor: secondaryColor,
                    textColor: white,
                    fontStyle: "bold",
                    halign: "center",
                  },
                  alternateRowStyles: { fillColor: lightGray },
                  margin: { left: margin, right: margin },
                  tableWidth: pageWidth - 2 * margin,
                });

                yPosition = doc.lastAutoTable.finalY + 10;
              }
            });
          }
        }

        const taggedTrackingPdf =
          (exportAggregatesRef.current || tableAggregates)?.taggedFishTracking;
        const taggedPivotPdf = {
          orderedDates: taggedTrackingPdf?.orderedDates || [],
          sortedTags: taggedTrackingPdf?.sortedTags || [],
          getEntry: (tag, key) =>
            taggedTrackingPdf?.cells?.[tag]?.[key] ?? null,
        };
        if (taggedPivotPdf.sortedTags.length > 0) {
          const TAGS_PER_CHUNK_PDF = 4;
          const allTags = taggedPivotPdf.sortedTags;
          const numChunks = Math.ceil(allTags.length / TAGS_PER_CHUNK_PDF);

          checkTableSectionPageBreak(22, 18);
          yPosition = addSectionTitle("Tagged Fish Tracking", yPosition);

          for (let ci = 0; ci < numChunks; ci++) {
            const chunk = allTags.slice(
              ci * TAGS_PER_CHUNK_PDF,
              ci * TAGS_PER_CHUNK_PDF + TAGS_PER_CHUNK_PDF
            );
            const estRows = taggedPivotPdf.orderedDates.length + 4;
            checkTableSectionPageBreak(28, Math.min(estRows, 20));

            yPosition = addSubsectionTitle(
              `Group ${ci + 1} of ${numChunks} (${chunk.length} tag${chunk.length === 1 ? "" : "s"})`,
              yPosition
            );
            /* addSubsectionTitle returns y+7 from the title baseline; add room before body text */
            yPosition += 3;
            doc.setFontSize(7);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(...black);
            const tagListLines = doc.splitTextToSize(
              chunk.join(", "),
              pageWidth - 2 * margin
            );
            doc.text(tagListLines, margin, yPosition);
            yPosition += tagListLines.length * 3.6 + 5;
            doc.setTextColor(...black);

            const headRow1 = [
              {
                content: "Date",
                rowSpan: 2,
                styles: { valign: "middle", halign: "left" },
              },
              ...chunk.map((tag) => ({
                content: String(tag),
                colSpan: 3,
                styles: { halign: "center" },
              })),
            ];
            const headRow2 = chunk.flatMap(() => ["LGT", "WGT", "Wr"]);

            const taggedBody = taggedPivotPdf.orderedDates.map((d) => [
              d.label,
              ...chunk.flatMap((tag) => {
                const e = taggedPivotPdf.getEntry(tag, d.key);
                if (!e) return ["-", "-", "-"];
                return [
                  String(formatTaggedLgtWgtCell(e.length)),
                  String(formatTaggedLgtWgtCell(e.weight)),
                  String(formatValue(e.wr, "Wr", e.unitSystem, "-")),
                ];
              }),
            ]);

            const applyRightDivider = (cellStyles, width = 0.9) => {
              const currentLineWidth = cellStyles.lineWidth;
              if (
                currentLineWidth &&
                typeof currentLineWidth === "object" &&
                !Array.isArray(currentLineWidth)
              ) {
                cellStyles.lineWidth = {
                  ...currentLineWidth,
                  right: Math.max(currentLineWidth.right || 0, width),
                };
              } else {
                const uniformWidth =
                  typeof currentLineWidth === "number" ? currentLineWidth : 0;
                cellStyles.lineWidth = {
                  top: uniformWidth,
                  right: Math.max(uniformWidth, width),
                  bottom: uniformWidth,
                  left: uniformWidth,
                };
              }
              cellStyles.lineColor = [0, 0, 0];
            };

            autoTable(doc, {
              head: [headRow1, headRow2],
              body: taggedBody,
              startY: yPosition,
              styles: {
                fontSize: 8,
                cellPadding: 2.2,
                overflow: "linebreak",
                halign: "center",
                valign: "middle",
              },
              headStyles: {
                fillColor: secondaryColor,
                textColor: white,
                fontStyle: "bold",
                halign: "center",
              },
              columnStyles: {
                0: { halign: "left", cellWidth: 28 },
              },
              alternateRowStyles: { fillColor: lightGray },
              margin: { left: margin, right: margin },
              tableWidth: pageWidth - 2 * margin,
              didParseCell: (data) => {
                const { section, row, column, cell } = data;
                if (section === "head" && row.index === 0) {
                  // Row 1 uses colSpan=3 per tag, so each tag cell marks a group boundary.
                  if (column.index === 0 || column.index >= 1) {
                    applyRightDivider(cell.styles);
                  }
                  return;
                }

                // Row 2 and body rows are expanded columns: Date | (LGT, WGT, Wr) x N.
                const isGroupBoundaryColumn =
                  column.index === 0 || (column.index > 0 && column.index % 3 === 0);
                if (isGroupBoundaryColumn) {
                  applyRightDivider(cell.styles);
                }
              },
            });

            yPosition = doc.lastAutoTable.finalY + 12;
          }

          yPosition += 3;
        }
      }

      const totalPages = doc.internal.getNumberOfPages();

      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        drawPondStatPdfFooter(doc, pageWidth, pageHeight, margin);
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...darkGray);
        doc.text(
          `Page ${i} of ${totalPages}`,
          pageWidth - margin - 20,
          pageHeight - 18
        );
      }

      const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .split("T")[0];
      const fileName = `Fish_Sampling_Report_${selectedPondInfo.Name.replace(
        /\s+/g,
        "_"
      )}_${timestamp}.pdf`;

      const pdfBlob = doc.output("blob");

      downloadFile(pdfBlob, fileName, "application/pdf");

      enqueueSnackbar("PDF report generated successfully!", {
        variant: "success",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 2000,
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      enqueueSnackbar(`Failed to generate PDF report: ${error.message}`, {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 2000,
      });
    }
  }
};

export default FishDataTableView;

function augmentBinDataWithPopulationPct(binData) {
  const total = (binData || []).reduce((s, b) => s + (b.number || 0), 0);
  let cum = 0;
  return (binData || []).map((b) => {
    const pctPop = total > 0 ? ((b.number || 0) / total) * 100 : 0;
    cum += pctPop;
    return {
      ...b,
      pctPop,
      cumulativePct: Math.min(100, Number(cum.toFixed(4))),
    };
  });
}

function processBinData(data, unitSystem, binWidth = null) {
  // Determine bin width based on unit system
  const defaultBinWidth = unitSystem === "Metric" ? 10 : 1;
  const actualBinWidth = binWidth || defaultBinWidth;

  // Filter invalid/non-positive lengths
  const validFish = (data || []).filter(
    (f) => Number.isFinite(f.length) && f.length > 0
  );
  if (validFish.length === 0) return [];

  const lengths = validFish.map((f) => f.length);
  const minRaw = Math.min(...lengths);
  const maxRaw = Math.max(...lengths);

  // Bin domain: floor start to nearest bin width; ceil end to nearest bin width
  const minStart = Math.floor(minRaw / actualBinWidth) * actualBinWidth;
  let maxEnd = Math.ceil(maxRaw / actualBinWidth) * actualBinWidth;

  // Ensure at least one bin if all lengths equal
  if (maxEnd === minStart) {
    maxEnd = minStart + actualBinWidth;
  }

  const binCount = Math.max(1, Math.round((maxEnd - minStart) / actualBinWidth));

  const bins = Array.from({ length: binCount }, (_, i) => {
    const start = minStart + i * actualBinWidth;
    const end = start + actualBinWidth;

    // Format label based on unit system - non-overlapping for Metric, overlapping for English
    const label = unitSystem === "Metric"
      ? `${start}-${end - 1}`  // Integer values for metric (e.g., "13-25" instead of "13-26")
      : `${start.toFixed(1)}-${end.toFixed(1)}`;  // 1 decimal place for English (e.g., "0.5-1.0")

    return {
      min: start,
      max: end,
      label: label,
      number: 0,
      culled: 0,
      released: 0,
      totalWr: 0,
      wrSquared: 0,
      fishCount: 0,
    };
  });

  for (const fish of validFish) {
    const length = fish.length;

    // Compute index; assign internal boundaries to next bin (start-inclusive),
    // and clamp max to last bin to include the maximum value
    let index = Math.floor((length - minStart) / actualBinWidth);
    if (index < 0) continue;
    if (index >= binCount) index = binCount - 1;

    const bin = bins[index];
    bin.number += 1;
    bin.fishCount += 1;

    const wr = Number.isFinite(fish.relativeWeight) ? fish.relativeWeight : 0;
    bin.totalWr += wr;
    bin.wrSquared += wr * wr;

    if (fish.status === "Culled") bin.culled += 1;
    if (fish.status === "Released") bin.released += 1;
  }

  return bins
    .map((bin) => {
      const averageWr = bin.fishCount > 0 ? bin.totalWr / bin.fishCount : 0;
      const stdev =
        bin.fishCount > 1
          ? Math.sqrt(
            (bin.wrSquared - (bin.totalWr * bin.totalWr) / bin.fishCount) /
            (bin.fishCount - 1)
          )
          : 0;

      return {
        binLabel: bin.label,
        number: bin.number,
        culled: bin.culled,
        released: bin.released,
        averageWr,
        stdev,
      };
    })
    .filter((bin) => bin.number > 0);
}

function BinsTable({ binData, unitSystem: _unitSystem, showPopulationPct = false }) {
  if (!binData || !binData.length)
    return <div className="text-gray-500">No bin data available.</div>;
  const rows = showPopulationPct ? augmentBinDataWithPopulationPct(binData) : binData;
  return (
    <div className="table-responsive mt-2" style={MODERN_TABLE_WRAPPER_STYLE}>
      <table className={MODERN_TABLE_CLASS}>
        <thead className={MODERN_TABLE_HEAD_CLASS} style={MODERN_TABLE_HEAD_STYLE}>
          <tr>
            <th className="text-center" style={MODERN_TABLE_CELL_STYLE}>Bin</th>
            <th className="text-center" style={MODERN_TABLE_CELL_STYLE}>{LABEL.NUM}</th>
            <th className="text-center" style={MODERN_TABLE_CELL_STYLE}>Culled</th>
            <th className="text-center" style={MODERN_TABLE_CELL_STYLE}>Released</th>
            <th className="text-center" style={MODERN_TABLE_CELL_STYLE}>
              Avg <WrLabel />
            </th>
            <th className="text-center" style={MODERN_TABLE_CELL_STYLE}>
              {LABEL.STD_DEV} <WrLabel />
            </th>
            {showPopulationPct && (
              <>
                <th className="text-center" style={MODERN_TABLE_CELL_STYLE}>% Pop</th>
                <th className="text-center" style={MODERN_TABLE_CELL_STYLE}>{LABEL.CUM_PCT}</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((bin, idx) => (
            <tr key={idx}>
              <td className="text-center fw-semibold" style={MODERN_TABLE_CELL_STYLE}>
                {bin.binLabel}
              </td>
              <td className="text-center" style={MODERN_TABLE_CELL_STYLE}>{bin.number}</td>
              <td className="text-center" style={MODERN_TABLE_CELL_STYLE}>{bin.culled}</td>
              <td className="text-center" style={MODERN_TABLE_CELL_STYLE}>{bin.released}</td>
              <td className="text-center" style={MODERN_TABLE_CELL_STYLE}>
                {formatBinWrPercent(bin.averageWr)}
              </td>
              <td className="text-center" style={MODERN_TABLE_CELL_STYLE}>
                {formatBinWrPercent(bin.stdev)}
              </td>
              {showPopulationPct && (
                <>
                  <td className="text-center" style={MODERN_TABLE_CELL_STYLE}>
                    {bin.pctPop != null ? bin.pctPop.toFixed(2) : "—"}
                  </td>
                  <td className="text-center" style={MODERN_TABLE_CELL_STYLE}>
                    {bin.cumulativePct != null ? bin.cumulativePct.toFixed(2) : "—"}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Helper to find biggest/smallest/heaviest/lightest fish for a species across all pondDetails
function getOverallExtremesFish(speciesName, pondDetails, selectedTableUnit) {
  const allFish = pondDetails.flatMap((detail) => {
    const conv = getConversionsForDisplay(detail, selectedTableUnit);
    return (detail.SamplingData || [])
      .filter(
        (f) =>
          f.FishTypeName === speciesName && f.Length != null && f.Weight != null
      )
      .map((f) => ({
        ...f,
        Length: conv.convertLength(f.Length),
        Weight: conv.convertWeight(f.Weight),
      }));
  });
  if (!allFish.length)
    return { biggest: null, smallest: null, heaviest: null, lightest: null };
  let biggest = allFish[0],
    smallest = allFish[0],
    heaviest = allFish[0],
    lightest = allFish[0];
  allFish.forEach((f) => {
    if (parseFloat(f.Length) > parseFloat(biggest.Length)) biggest = f;
    if (parseFloat(f.Length) < parseFloat(smallest.Length)) smallest = f;
    if (parseFloat(f.Weight) > parseFloat(heaviest.Weight)) heaviest = f;
    if (parseFloat(f.Weight) < parseFloat(lightest.Weight)) lightest = f;
  });
  return { biggest, smallest, heaviest, lightest };
}

function mapAllPondFishToDisplayUnits(pondDetails, selectedTableUnit) {
  return pondDetails.flatMap((detail) => {
    const conv = getConversionsForDisplay(detail, selectedTableUnit);
    return (detail.SamplingData || []).map((fish) => ({
      ...fish,
      length: conv.convertLength(fish.Length),
      relativeWeight: parseFloat(fish.Wr || 0),
      status: fish.Status || "Unknown",
      species: fish.FishTypeName || "Unknown",
    }));
  });
}

function appendExcelPondStatFooter(sheetData) {
  sheetData.push([""]);
  sheetData.push([PONDSTAT_EXPORT_FOOTER]);
  return sheetData.length - 1;
}

// Utility for Summary by Species L/W (always two decimal places)
function compactFishValue(length, weight) {
  return formatSpeciesExtremeLW(length, weight);
}

function compactFishValueForPdf(length, weight) {
  return formatSpeciesExtremeLW(length, weight);
}

// Helper to find biggest/smallest/heaviest/lightest fish for a species in a session
function getBiggestSmallestHeaviestLightestFish(speciesName, samplingData) {
  const fishes = (samplingData || []).filter(
    (f) =>
      f.FishTypeName === speciesName && f.Length != null && f.Weight != null
  );
  if (!fishes.length)
    return { biggest: null, smallest: null, heaviest: null, lightest: null };
  let biggest = fishes[0],
    smallest = fishes[0],
    heaviest = fishes[0],
    lightest = fishes[0];
  fishes.forEach((f) => {
    if (parseFloat(f.Length) > parseFloat(biggest.Length)) biggest = f;
    if (parseFloat(f.Length) < parseFloat(smallest.Length)) smallest = f;
    if (parseFloat(f.Weight) > parseFloat(heaviest.Weight)) heaviest = f;
    if (parseFloat(f.Weight) < parseFloat(lightest.Weight)) lightest = f;
  });
  return { biggest, smallest, heaviest, lightest };
}
